// ----------------------------------------------------------------------------
// createExportPanel — shared persistent export panel factory.
// Wires to an existing #xtrc-export-panel DOM node (in index.html).
//
// Design:
//  - Panel is a grid column, not a modal overlay.
//  - For ZIP: auto-prefilled with all bucket items on open.
//  - For hardware targets: starts empty; user drags items from bucket.
//  - Capacity bar shows used bytes vs target limit (floppy/CD size).
//  - Removing from export list does NOT remove from bucket.
// ----------------------------------------------------------------------------

import { downloadZip } from 'client-zip';
import { RBMExporter, fileToUint8Array, detectFileType } from '../../rbm-export.js';
import { buildESX, buildES1, decodeWavToPcm, resample } from './electribe-export.js';
import { buildSF2, buildDLS, createSimpleBank, parseSF2, GEN as SF2_GEN, GEN_NAMES as SF2_GEN_NAMES } from './sf2-export.js';
import { buildDss1Disk, calcDss1Capacity, DSS1_DISK_SIZE, DSS1_DATA_SECTORS, DSS1_SECTOR_SIZE, DSM1_DISK_SIZE } from './dss1-export.js';
import { buildKSF, buildKMP, buildKSC, calcKsfCapacity } from './triton-export.js';
import { buildFzfDisk, calcFzfCapacity, nearestFzfRate, FZF_SAMPLE_RATES, FZF_FLOPPY_SIZE } from './casio-fzf-export.js';
import { buildEmuDisk, buildEmuE4b, calcEmuCapacity, nearestEmuRate, EMU_SAMPLE_RATES, EMU_FLOPPY_SIZE, EMU_DISK_SIZE, EMU_E3S1_HDR_SIZE } from './emu-eos-export.js';
import { buildSDiskDisk, calcSDiskCapacity, calcSDiskLayout, sdiskRateFlag, SDISK_SAMPLE_RATES, SDISK_DISK_SIZE, SDISK_BLOCK_BYTES, SDISK_MAX_BLOCKS, SDISK_BLOCKS_PER_BANK } from './roland-sdisk-export.js';
import { buildS7Disk, calcS7Layout, S7_SAMPLE_RATES, S7_DISK_SIZE, S7_AUDIO_UNIT, S7_OFFSETS } from './roland-s7-export.js';
import { buildMpcDisk, calcMpcLayout, MPC_DISK_SIZE, MPC_SAMPLE_RATE, MPC_SND_HEADER_SIZE, MPC_MAX_SAMPLES, MPC_SECTOR_SIZE } from './mpc-export.js';
import { buildYamahaDisk, calcYamahaLayout } from './yamaha-export.js';
import { detectTransients, slicesFromOnsets, buildRex2 } from './rex2-export.js';
import { Midi } from '@tonejs/midi';
import { OpenSheetMusicDisplay } from 'opensheetmusicdisplay';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { toArrayBuffer, makeId, dbPutItem } from './bucket.js';
const { drawWaveformFromInt16, drawWaveformFromInt32, getThemeDrawColors, playAudioBuffer, getGlobalAudioContext, stopAllAudioPlayback } = require('../../common/draw-utils.cjs');
const { drawWaveformFromWav: drawWaveformFromWavBucket, drawMidiPianoRoll } = require('./bucket.js');
const { midiToMusicXml } = require('../../common/midi-to-musicxml.cjs');

// ---- RBM sample normalization (mono conversion for ReBirth) ----------------
// ReBirth only supports mono samples. This decodes any audio format via
// AudioContext, mixes to mono, and re-encodes as 16-bit PCM WAV.
// AIFF samples are passed through as-is (exporter handles them).
async function normalizeRbmSample(rawBytes) {
   if (!(rawBytes instanceof Uint8Array)) rawBytes = new Uint8Array(rawBytes);
   const magic = String.fromCharCode(rawBytes[0], rawBytes[1], rawBytes[2], rawBytes[3]);
   if (magic === 'FORM') return rawBytes; // Already AIFF — pass through
   // Decode via AudioContext, mix to mono, re-encode as WAV
   const ac = getGlobalAudioContext();
   const audioBuffer = await ac.decodeAudioData(rawBytes.buffer.slice(0));
   const channels = audioBuffer.numberOfChannels;
   const frames = audioBuffer.length;
   const sampleRate = audioBuffer.sampleRate;
   let mono;
   if (channels === 1) {
      mono = audioBuffer.getChannelData(0);
   } else {
      mono = new Float32Array(frames);
      for (let ch = 0; ch < channels; ch++) {
         const data = audioBuffer.getChannelData(ch);
         for (let i = 0; i < frames; i++) mono[i] += data[i] / channels;
      }
   }
   const dataSize = frames * 2;
   const buf = new ArrayBuffer(44 + dataSize);
   const view = new DataView(buf);
   const u8 = new Uint8Array(buf);
   u8.set([0x52,0x49,0x46,0x46], 0); // RIFF
   view.setUint32(4, 36 + dataSize, true);
   u8.set([0x57,0x41,0x56,0x45], 8); // WAVE
   u8.set([0x66,0x6d,0x74,0x20], 12); // fmt
   view.setUint32(16, 16, true);
   view.setUint16(20, 1, true); // PCM
   view.setUint16(22, 1, true); // mono
   view.setUint32(24, sampleRate, true);
   view.setUint32(28, sampleRate * 2, true);
   view.setUint16(32, 2, true);
   view.setUint16(34, 16, true);
   u8.set([0x64,0x61,0x74,0x61], 36); // data
   view.setUint32(40, dataSize, true);
   let off = 44;
   for (let i = 0; i < frames; i++) {
      let s = Math.max(-1, Math.min(1, mono[i]));
      view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      off += 2;
   }
   return new Uint8Array(buf);
}

// ---- Audio conversion formats ----------------------------------------------
const AUDIO_FORMATS = {
   wav:  { label: 'WAV (no conversion)',  ext: 'wav',  codec: 'pcm_s16le', container: 'wav' },
   mp3:  { label: 'MP3',  ext: 'mp3',  codec: 'libmp3lame', container: 'mp3', bitrate: '192k' },
   flac: { label: 'FLAC', ext: 'flac', codec: 'flac', container: 'flac' },
   aac:  { label: 'AAC',  ext: 'aac',  codec: 'aac', container: 'adts', bitrate: '192k' },
   aiff: { label: 'AIFF', ext: 'aiff', codec: 'pcm_s16be', container: 'aiff' },
   m4a:  { label: 'M4A',  ext: 'm4a',  codec: 'aac', container: 'mp4', bitrate: '192k' },
   ogg:  { label: 'OGG',  ext: 'ogg',  codec: 'libvorbis', container: 'ogg', bitrate: '192k' },
};

// ---- Target metadata -------------------------------------------------------
const TARGETS = {
   zip:    { label: 'ZIP',            limit: 0,           slots: 0,   fmt: 'zip'  },
   allzip: { label: 'All Files ZIP', limit: 0,           slots: 0,   fmt: 'zip'  },
   midi:   { label: 'MIDI folder',    limit: 0,           slots: 0,   fmt: 'mid'  },
   musicxmlpdf: { label: 'Sheet Music PDF', limit: 0,     slots: 0,   fmt: 'pdf'  },
   akai:   { label: 'AKAI S1000',     limit: 1_474_560,   slots: 200, fmt: 'akai' },
   emu:    { label: 'EMU EOS',        limit: EMU_FLOPPY_SIZE, slots: 1000, fmt: 'emu'  },
   emue4b: { label: 'EMU E4B Bank',   limit: 0,               slots: 1000, fmt: 'e4b'  },
   roland: { label: 'Roland samplers', limit: S7_DISK_SIZE, slots: 128, fmt: 'rol'  },
   mpc:    { label: 'Akai MPC2000',    limit: MPC_DISK_SIZE, slots: MPC_MAX_SAMPLES, fmt: 'mpc'  },
   yamaha: { label: 'Yamaha A3000/A5000', limit: 1474560,  slots: 128, fmt: 'yamaha' },
   gig:    { label: 'GigaSampler',    limit: 0,           slots: 0,   fmt: 'gig'  },
   rex2:   { label: 'ReCycle REX2',   limit: 0,           slots: 128, fmt: 'rx2'  },
   rbm:    { label: 'ReBirth RB-338', limit: 0,           slots: 0,   fmt: 'rbm'  },
   electribe: { label: 'Electribe ESX/ES1', limit: 0,    slots: 0,   fmt: 'esx'  },
   sf2:       { label: 'SoundFont SF2',     limit: 0,    slots: 0,   fmt: 'sf2'  },
   dss1:      { label: 'Korg DSS-1',        limit: DSS1_DATA_SECTORS * DSS1_SECTOR_SIZE, slots: 0,   fmt: 'dss1' },
   triton:    { label: 'Korg Triton/M3',    limit: 0,    slots: 0,   fmt: 'triton' },
   casio:     { label: 'Casio FZ-1',        limit: FZF_FLOPPY_SIZE, slots: 64, fmt: 'fzf' },
};

// ---- Helper ----------------------------------------------------------------
function fld(label, inputHtml) {
   return `<div class="xep-field"><label>${label}</label>${inputHtml}</div>`;
}

function fmtBytes(b) {
   if (!b && b !== 0) return '';
   if (b < 1024) return b + ' B';
   if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
   return (b / (1024 * 1024)).toFixed(2) + ' MB';
}

// ---- ReBirth CD data (RB20FUL.DAT) for RBM sample encryption ---------------
// Reuses the same IndexedDB cache the RBM decoder/adapter populates so we don't
// re-download the 128MB file.
const RBM_CD_IDB_NAME = 'rb338_extractor';
const RBM_CD_IDB_STORE = 'cd_data';
const RBM_CD_IDB_KEY = 'rb20ful';
const RBM_CD_SIZE = 134217984;
const RBM_CD_URL = '/data/RB20FUL.DAT';

function rbmCdOpenDb() {
   return new Promise((resolve, reject) => {
      const req = indexedDB.open(RBM_CD_IDB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(RBM_CD_IDB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
   });
}
function rbmCdIdbGet(key) {
   return rbmCdOpenDb().then((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(RBM_CD_IDB_STORE, 'readonly');
      const r = tx.objectStore(RBM_CD_IDB_STORE).get(key);
      r.onsuccess = () => resolve(r.result || null);
      r.onerror = () => reject(r.error);
   }));
}
function rbmCdIdbPut(key, val) {
   return rbmCdOpenDb().then((db) => new Promise((resolve, reject) => {
      const tx = db.transaction(RBM_CD_IDB_STORE, 'readwrite');
      tx.objectStore(RBM_CD_IDB_STORE).put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
   }));
}

let rbmCdBytesCache = null;
async function ensureRbmCdData(onProgress) {
   if (rbmCdBytesCache) return rbmCdBytesCache;
   try {
      const blob = await rbmCdIdbGet(RBM_CD_IDB_KEY);
      if (blob) {
         const arr = new Uint8Array(blob);
         // Validate cached data size before using it
         if (arr.length === RBM_CD_SIZE) {
            rbmCdBytesCache = arr;
            return rbmCdBytesCache;
         }
         // Cached data is wrong size, fall through to download
      }
   } catch (e) { /* fall through to download */ }

   const isElectron = typeof window !== 'undefined' && window.electron;
   // In browser, use the current origin (localhost:3000); in Electron, use the local proxy
   const url = (isElectron ? 'http://127.0.0.1:8765' : window.location.origin) + RBM_CD_URL;
   const arrayBuffer = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'arraybuffer';
      xhr.onprogress = (event) => {
         if (event.lengthComputable && onProgress) {
            const mb = (event.loaded / 1024 / 1024).toFixed(1);
            const total = (event.total / 1024 / 1024).toFixed(1);
            onProgress(Math.round((event.loaded / event.total) * 100), `Downloading CD data ${mb} / ${total} MB`);
         }
      };
      xhr.onload = () => xhr.status === 200 ? resolve(xhr.response) : reject(new Error(`HTTP ${xhr.status} fetching RB20FUL.DAT`));
      xhr.onerror = () => reject(new Error('network error fetching RB20FUL.DAT'));
      xhr.send();
   });
   const arr = new Uint8Array(arrayBuffer);
   if (arr.length !== RBM_CD_SIZE) {
      throw new Error(`RB20FUL.DAT size mismatch: got ${arr.length} bytes, expected ${RBM_CD_SIZE}`);
   }
   rbmCdBytesCache = arr;
   try { await rbmCdIdbPut(RBM_CD_IDB_KEY, arrayBuffer.slice(0)); } catch (e) { /* cache best-effort */ }
   return rbmCdBytesCache;
}

// ---- Per-target config HTML ------------------------------------------------
const TARGET_CONFIGS = {
   zip: () => {
      const formatOptions = Object.entries(AUDIO_FORMATS)
         .map(([key, fmt]) => `<option value="${key}">${fmt.label}</option>`)
         .join('');
      return `
      ${fld('Folder prefix', '<input id="exp-zip-prefix" type="text" value="xtrc_export" />')}
      ${fld('Audio format', `<select id="exp-zip-format">${formatOptions}</select>`)}
      ${fld('Normalize names', '<input id="exp-zip-normalize" type="checkbox" checked />')}
   `;
   },
   allzip: () => {
      return `
      ${fld('Folder prefix', '<input id="exp-allzip-prefix" type="text" value="xtrc_export" />')}
      ${fld('Normalize names', '<input id="exp-allzip-normalize" type="checkbox" checked />')}
      <div class="xep-note">Drag any files from the bucket — audio, MIDI, images, videos, modules.</div>
   `;
   },
   midi: () => `
      ${fld('Folder prefix', '<input id="exp-midi-prefix" type="text" value="xtrc_midi" />')}
      ${fld('Normalize names', '<input id="exp-midi-normalize" type="checkbox" checked />')}
      <div class="xep-note">Drag MIDI files from the bucket into the list below.</div>
   `,
   musicxmlpdf: () => `
      ${fld('Paper size', '<select id="exp-musicxmlpdf-size"><option value="a4">A4</option><option value="letter">Letter</option></select>')}
      ${fld('Normalize names', '<input id="exp-musicxmlpdf-normalize" type="checkbox" checked />')}
      <div class="xep-note">Drag MIDI or MusicXML files from the bucket into the list below. Each file will be rendered as a printable PDF score.</div>
   `,
   akai: () => `
      ${fld('Volume name', '<input id="exp-akai-vol" type="text" maxlength="11" value="XTRC" />')}
      ${fld('Media', '<select id="exp-akai-fmt"><option value="s1000">S1000 720K floppy</option><option value="s3000">S3000 1.44M floppy</option><option value="cd">CD-ROM ISO</option></select>')}
      <div class="xep-note">⚠ Image gen is stub — exports WAV ZIP with AKAI naming.</div>
   `,
   roland: () => {
      const sdiskRates = SDISK_SAMPLE_RATES
         .map(r => `<option value="${r.rate}">${r.label}</option>`)
         .join('');
      const s7Rates = S7_SAMPLE_RATES
         .map(r => `<option value="${r.rate}">${r.label}</option>`)
         .join('');
      return `
      ${fld('Volume label', '<input id="exp-roland-vol" type="text" maxlength="11" value="XTRC" />')}
      ${fld('Format', '<select id="exp-roland-format"><option value="sdisk">S-50 family (720KB, 12-bit)</option><option value="s7">S-770/S-760/S-750 (1.44MB, 16-bit)</option></select>')}
      ${fld('Sampler', '<select id="exp-roland-family"><option value="S-550">S-550</option><option value="S-330">S-330</option><option value="S-50">S-50</option><option value="W-30">W-30</option></select>')}
      ${fld('Sample rate', `<select id="exp-roland-rate">${sdiskRates}</select>`)}
      <div class="xep-note xep-roland-note">Drag samples from the bucket into the list below. Creates a floppy image with native PCM — works with Gotek and ZuluSCSI.</div>
   `;
   },
   mpc: () => {
      return `
      ${fld('Volume label', '<input id="exp-mpc-vol" type="text" maxlength="11" value="MPC DISK" />')}
      ${fld('Model', '<select id="exp-mpc-model"><option value="MPC2000">MPC2000 / MPC2000XL</option><option value="MPC3000">MPC3000</option></select>')}
      <div class="xep-note">Drag samples from the bucket. Creates a 1.44 MB FAT12 floppy image with Akai .SND files (16-bit, 44100 Hz). Works with Gotek and real MPC hardware.</div>
   `;
   },
   yamaha: () => {
      return `
      ${fld('Disk name', '<input id="exp-yamaha-diskname" type="text" maxlength="16" value="YAMAHA DISK" />')}
      ${fld('Volume name', '<input id="exp-yamaha-volname" type="text" maxlength="16" value="VOLUME 1" />')}
      <div class="xep-note">Drag samples from the bucket. Creates a 1.44 MB FAT12 floppy image with native Yamaha A-series format (SMPL/SBNK directory structure, 16-bit big-endian PCM). Works with A3000/A4000/A5000 and Gotek.</div>
   `;
   },
   gig: () => `
      ${fld('Instrument', '<input id="exp-gig-name" type="text" value="XTRC Instrument" />')}
      <div class="xep-note">⚠ .gig export is stub — exports WAV ZIP.</div>
   `,
   rex2: () => `
      ${fld('Tempo (BPM)', '<input id="exp-rex2-bpm" type="number" value="120" min="40" max="300" />')}
      ${fld('Slice mode', '<select id="exp-rex2-mode"><option value="auto">Auto-detect transients (single loop)</option><option value="manual">Each sample = one slice</option></select>')}
      ${fld('Sensitivity', '<input id="exp-rex2-sens" type="range" min="0.5" max="5" step="0.1" value="1.5" />')}
      ${fld('Min slice (ms)', '<input id="exp-rex2-minslice" type="number" value="80" min="10" max="500" />')}
      <div class="xep-note">Creates a real REX2 file with DWOP-compressed audio and slice markers. For auto mode, drag a single drum loop. For manual mode, drag individual hits — each becomes a slice.</div>
   `,
   rbm: () => `
      ${fld('Mod name', '<input id="exp-rbm-name" type="text" value="My Mod" />')}
   `,
   electribe: () => `
      ${fld('Bank name', '<input id="exp-electribe-name" type="text" maxlength="8" value="XTRC" />')}
      ${fld('Format', '<select id="exp-electribe-fmt"><option value="esx">ESX (Electribe SX)</option><option value="es1">ES1 (Electribe S-1)</option></select>')}
      <div class="xep-note">Drag samples from the bucket into the slot grid. ESX uses raw PCM; ES1 uses mono ADPCM at 32 kHz (stereo samples are mixed down to mono).</div>
   `,
   sf2: () => `
      ${fld('Bank name', '<input id="exp-sf2-name" type="text" maxlength="20" value="My SoundFont" />')}
      ${fld('Format', '<select id="exp-sf2-fmt"><option value="sf2">SF2 (SoundFont 2)</option><option value="sfz">SFZ (Text Format)</option><option value="dls">DLS (Downloadable Sounds)</option></select>')}
      ${fld('Engineer', '<input id="exp-sf2-engineer" type="text" maxlength="40" value="" placeholder="optional" />')}
      ${fld('Comment', '<input id="exp-sf2-comment" type="text" maxlength="80" value="" placeholder="optional" />')}
      <div class="xep-note xep-sf2-note">Drag samples from bucket → grid. Click sample to edit. Right-click to remove. <button id="exp-sf2-import-btn" type="button" class="xep-sf2-import-btn">Import SF2…</button></div>
   `,
   dss1: () => `
      ${fld('Disk name', '<input id="exp-dss1-name" type="text" maxlength="8" value="DSS1DISK" />')}
      ${fld('Format', '<select id="exp-dss1-fmt"><option value="img">DSS-1 Disk Image (.img)</option><option value="dsm1">DSM-1 HD Disk Image (.img)</option></select>')}
      ${fld('Sample rate', '<select id="exp-dss1-rate"><option value="0">Auto (nearest match)</option><option value="16000">16000 Hz (max ~25s)</option><option value="24000">24000 Hz (max ~17s)</option><option value="32000">32000 Hz (max ~12s)</option><option value="48000">48000 Hz (max ~8s)</option></select>')}
      <div class="xep-note">Korg DSS-1 floppy disk image with 12-bit packed samples. Samples are mono — stereo samples are mixed down. The DSS-1 supports 16/24/32/48 kHz sample rates. Disk capacity: 800 KB (DSS-1) / 1.6 MB (DSM-1).</div>
   `,
   triton: () => `
      ${fld('Multisample name', '<input id="exp-triton-name" type="text" maxlength="16" value="My Multisample" />')}
      ${fld('Format', '<select id="exp-triton-fmt"><option value="ksf">KSF only (individual samples)</option><option value="kmp">KMP + KSF (multisample + samples)</option></select>')}
      ${fld('Sample rate', '<select id="exp-triton-rate"><option value="44100">44100 Hz (CD quality)</option><option value="48000">48000 Hz</option><option value="32000">32000 Hz</option><option value="22050">22050 Hz</option></select>')}
      <div class="xep-note">Korg Triton/M3/Karma/Kronos KSF sample files with 16-bit big-endian PCM. Exports as a ZIP with .KSF files (and .KMP multisample + .KSC script if selected). Samples are resampled to the target rate to preserve tonality.</div>
   `,
   casio: () => `
      ${fld('Disk name', '<input id="exp-casio-name" type="text" maxlength="12" value="FZ1 DISK" />')}
      ${fld('Sample rate', '<select id="exp-casio-rate"><option value="36000">36000 Hz (high quality)</option><option value="18000">18000 Hz (medium)</option><option value="9000">9000 Hz (longest time)</option></select>')}
      <div class="xep-note">Casio FZ-1/FZ-10M/FZ-20M Full Dump Data (.fzf) with 16-bit signed PCM. Samples are mono — stereo samples are mixed down. Each sample becomes a voice with its own region in the shared PCM pool. Max 64 voices. Sample rates: 36/18/9 kHz.</div>
   `,
   emu: () => `
      ${fld('Bank name', '<input id="exp-emu-name" type="text" maxlength="16" value="EMU Bank" />')}
      ${fld('Media', '<select id="exp-emu-media"><option value="floppy">Floppy disk (1.44 MB)</option><option value="disk">Hard disk (512 MB)</option></select>')}
      ${fld('Sample rate', '<select id="exp-emu-rate"><option value="44100">44100 Hz (CD quality)</option><option value="48000">48000 Hz</option><option value="32000">32000 Hz</option><option value="22050">22050 Hz</option><option value="16000">16000 Hz</option></select>')}
      <div class="xep-note">EMU EOS (E4/E5000/E6400 Ultra) disk image (.img) with E4B0 bank directory and E3S1 sample chunks. 16-bit little-endian PCM. Samples are mono — stereo samples are mixed down. Floppy = 1.44 MB, Hard disk = 512 MB.</div>
   `,
   emue4b: () => `
      ${fld('Bank name', '<input id="exp-emu-name" type="text" maxlength="16" value="EMU Bank" />')}
      ${fld('Sample rate', '<select id="exp-emu-rate"><option value="44100">44100 Hz (CD quality)</option><option value="48000">48000 Hz</option><option value="32000">32000 Hz</option><option value="22050">22050 Hz</option><option value="16000">16000 Hz</option></select>')}
      <div class="xep-note">EMU E4B bank file (.e4b) — IFF-style FORM+E4B0 with E4Ma TOC and E3S1 sample chunks. 16-bit little-endian PCM. Samples are mono — stereo samples are mixed down. No size limit (standalone bank file).</div>
   `,
};

// ---- Main factory ----------------------------------------------------------
export function createExportModal(bucket) {
   // Create progress modal
   const progressModal = document.createElement('div');
   progressModal.id = 'export-progress-modal';
   progressModal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      display: none;
      align-items: center;
      justify-content: center;
   `;
   progressModal.innerHTML = `
      <div style="background: var(--c1); padding: 30px; border-radius: 8px; border: 2px solid var(--c6); min-width: 300px; text-align: center;">
         <h3 style="margin: 0 0 20px 0; color: var(--cf); font-family: 'bit', sans-serif;">Export Progress</h3>
         <div id="export-progress-status" style="color: var(--c9); font-family: 'bit', sans-serif; font-size: 14px; margin-bottom: 15px;">Preparing...</div>
         <div style="width: 100%; height: 6px; background: var(--c3); border-radius: 3px; overflow: hidden;">
            <div id="export-progress-bar" style="width: 0%; height: 100%; background: var(--ce); transition: width 0.2s;"></div>
         </div>
         <div id="export-progress-detail" style="color: var(--c7); font-family: 'bit', sans-serif; font-size: 12px; margin-top: 10px;"></div>
      </div>
   `;
   document.body.appendChild(progressModal);

   const progressStatus = progressModal.querySelector('#export-progress-status');
   const progressBar = progressModal.querySelector('#export-progress-bar');
   const progressDetail = progressModal.querySelector('#export-progress-detail');

   function showProgress() {
      progressModal.style.display = 'flex';
      progressBar.style.width = '0%';
   }

   function updateProgress(status, percent, detail) {
      if (progressStatus) progressStatus.textContent = status;
      if (progressBar) progressBar.style.width = percent + '%';
      if (progressDetail) progressDetail.textContent = detail;
   }

   function hideProgress() {
      progressModal.style.display = 'none';
   }

   // Resolve DOM refs — panel must exist in index.html
   const panelEl   = document.getElementById('xtrc-export-panel');
   if (!panelEl) {
      return { open() {}, close() {} };
   }

   const workspace  = panelEl.closest('.col-workspace') || panelEl.closest('.editor-panel') || panelEl.parentElement;
   const dlBucketBtn = document.getElementById('dlbucket');
   const targetSel  = document.getElementById('xtrc-export-target');
   const configEl   = document.getElementById('xtrc-export-config');
   const capBar     = document.getElementById('xep-capacity-bar');
   const capUsed    = document.getElementById('xep-cap-used');
   const capLimit   = document.getElementById('xep-cap-limit');
   const capFill    = document.getElementById('xep-cap-fill');
   const capSlots   = document.getElementById('xep-cap-slots');
   const dropzone   = document.getElementById('xep-dropzone');
   const sampleList = document.getElementById('xep-sample-list');
   const pickerEl   = document.getElementById('xep-bucket-picker');
   const pickerSelectAllBtn = document.getElementById('xep-picker-select-all');
   const clearBtn   = document.getElementById('xep-clear-list');
   const statusEl   = document.getElementById('xtrc-export-status');
   const runBtn     = document.getElementById('xtrc-export-run');

   // Internal export list: [{ id, name, wav (ArrayBuffer) }]
   let exportItems = [];

   // ---- Capacity ------------------------------------------------------------
   function updateCapacity() {
      const target = TARGETS[targetSel.value] || TARGETS.zip;
      let totalBytes = exportItems.reduce((s, it) => s + (it.wav ? it.wav.byteLength : 0), 0);

      // For DSS-1, compute estimated 12-bit packed on-disk size after resampling
      const isDss1 = targetSel.value === 'dss1';
      const isCasio = targetSel.value === 'casio';
      const isEmu = targetSel.value === 'emu' || targetSel.value === 'emue4b';
      const isRoland = targetSel.value === 'roland';
      const isMpc = targetSel.value === 'mpc';
      if (isDss1 || isCasio || isEmu || isRoland || isMpc) {
         let packedBytes = 0;
         const dss1Rates = [16000, 24000, 32000, 48000];
         const dss1RateEl = document.getElementById('exp-dss1-rate');
         const dss1UserRate = dss1RateEl ? parseInt(dss1RateEl.value) : 0;
         const casioRateEl = document.getElementById('exp-casio-rate');
         const casioTargetRate = casioRateEl ? parseInt(casioRateEl.value) : 36000;
         const emuRateEl = document.getElementById('exp-emu-rate');
         const emuTargetRate = emuRateEl ? parseInt(emuRateEl.value) : 44100;
         const rolandRateEl = document.getElementById('exp-roland-rate');
         const rolandTargetRate = rolandRateEl ? parseInt(rolandRateEl.value) : 30000;
         const rolandFormatEl = document.getElementById('exp-roland-format');
         const rolandFormat = rolandFormatEl ? rolandFormatEl.value : 'sdisk';
         for (const it of exportItems) {
            if (!it.wav) continue;
            const dv = new DataView(it.wav);
            let numChannels = 1, bitsPerSample = 16, sampleRate = isDss1 ? 32000 : (isCasio ? 36000 : (isRoland ? 30000 : 44100));
            let dataLen = 0;
            let off = 12;
            while (off + 8 <= it.wav.byteLength) {
               const cid = dv.getUint32(off, true);
               const csz = dv.getUint32(off + 4, true);
               if (cid === 0x20746d66) { // 'fmt '
                  numChannels = dv.getUint16(off + 10, true);
                  bitsPerSample = dv.getUint16(off + 22, true);
                  sampleRate = dv.getUint32(off + 12, true);
               } else if (cid === 0x61746164) { // 'data'
                  dataLen = csz;
                  break;
               }
               off += 8 + csz + (csz & 1);
            }
            const bytesPerSample = (bitsPerSample / 8) * numChannels;
            let numSamples = bytesPerSample > 0 ? Math.floor(dataLen / bytesPerSample) : 0;
            // Estimate resampled size
            if (isDss1) {
               let bestRate;
               if (dss1UserRate > 0) {
                  bestRate = dss1UserRate;
               } else {
                  bestRate = 32000;
                  for (const r of dss1Rates) { if (Math.abs(sampleRate - r) < Math.abs(sampleRate - bestRate)) bestRate = r; }
               }
               if (sampleRate !== bestRate && sampleRate > 0) numSamples = Math.floor(numSamples * bestRate / sampleRate);
               // DSS-1: 12-bit packed, first sector 450 samples, subsequent 682
               const FIRST_SAMPLES = 450;
               const SAMPLES_PER_SECTOR = 682;
               let remaining = numSamples;
               remaining -= Math.min(remaining, FIRST_SAMPLES);
               const numDataSectors = Math.ceil(remaining / SAMPLES_PER_SECTOR);
               packedBytes += (1 + numDataSectors) * DSS1_SECTOR_SIZE;
            } else if (isEmu) {
               // EMU EOS: E3S1 chunks (8B marker+size + 98B header + 16-bit LE PCM)
               if (sampleRate !== emuTargetRate && sampleRate > 0) numSamples = Math.floor(numSamples * emuTargetRate / sampleRate);
               const pcmBytes = numSamples * 2;
               packedBytes += 8 + EMU_E3S1_HDR_SIZE + pcmBytes;
            } else if (isRoland) {
               if (rolandFormat === 's7') {
                  // Roland S-770/S-760/S-750: 16-bit LE PCM, 8192-byte aligned units
                  if (sampleRate !== rolandTargetRate && sampleRate > 0) numSamples = Math.floor(numSamples * rolandTargetRate / sampleRate);
                  const pcmBytes = numSamples * 2;
                  packedBytes += Math.ceil(pcmBytes / 8192) * 8192;
               } else {
                  // Roland S-50 family: 12-bit packed, 18432 bytes per block (12288 samples)
                  if (sampleRate !== rolandTargetRate && sampleRate > 0) numSamples = Math.floor(numSamples * rolandTargetRate / sampleRate);
                  const numBlocks = Math.ceil(numSamples / 12288);
                  packedBytes += numBlocks * 18432;
               }
            } else if (isMpc) {
               // MPC: 42-byte SND header + 16-bit LE PCM, 512-byte cluster aligned
               if (sampleRate !== MPC_SAMPLE_RATE && sampleRate > 0) numSamples = Math.floor(numSamples * MPC_SAMPLE_RATE / sampleRate);
               const sndBytes = MPC_SND_HEADER_SIZE + numSamples * 2;
               packedBytes += Math.ceil(sndBytes / MPC_SECTOR_SIZE) * MPC_SECTOR_SIZE;
            } else {
               // Casio FZF: voice descriptors (256B each, 4 per sector) + shared PCM pool
               if (sampleRate !== casioTargetRate && sampleRate > 0) numSamples = Math.floor(numSamples * casioTargetRate / sampleRate);
               const pcmBytes = numSamples * 2;
               packedBytes += pcmBytes; // accumulate PCM pool size
            }
         }
         // For Casio FZF, add voice descriptor overhead (256B per voice, padded to sectors)
         if (isCasio && exportItems.length > 0) {
            const voiceBytes = exportItems.filter(it => it.wav).length * 256;
            const voiceSectors = Math.ceil(voiceBytes / 1024);
            packedBytes += voiceSectors * 1024;
         }
         totalBytes = packedBytes;
      }

      // Show/hide capacity bar
      const hasLimit = target.limit > 0;
      capBar.hidden = !hasLimit && !target.slots;

      if (hasLimit) {
         // EMU: use media-specific limit
         let effectiveLimit = target.limit;
         if (isEmu) {
            const emuMediaEl = document.getElementById('exp-emu-media');
            const emuMedia = emuMediaEl ? emuMediaEl.value : 'floppy';
            effectiveLimit = emuMedia === 'disk' ? EMU_DISK_SIZE : EMU_FLOPPY_SIZE;
         }
         if (isRoland) {
            const rolandFmtEl3 = document.getElementById('exp-roland-format');
            const rolandFmt3 = rolandFmtEl3 ? rolandFmtEl3.value : 'sdisk';
            if (rolandFmt3 === 's7') {
               // S-770/S-760/S-750: audio area = disk size - header/dirs
               effectiveLimit = S7_DISK_SIZE - S7_OFFSETS.AUDIO_START;
            } else {
               // S-50 family: sample data area = 2 banks × 18 blocks × 18432 bytes
               effectiveLimit = 2 * SDISK_BLOCKS_PER_BANK * SDISK_BLOCK_BYTES;
            }
         }
         if (isMpc) {
            // MPC: data area = disk - boot - FATs - root dir
            effectiveLimit = MPC_DISK_SIZE - 0x4200;
         }
         const pct = Math.min(100, (totalBytes / effectiveLimit) * 100);
         capFill.style.width = pct + '%';
         capFill.classList.toggle('xep-cap-warn', pct > 75 && pct <= 100);
         capFill.classList.toggle('xep-cap-over', pct > 100);
         capUsed.textContent = fmtBytes(totalBytes);
         capLimit.textContent = fmtBytes(effectiveLimit);
      } else {
         capUsed.textContent = fmtBytes(totalBytes);
         capLimit.textContent = '';
         capFill.style.width = '0%';
      }

      if (target.slots > 0) {
         capSlots.textContent = `${exportItems.length} / ${target.slots} slots`;
      } else {
         capSlots.textContent = '';
      }
   }

   // ---- Render list ---------------------------------------------------------
   function updateDropzoneVisibility() {
      dropzone.classList.toggle('xep-dz-hidden', exportItems.length > 0);
   }

   async function drawWaveformFromWavFallback(canvas, audioBuffer) {
      const ctx = canvas.getContext('2d');
      const W = canvas.width, H = canvas.height;
      // Use theme colors
      const style = getComputedStyle(document.body);
      const bgColor = style.getPropertyValue('--c0').trim() || '#1a1a1a';
      const waveColor = style.getPropertyValue('--c9').trim() || '#c0c0c0';
      const errorColor = style.getPropertyValue('--c4').trim() || '#444';
      
      // Check if audioBuffer is valid ArrayBuffer
      if (!audioBuffer || typeof audioBuffer === 'string' || audioBuffer.byteLength === undefined) {
         ctx.fillStyle = errorColor;
         ctx.fillRect(0, H / 2 - 1, W, 2);
         return;
      }
      
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, W, H);
      
      // Handle both ArrayBuffer and TypedArray (Uint8Array, etc.)
      let buffer = audioBuffer;
      let byteOffset = 0;
      let byteLength = 0;
      
      if (audioBuffer instanceof ArrayBuffer) {
         buffer = audioBuffer;
         byteOffset = 0;
         byteLength = audioBuffer.byteLength;
      } else if (audioBuffer instanceof Uint8Array) {
         buffer = audioBuffer.buffer;
         byteOffset = audioBuffer.byteOffset;
         byteLength = audioBuffer.byteLength;
      } else if (audioBuffer && audioBuffer.buffer) {
         buffer = audioBuffer.buffer;
         byteOffset = audioBuffer.byteOffset || 0;
         byteLength = audioBuffer.byteLength || audioBuffer.buffer.byteLength;
      }
      
      if (!buffer || !(buffer instanceof ArrayBuffer) || byteLength < 44) {
         ctx.fillStyle = errorColor;
         ctx.fillRect(0, H / 2 - 1, W, 2);
         return;
      }
      try {
         // Check if it's AIF format
         const dv = new DataView(buffer, byteOffset, byteLength);
         const isAif = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3)) === 'FORM';
         
         if (isAif) {
            // Parse AIF format manually for waveform
            let dataOffset = 0;
            let numChannels = 1;
            let bitsPerSample = 16;
            let sampleCount = 0;
            
            // Parse AIF chunks
            let offset = 12;
            while (offset + 8 < byteLength) {
               const chunkId = String.fromCharCode(
                  dv.getUint8(offset), dv.getUint8(offset + 1),
                  dv.getUint8(offset + 2), dv.getUint8(offset + 3)
               );
               const chunkSize = dv.getUint32(offset + 4, false); // Big-endian
               
               if (chunkId === 'COMM') {
                  numChannels = dv.getUint16(offset + 8, false);
                  sampleCount = dv.getUint32(offset + 10, false); // numSampleFrames
                  bitsPerSample = dv.getUint16(offset + 14, false);
               } else if (chunkId === 'SSND') {
                  const offsetSize = dv.getUint32(offset + 8, false);
                  const blockSize = dv.getUint32(offset + 12, false);
                  dataOffset = offset + 8 + offsetSize;
                  break;
               }
               offset += 8 + chunkSize;
            }
            
            
            if (sampleCount === 0 || dataOffset === 0 || dataOffset >= byteLength) {
               throw new Error('Invalid AIF structure');
            }
            
            // Calculate actual sample count from data size
            const bytesPerSample = bitsPerSample / 8;
            const actualSampleCount = Math.floor((byteLength - dataOffset) / (bytesPerSample * numChannels));
            
            // Extract PCM into Int16Array and use drawWaveformFromInt16
            // for the same min/max per column + auto-scale algorithm as bucket
            const drawSampleCount = Math.min(sampleCount, actualSampleCount);
            const totalSamples = drawSampleCount * numChannels;
            let pcm;
            if (bitsPerSample === 16) {
               pcm = new Int16Array(totalSamples);
               for (let i = 0; i < totalSamples; i++) {
                  pcm[i] = dv.getInt16(dataOffset + i * 2, false);
               }
            } else if (bitsPerSample === 8) {
               pcm = new Int16Array(totalSamples);
               for (let i = 0; i < totalSamples; i++) {
                  pcm[i] = (dv.getUint8(dataOffset + i) - 128) << 8;
               }
            } else if (bitsPerSample === 24) {
               pcm = new Int16Array(totalSamples);
               for (let i = 0; i < totalSamples; i++) {
                  const byteIdx = dataOffset + i * 3;
                  const b0 = dv.getUint8(byteIdx), b1 = dv.getUint8(byteIdx + 1), b2 = dv.getUint8(byteIdx + 2);
                  let s = (b2 << 16) | (b1 << 8) | b0;
                  if (s & 0x800000) s -= 0x1000000;
                  pcm[i] = s >> 8;
               }
            } else {
               throw new Error('Unsupported AIFF bit depth: ' + bitsPerSample);
            }
            drawWaveformFromInt16(canvas, pcm, numChannels, 16);
         } else {
            // WAV format — use drawWaveformFromWavBucket for same algorithm as bucket
            drawWaveformFromWavBucket(canvas, buffer.slice(byteOffset, byteOffset + byteLength), null, null, null, null);
         }
      } catch (err) {
         console.error('[drawWaveform] Error:', err);
         ctx.strokeStyle = errorColor;
         ctx.beginPath();
         ctx.moveTo(0, H / 2);
         ctx.lineTo(W, H / 2);
         ctx.stroke();
      }
   }

   // ---- Playback ------------------------------------------------------------
   let activeSource = null;
   let activeCanvas = null;

   function stopPlayback() {
      if (activeSource) {
         try { activeSource.stop(); } catch (_) {}
         try { activeSource.disconnect(); } catch (_) {}
         activeSource = null;
      }
      if (activeCanvas) {
         activeCanvas.classList.remove('xep-canvas-playing');
         activeCanvas.style.border = '';
         activeCanvas = null;
      }
   }

   async function playItem(wav, canvas, pcm, sampleRate, channels, bitDepth) {
      if (!wav) return;
      
      // Stop all audio playback globally (without dispatching events to avoid self-stop)
      stopAllAudioPlayback('export');
      
      try {
         const ac = getGlobalAudioContext();
         
         // If PCM data is available, use it directly (better for 24-bit audio)
         if (pcm && pcm.length && sampleRate) {
            const scale = bitDepth === 24 ? 8388608 : (bitDepth === 32 ? 2147483648 : 32768);
            const audioBuffer = ac.createBuffer(channels || 1, pcm.length, sampleRate);
            
            if (channels === 1) {
               const channelData = audioBuffer.getChannelData(0);
               for (let i = 0; i < pcm.length; i++) {
                  channelData[i] = pcm[i] / scale;
               }
            } else if (channels === 2) {
               const left = audioBuffer.getChannelData(0);
               const right = audioBuffer.getChannelData(1);
               for (let i = 0; i < pcm.length / 2; i++) {
                  left[i] = pcm[i * 2] / scale;
                  right[i] = pcm[i * 2 + 1] / scale;
               }
            }
            
            // Dispatch stop event to stop other audio
            window.dispatchEvent(new CustomEvent('bucket-stop-all'));
            
            playAudioBuffer(audioBuffer, ac, 1, canvas, () => {
               activeCanvas = null;
            }, 'export');
            activeCanvas = canvas;
            canvas.classList.add('xep-canvas-playing');
            return;
         }
         
         // Check if it's AIF format
         // Handle both TypedArray and ArrayBuffer
         const buffer = wav.buffer || wav;
         const byteOffset = wav.byteOffset || 0;
         const byteLength = wav.byteLength || buffer.byteLength;
         const dv = new DataView(buffer, byteOffset, byteLength);
         const isAif = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3)) === 'FORM';
         
         let decoded;
         if (isAif) {
            // Parse AIF and create AudioBuffer manually
            let dataOffset = 0;
            let numChannels = 1;
            let bitsPerSample = 16;
            let sampleCount = 0;
            let sampleRate = 44100;
            
            // Parse AIF chunks
            let offset = 12;
            while (offset + 8 < byteLength) {
               const chunkId = String.fromCharCode(
                  dv.getUint8(offset), dv.getUint8(offset + 1),
                  dv.getUint8(offset + 2), dv.getUint8(offset + 3)
               );
               const chunkSize = dv.getUint32(offset + 4, false);
               
               if (chunkId === 'COMM') {
                  numChannels = dv.getUint16(offset + 8, false);
                  sampleCount = dv.getUint32(offset + 10, false);
                  bitsPerSample = dv.getUint16(offset + 14, false);
                  // Parse 80-bit float sample rate
                  const srBytes = new Uint8Array(buffer, byteOffset + offset + 16, 10);
                  sampleRate = parseAIFSampleRate(srBytes);
               } else if (chunkId === 'SSND') {
                  const offsetSize = dv.getUint32(offset + 8, false);
                  dataOffset = offset + 8 + offsetSize;
                  break;
               }
               offset += 8 + chunkSize;
            }
            
            // Create AudioBuffer from PCM data
            decoded = ac.createBuffer(numChannels, sampleCount, sampleRate);
            const bytesPerSample = bitsPerSample / 8;
            
            for (let ch = 0; ch < numChannels; ch++) {
               const channelData = decoded.getChannelData(ch);
               for (let i = 0; i < sampleCount; i++) {
                  const byteIdx = dataOffset + (i * numChannels + ch) * bytesPerSample;
                  if (byteIdx + bytesPerSample > byteLength) break;
                  
                  if (bitsPerSample === 16) {
                     channelData[i] = dv.getInt16(byteIdx, false) / 32768;
                  } else if (bitsPerSample === 8) {
                     channelData[i] = (dv.getUint8(byteIdx) - 128) / 128;
                  }
               }
            }
         } else {
            // Use AudioContext for WAV format
            const arrayBuffer = buffer.slice(byteOffset, byteOffset + byteLength);
            decoded = await ac.decodeAudioData(arrayBuffer);
         }
         
         // Dispatch stop event to stop other audio
         window.dispatchEvent(new CustomEvent('bucket-stop-all'));
         
         playAudioBuffer(decoded, ac, 1, canvas, () => {
            activeCanvas = null;
         }, 'export');
         activeCanvas = canvas;
         canvas.classList.add('xep-canvas-playing');
      } catch (err) {
      }
   }
   
   // Parse AIF 80-bit extended float sample rate
   function parseAIFSampleRate(bytes) {
      const sign = (bytes[0] >> 7) & 1;
      const exponent = ((bytes[0] & 0x7F) << 8) | bytes[1];
      const mantissa = ((bytes[2] << 24) | (bytes[3] << 16) | (bytes[4] << 8) | bytes[5]) >>> 0;
      
      if (exponent === 0 && mantissa === 0) return 0;
      if (exponent === 0x7FFF) return sign ? -Infinity : Infinity;
      
      const value = mantissa / Math.pow(2, 32);
      return (sign ? -1 : 1) * Math.pow(2, exponent - 16383) * value;
   }

   function renderList() {
      stopPlayback();
      sampleList.innerHTML = '';
      for (const item of exportItems) {
         const sampleDiv = document.createElement('div');
         sampleDiv.classList.add('xep-sample-item');
         sampleDiv.dataset.id = item.id;
         sampleDiv.dataset.type = item.type || 'wav';

         // Create name label
         const nameLabel = document.createElement('div');
         nameLabel.className = 'xep-sample-name';
         nameLabel.textContent = item.name;
         sampleDiv.appendChild(nameLabel);

         // Create visual based on file type
         if (item.type === 'midi') {
            // MIDI piano roll canvas
            const canvas = document.createElement('canvas');
            canvas.classList.add('xep-sample-canvas');
            canvas.width = 200;
            canvas.height = 52;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `MIDI piano roll for ${item.name}, click to play`);
            drawMidiPianoRoll(canvas, item.wav);
            canvas.addEventListener('click', () => {
               // Dispatch custom event for MIDI playback
               const event = new CustomEvent('bucket-midi-play', {
                  detail: { name: item.name, data: item.wav, source: item.source }
               });
               window.dispatchEvent(event);
            });
            sampleDiv.appendChild(canvas);
         } else if (item.type === 'image') {
            // Image thumbnail
            const canvas = document.createElement('canvas');
            canvas.classList.add('xep-sample-canvas');
            canvas.width = 200;
            canvas.height = 52;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Image thumbnail for ${item.name}, click to view`);
            const ext = (item.name.split('.').pop() || '').toLowerCase();
            const imgMime = {png:'image/png',jpg:'image/jpeg',jpeg:'image/jpeg',gif:'image/gif',bmp:'image/bmp',webp:'image/webp',tga:'image/x-tga',tiff:'image/tiff',tif:'image/tiff',pcx:'image/x-pcx'}[ext] || 'application/octet-stream';
            const cctx = canvas.getContext('2d');
            cctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--c0').trim() || '#000';
            cctx.fillRect(0, 0, canvas.width, canvas.height);
            const img = new Image();
            img.onload = () => {
               const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
               const w = img.width * scale, h = img.height * scale;
               cctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
            };
            img.src = URL.createObjectURL(new Blob([item.wav], { type: imgMime }));
            canvas.addEventListener('click', () => {
               const url = URL.createObjectURL(new Blob([item.wav], { type: imgMime }));
               if (window.openImageViewer) {
                  window.openImageViewer({ url, name: item.name, downloadable: true, downloadUrl: url, downloadName: item.name });
               } else {
                  window.open(url, '_blank');
               }
            });
            sampleDiv.appendChild(canvas);
         } else if (item.type === 'video') {
            // Video first-frame thumbnail
            const canvas = document.createElement('canvas');
            canvas.classList.add('xep-sample-canvas');
            canvas.width = 200;
            canvas.height = 52;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Video thumbnail for ${item.name}, click to play`);
            const mimeType = item.name.endsWith('.webm') ? 'video/webm' : 'video/mp4';
            const cctx = canvas.getContext('2d');
            cctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--c0').trim() || '#000';
            cctx.fillRect(0, 0, canvas.width, canvas.height);
            cctx.fillStyle = '#aaa';
            cctx.font = '10px monospace';
            cctx.fillText('video', 5, 30);
            const video = document.createElement('video');
            video.muted = true;
            video.preload = 'metadata';
            video.onloadeddata = () => {
               const scale = Math.min(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
               const w = video.videoWidth * scale, h = video.videoHeight * scale;
               cctx.drawImage(video, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
            };
            video.src = URL.createObjectURL(new Blob([item.wav], { type: mimeType }));
            canvas.addEventListener('click', () => {
               const url = URL.createObjectURL(new Blob([item.wav], { type: mimeType }));
               if (window.openVideoPlayer) {
                  window.openVideoPlayer({ url, name: item.name, downloadable: true, downloadUrl: url, downloadName: item.name });
               } else {
                  window.open(url, '_blank');
               }
            });
            sampleDiv.appendChild(canvas);
         } else if (item.type === 'module') {
            // Module — show a label icon
            const canvas = document.createElement('canvas');
            canvas.classList.add('xep-sample-canvas');
            canvas.width = 200;
            canvas.height = 52;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Tracker module ${item.name}`);
            const cctx = canvas.getContext('2d');
            cctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--c0').trim() || '#000';
            cctx.fillRect(0, 0, canvas.width, canvas.height);
            cctx.fillStyle = '#8cf';
            cctx.font = 'bold 10px monospace';
            cctx.fillText('module', 5, 20);
            cctx.fillStyle = '#aaa';
            cctx.font = '9px monospace';
            cctx.fillText(item.name, 5, 35);
            sampleDiv.appendChild(canvas);
         } else {
            // WAV waveform canvas
            const canvas = document.createElement('canvas');
            canvas.classList.add('xep-sample-canvas');
            canvas.width = 200;
            canvas.height = 52;
            canvas.style.cursor = 'pointer';
            canvas.setAttribute('role', 'img');
            canvas.setAttribute('aria-label', `Waveform for ${item.name}, click to play`);

            // Use shared drawWaveformFromInt16 if PCM data is available
            if (item.pcm && item.pcm.length) {
               const bitDepthToUse = item.bitDepth || (item.pcm instanceof Int32Array ? 24 : 16);
               drawWaveformFromInt16(canvas, item.pcm, item.channels || 1, bitDepthToUse);
            } else {
               drawWaveformFromWavBucket(canvas, item.wav, item.pcm, item.sampleRate, item.channels, item.bitDepth);
            }
            
            canvas.addEventListener('click', () => {
               if (activeSource && activeCanvas === canvas) { 
                  stopPlayback(); 
                  return; 
               }
               playItem(item.wav, canvas, item.pcm, item.sampleRate, item.channels, item.bitDepth);
            });
            sampleDiv.appendChild(canvas);
         }

         const removeBtn = document.createElement('button');
         removeBtn.className = 'xep-sample-remove';
         removeBtn.textContent = 'remove';
         removeBtn.title = 'Remove from export (stays in bucket)';
         removeBtn.setAttribute('aria-label', `Remove ${item.name} from export list`);
         removeBtn.addEventListener('click', () => removeItem(item.id));

         sampleDiv.appendChild(removeBtn);
         sampleList.appendChild(sampleDiv);
      }
      updateDropzoneVisibility();
      updateCapacity();
      // Sync the accessible picker checkboxes with the new export list state
      // (rebuild happens on open / target change / bucket change, not here,
      //  to preserve screen-reader focus)
      syncPickerChecked();
      // Reload REX2 waveform editor if in REX2 mode
      if (targetSel.value === 'rx2') {
         rex2LoadFromExportItems();
      }
   }

   // ---- Item management -----------------------------------------------------

   // Returns true if a bucket item is accepted for the given export target.
   // Mirrors the acceptance logic in addItem() so the accessible picker can
   // show only relevant samples.
   function itemAcceptedForTarget(bucketItem, targetKey) {
      if (!bucketItem || !bucketItem.id) return false;
      const itemType = bucketItem.type || 'wav';
      if (targetKey === 'midi') return itemType === 'midi';
      if (targetKey === 'musicxmlpdf') return itemType === 'midi' || itemType === 'musicxml';
      if (targetKey === 'zip') return itemType === 'wav' || itemType === 'audio' || itemType === 'module';
      // allzip and hardware targets: accept audio/module/midi/image/video/raw/archive
      // (hardware exporters filter further at build time; the picker lets the user
      //  add anything and surface errors at export, matching drag-drop behaviour)
      return true;
   }

   // ---- Accessible bucket picker (checkbox list) ---------------------------
   // Renders a checkbox list of all bucket items accepted by the current target.
   // Checking a box adds the item to the export list; unchecking removes it.
   // This is the keyboard/screen-reader alternative to drag-and-drop.
   function renderPicker() {
      if (!pickerEl) return;
      pickerEl.innerHTML = '';
      const targetKey = targetSel.value;
      const target = TARGETS[targetKey] || TARGETS.zip;
      const accepted = bucket.items.filter((it) => itemAcceptedForTarget(it, targetKey));

      if (accepted.length === 0) {
         const empty = document.createElement('div');
         empty.className = 'xep-picker-empty';
         empty.textContent = bucket.items.length === 0
            ? 'Bucket is empty — add samples to the bucket first.'
            : 'No samples in the bucket match this export target.';
         pickerEl.appendChild(empty);
         if (pickerSelectAllBtn) pickerSelectAllBtn.disabled = true;
         return;
      }
      if (pickerSelectAllBtn) pickerSelectAllBtn.disabled = false;

      for (const item of accepted) {
         const id = item.id;
         const inList = exportItems.some((i) => i.id === id);
         const row = document.createElement('label');
         row.className = 'xep-picker-item';

         const cbWrap = document.createElement('span');
         cbWrap.className = 'xep-picker-checkbox';

         const checkbox = document.createElement('input');
         checkbox.type = 'checkbox';
         checkbox.id = `xep-picker-cb-${CSS.escape(id)}`;
         checkbox.checked = inList;
         checkbox.dataset.id = id;
         checkbox.setAttribute('aria-label', `${inList ? 'Remove' : 'Add'} ${item.name} ${inList ? 'from' : 'to'} export`);

         const checkmark = document.createElement('span');
         checkmark.className = 'checkmark';

         cbWrap.appendChild(checkbox);
         cbWrap.appendChild(checkmark);

         const labelText = document.createElement('span');
         labelText.className = 'xep-picker-label';
         labelText.textContent = `${item.name} (${item.type || 'wav'})`;

         checkbox.addEventListener('change', () => {
            if (checkbox.checked) {
               const ok = addItem(item);
               if (!ok) {
                  // addItem rejected it (e.g. slot limit, duplicate) — uncheck
                  checkbox.checked = false;
               } else {
                  checkbox.setAttribute('aria-label', `Remove ${item.name} from export`);
               }
            } else {
               removeItem(id);
               checkbox.setAttribute('aria-label', `Add ${item.name} to export`);
            }
         });

         row.appendChild(cbWrap);
         row.appendChild(labelText);
         pickerEl.appendChild(row);
      }
   }

   // Sync existing picker checkboxes with the current exportItems state
   // WITHOUT rebuilding the DOM (preserves screen-reader focus).
   function syncPickerChecked() {
      if (!pickerEl) return;
      const cbs = pickerEl.querySelectorAll('input[type="checkbox"]');
      cbs.forEach((cb) => {
         const inList = exportItems.some((i) => i.id === cb.dataset.id);
         if (cb.checked !== inList) cb.checked = inList;
      });
   }

   function addItem(bucketItem) {

      if (!bucketItem || !bucketItem.id) return false;
      if (exportItems.find((i) => i.id === bucketItem.id)) return false;
      const targetKey = targetSel.value;
      const target = TARGETS[targetKey] || TARGETS.zip;


      // MIDI target only accepts MIDI items
      if (targetKey === 'midi' && (bucketItem.type || 'wav') !== 'midi') {
         setStatus('only MIDI files can be added to a MIDI export', true);
         return false;
      }
      // MusicXML PDF target only accepts MIDI or MusicXML items
      if (targetKey === 'musicxmlpdf') {
         const itemType = bucketItem.type || 'wav';
         if (itemType !== 'midi' && itemType !== 'musicxml') {
            setStatus('only MIDI or MusicXML files can be added to a Sheet Music PDF export', true);
            return false;
         }
      }
      // Audio ZIP target: accept audio/WAV and module items — no MIDI, images, videos
      if (targetKey === 'zip') {
         const itemType = bucketItem.type || 'wav';
         if (itemType !== 'wav' && itemType !== 'audio' && itemType !== 'module') {
            setStatus(`Only audio or module files can be added to ZIP export (got ${itemType})`, true);
            return false;
         }
      }
      if (target.slots > 0 && exportItems.length >= target.slots) {
         setStatus(`slot limit reached (${target.slots})`, true);
         return false;
      }
      exportItems.push({ 
         id: bucketItem.id, 
         name: bucketItem.name, 
         wav: bucketItem.wav, 
         type: bucketItem.type || 'wav',
         pcm: bucketItem.pcm,
         sampleRate: bucketItem.sampleRate,
         channels: bucketItem.channels,
         bitDepth: bucketItem.bitDepth
      });
      renderList();
      return true;
   }

   function removeItem(id) {
      exportItems = exportItems.filter((i) => i.id !== id);
      renderList();
   }

   function clearItems() {
      exportItems = [];
      renderList();
      setStatus('');
   }

   // ---- Prefill (ZIP only) --------------------------------------------------
   function prefillAll() {
      exportItems = [];
      const targetKey = targetSel.value;
      const target = TARGETS[targetKey] || TARGETS.zip;
      
      for (const item of bucket.items) {
         const itemType = item.type || 'wav';
         // ZIP (audio) export: only include audio/WAV and module items — skip MIDI, images, videos
         if (targetKey === 'zip' && itemType !== 'wav' && itemType !== 'audio' && itemType !== 'module') {
            continue;
         }
         // allzip: include everything
         exportItems.push({ 
            id: item.id, 
            name: item.name, 
            wav: item.wav, 
            type: itemType,
            pcm: item.pcm,
            sampleRate: item.sampleRate,
            channels: item.channels,
            bitDepth: item.bitDepth
         });
      }
      renderList();
   }

   // ---- Config + target change ----------------------------------------------
   function renderConfig(target) {
      const fn = TARGET_CONFIGS[target];
      configEl.innerHTML = fn ? fn() : '';

      // Update capacity when any config dropdown changes (affects resampled size)
      const cfgEls = configEl.querySelectorAll('select, input');
      cfgEls.forEach(el => el.addEventListener('change', () => updateCapacity()));

      // Roland: dynamic sampler + rate dropdowns based on format selection
      const rolandFormatEl = document.getElementById('exp-roland-format');
      if (rolandFormatEl) {
         const updateRolandOptions = () => {
            const fmt = rolandFormatEl.value;
            const familyEl = document.getElementById('exp-roland-family');
            const rateEl = document.getElementById('exp-roland-rate');
            const noteEl = document.querySelector('.xep-roland-note');
            if (fmt === 's7') {
               // S-770/S-760/S-750 family
               if (familyEl) {
                  familyEl.innerHTML = '<option value="S-770">S-770</option><option value="S-760">S-760</option><option value="S-750">S-750</option>';
               }
               if (rateEl) {
                  rateEl.innerHTML = S7_SAMPLE_RATES.map(r => `<option value="${r.rate}">${r.label}</option>`).join('');
               }
               if (noteEl) noteEl.textContent = 'Drag samples from the bucket. Creates a 1.44 MB floppy image with 16-bit PCM — works with Gotek and ZuluSCSI.';
            } else {
               // S-50 family
               if (familyEl) {
                  familyEl.innerHTML = '<option value="S-550">S-550</option><option value="S-330">S-330</option><option value="S-50">S-50</option><option value="W-30">W-30</option>';
               }
               if (rateEl) {
                  rateEl.innerHTML = SDISK_SAMPLE_RATES.map(r => `<option value="${r.rate}">${r.label}</option>`).join('');
               }
               if (noteEl) noteEl.textContent = 'Drag samples from the bucket. Creates a 720 KB floppy image with 12-bit packed PCM — works with Gotek and ZuluSCSI.';
            }
            updateCapacity();
         };
         rolandFormatEl.addEventListener('change', updateRolandOptions);
         updateRolandOptions(); // initialize for current selection
      }

      updateCapacity();
   }

   // ---- Status --------------------------------------------------------------
   function setStatus(msg, isErr) {
      if (!statusEl) return;
      statusEl.textContent = msg;
      statusEl.style.color = isErr ? 'var(--c8)' : 'var(--ca)';
   }

   // ---- Drag from bucket panel ----------------------------------------------
   // The bucket renders rows with data-id on .bucketitem elements.
   // We listen for drops on the dropzone AND anywhere on the export panel.
   function handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      dropzone.classList.remove('xep-dz-active');

      // Try wavedit tab drop first
      const waveditTabId = e.dataTransfer.getData('application/x-wavedit-tab');
      if (waveditTabId) {
         // Dispatch event so wavedit can handle it (it has access to the AudioBuffer)
         window.dispatchEvent(new CustomEvent('wavedit-tab-drop-on-export', {
            detail: { tabId: waveditTabId }
         }));
         return;
      }

      // Try custom type first, then text/plain
      let id = e.dataTransfer.getData('application/x-bucket-item-id');
      if (!id) id = e.dataTransfer.getData('text/plain');
      if (!id) {
         // Try direct MIDI card drop
         const midiUrl = e.dataTransfer.getData('application/x-midi-url');
         const midiName = e.dataTransfer.getData('application/x-midi-sample-name');
         if (midiUrl && midiName) {
            handleDirectMidiDrop(midiUrl, midiName);
            return;
         }
         // Try direct module card drop
         const modUrl = e.dataTransfer.getData('application/x-module-url');
         const modName = e.dataTransfer.getData('application/x-module-sample-name');
         if (modUrl && modName) {
            handleDirectModuleDrop(modUrl, modName);
            return;
         }
         // Try direct video card drop
         const videoUrl = e.dataTransfer.getData('application/x-video-url');
         const videoName = e.dataTransfer.getData('application/x-video-name');
         if (videoUrl && videoName) {
            handleDirectVideoDrop(videoUrl, videoName);
            return;
         }
         return;
      }

      const item = bucket.items.find((i) => i.id === id);
      if (!item) return;
      
      // Create a full item copy with all audio data
      const fullItem = {
         id: item.id,
         name: item.name,
         wav: item.wav,
         type: item.type || 'wav',
         pcm: item.pcm,
         sampleRate: item.sampleRate,
         channels: item.channels,
         bitDepth: item.bitDepth
      };
      
      if (!addItem(fullItem)) return;
   }

   // ---- Handle direct MIDI card drops ----------------------------------------
   async function handleDirectMidiDrop(midiUrl, midiName) {
      try {
         // Fetch the MIDI data and add it as a bucket item first
         const data = await toArrayBuffer(midiUrl);
         if (!data || !data.byteLength) {
            setStatus('Failed to load MIDI data', true);
            return;
         }

         // Add to bucket with correct type
         const uniqueName = bucket.getUniqueName('default', midiName);
         const id = makeId('default', 'default', uniqueName);
         
         const item = { 
            id, 
            bucketId: 'default', 
            source: 'midi', 
            name: uniqueName, 
            addedAt: Date.now(), 
            wav: data, 
            type: 'midi' 
         };
         
         bucket.items.push(item);
         await dbPutItem(item);
         bucket.render();
         bucket.setStatus(`+ ${uniqueName}`);

         // Now add to export list
         const fullItem = {
            id: item.id,
            name: item.name,
            wav: item.wav,
            type: item.type,
            pcm: null,
            sampleRate: null,
            channels: null,
            bitDepth: null
         };

         addItem(fullItem);
      } catch (err) {
         console.error('[export-modal] Failed to load MIDI:', err);
         setStatus('Failed to load MIDI data', true);
      }
   }

   // ---- Handle direct module card drops (MOD/XM/S3M/IT) ---------------------
   async function handleDirectModuleDrop(modUrl, modName) {
      try {
         const data = await toArrayBuffer(modUrl);
         if (!data || !data.byteLength) {
            setStatus('Failed to load module data', true);
            return;
         }

         // Add to bucket with module type
         const uniqueName = bucket.getUniqueName('default', modName);
         const id = makeId('default', 'default', uniqueName);

         const item = {
            id,
            bucketId: 'default',
            source: 'module',
            name: uniqueName,
            addedAt: Date.now(),
            wav: data,
            type: 'module'
         };

         bucket.items.push(item);
         await dbPutItem(item);
         bucket.render();
         bucket.setStatus(`+ ${uniqueName}`);

         // Now add to export list
         const fullItem = {
            id: item.id,
            name: item.name,
            wav: item.wav,
            type: item.type,
            pcm: null,
            sampleRate: null,
            channels: null,
            bitDepth: null
         };

         addItem(fullItem);
      } catch (err) {
         console.error('[export-modal] Failed to load module:', err);
         setStatus('Failed to load module data', true);
      }
   }

   // ---- Handle direct video card drops ---------------------------------------
   async function handleDirectVideoDrop(videoUrl, videoName) {
      try {
         const data = await toArrayBuffer(videoUrl);
         if (!data || !data.byteLength) {
            setStatus('Failed to load video data', true);
            return;
         }

         // Add to bucket with video type — bucket.add() will convert if needed
         await bucket.add({
            source: 'raw',
            name: videoName,
            wavLike: videoUrl,
            fileType: 'video',
            videoData: data,
            videoFormat: (videoName.split('.').pop() || '').toLowerCase(),
         });

         // Now add to export list
         const item = bucket.items[bucket.items.length - 1];
         if (item) {
            const fullItem = {
               id: item.id,
               name: item.name,
               wav: item.wav,
               type: item.type,
               pcm: null,
               sampleRate: null,
               channels: null,
               bitDepth: null
            };
            addItem(fullItem);
         }
      } catch (err) {
         console.error('[export-modal] Failed to load video:', err);
         setStatus('Failed to load video data', true);
      }
   }

   [dropzone, sampleList].forEach((el) => {
      el.addEventListener('dragover', (e) => {
         e.preventDefault();
         dropzone.classList.add('xep-dz-active');
         e.dataTransfer.dropEffect = 'copy';
      });
      el.addEventListener('dragleave', (e) => {
         if (!panelEl.contains(e.relatedTarget)) {
            dropzone.classList.remove('xep-dz-active');
         }
      });
      el.addEventListener('drop', handleDrop);
   });

   // Also allow drop anywhere on the panel (catches drops outside dropzone/sampleList)
   panelEl.addEventListener('dragover', (e) => {
      if (e.dataTransfer.types.includes('application/x-wavedit-tab') ||
          e.dataTransfer.types.includes('application/x-bucket-item-id') ||
          e.dataTransfer.types.includes('text/plain')) {
         e.preventDefault();
         e.dataTransfer.dropEffect = 'copy';
      }
   });
   panelEl.addEventListener('drop', handleDrop);

   // ---- Audio conversion with FFmpeg.wasm ------------------------------------
   async function ensureFfmpeg() {
      // Try to use window.getFFmpeg from xtrc.js
      if (typeof window !== 'undefined' && window.getFFmpeg) {
         try {
            return await window.getFFmpeg();
         } catch (err) {
            console.error('[export-modal] Failed to get FFmpeg via window.getFFmpeg:', err);
         }
      }
      
      // Fallback: try window.ffmpeg (already loaded)
      if (typeof window !== 'undefined' && window.ffmpeg) {
         return window.ffmpeg;
      }
      
      return null;
   }

   async function convertAudio(wavBuffer, format, index, total) {
      const ffmpeg = await ensureFfmpeg();
      if (!ffmpeg) {
         return wavBuffer;
      }

      const inputName = `input_${Date.now()}.wav`;
      const outputName = `output_${Date.now()}.${format.ext}`;

      try {
         // Write input file - create a copy to avoid detached ArrayBuffer
         const wavCopy = new Uint8Array(wavBuffer.byteLength);
         wavCopy.set(new Uint8Array(wavBuffer));
         await ffmpeg.writeFile(inputName, wavCopy);
         
         // Build FFmpeg command
         const cmd = ['-i', inputName];
         if (format.bitrate) {
            cmd.push('-b:a', format.bitrate);
         }
         cmd.push('-c:a', format.codec);
         cmd.push('-y');
         cmd.push(outputName);
         
         
         // Listen to FFmpeg progress
         ffmpeg.on('progress', ({ progress, time }) => {
            const fileProgress = Math.round(progress * 100);
            const overallProgress = Math.round(((index) / total) * 80 + (progress * 80 / total));
            updateProgress(`Converting to ${format.label}…`, overallProgress, `${index + 1}/${total} - ${fileProgress}%`);
         });
         
         // Run conversion
         await ffmpeg.exec(cmd);
         
         // Remove progress listener
         ffmpeg.off('progress');
         
         // Read output file
         const outputData = await ffmpeg.readFile(outputName);
         
         // Cleanup
         await ffmpeg.deleteFile(inputName);
         await ffmpeg.deleteFile(outputName);
         
         return outputData.buffer;
      } catch (err) {
         console.error('[export-modal] FFmpeg conversion failed:', err);
         console.error('[export-modal] Error details:', err.message, err.stack);
         // Remove progress listener on error
         try { ffmpeg.off('progress'); } catch (_) {}
         // Return original WAV on error
         return wavBuffer;
      }
   }

   // ---- Export run ----------------------------------------------------------
   async function run() {
      const target = targetSel.value;
      // RBM builds from the packer slots (defaults + user replacements), not the
      // bucket export list, so it does not require items here.
      // Electribe also builds from its own slot grid, not the export list.
      // SF2 builds from its own sample grid, not the export list.
      if (target !== 'rbm' && target !== 'electribe' && target !== 'sf2' && !exportItems.length) { setStatus('no samples in export list', true); return; }

      const totalBytes = exportItems.reduce((s, i) => s + (i.wav ? i.wav.byteLength : 0), 0);
      const tgt = TARGETS[target];

      // For DSS-1, compute actual 12-bit packed on-disk size after resampling
      let capacityBytes = totalBytes;
      if (target === 'dss1') {
         capacityBytes = 0;
         const dss1Rates = [16000, 24000, 32000, 48000];
         const dss1RateEl = document.getElementById('exp-dss1-rate');
         const dss1UserRate = dss1RateEl ? parseInt(dss1RateEl.value) : 0;
         for (const it of exportItems) {
            if (!it.wav) continue;
            const dv = new DataView(it.wav);
            let off = 12, dataLen = 0, numChannels = 1, bitsPerSample = 16, sampleRate = 32000;
            while (off + 8 <= it.wav.byteLength) {
               const cid = dv.getUint32(off, true);
               const csz = dv.getUint32(off + 4, true);
               if (cid === 0x20746d66) { numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); sampleRate = dv.getUint32(off + 12, true); }
               else if (cid === 0x61746164) { dataLen = csz; break; }
               off += 8 + csz + (csz & 1);
            }
            const bps = (bitsPerSample / 8) * numChannels;
            let numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
            // Estimate resampled size
            let bestRate;
            if (dss1UserRate > 0) {
               bestRate = dss1UserRate;
            } else {
               bestRate = 32000;
               for (const r of dss1Rates) { if (Math.abs(sampleRate - r) < Math.abs(sampleRate - bestRate)) bestRate = r; }
            }
            if (sampleRate !== bestRate && sampleRate > 0) {
               numSamples = Math.floor(numSamples * bestRate / sampleRate);
            }
            const FIRST_SAMPLES = 450;
            const SAMPLES_PER_SECTOR = 682;
            let remaining = numSamples;
            remaining -= Math.min(remaining, FIRST_SAMPLES);
            const numDataSectors = Math.ceil(remaining / SAMPLES_PER_SECTOR);
            capacityBytes += (1 + numDataSectors) * DSS1_SECTOR_SIZE;
         }
      } else if (target === 'casio') {
         // FZ-1: compute on-disk size after resampling to target rate
         const casioRateEl = document.getElementById('exp-casio-rate');
         const casioTargetRate = casioRateEl ? parseInt(casioRateEl.value) : 36000;
         capacityBytes = 0;
         for (const it of exportItems) {
            if (!it.wav) continue;
            const dv = new DataView(it.wav);
            let off = 12, dataLen = 0, numChannels = 1, bitsPerSample = 16, sampleRate = 36000;
            while (off + 8 <= it.wav.byteLength) {
               const cid = dv.getUint32(off, true);
               const csz = dv.getUint32(off + 4, true);
               if (cid === 0x20746d66) { numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); sampleRate = dv.getUint32(off + 12, true); }
               else if (cid === 0x61746164) { dataLen = csz; break; }
               off += 8 + csz + (csz & 1);
            }
            const bps = (bitsPerSample / 8) * numChannels;
            let numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
            // Estimate resampled size
            if (sampleRate !== casioTargetRate && sampleRate > 0) {
               numSamples = Math.floor(numSamples * casioTargetRate / sampleRate);
            }
            const pcmBytes = numSamples * 2; // 16-bit output
            capacityBytes += pcmBytes; // PCM pool
         }
         // Add voice descriptor overhead
         const voiceBytes = exportItems.filter(it => it.wav).length * 256;
         const voiceSectors = Math.ceil(voiceBytes / 1024);
         capacityBytes += voiceSectors * 1024;
      } else if (target === 'emu') {
         // EMU EOS: E3S1 chunks (8B marker+size + 98B header + 16-bit LE PCM)
         const emuRateEl = document.getElementById('exp-emu-rate');
         const emuTargetRate = emuRateEl ? parseInt(emuRateEl.value) : 44100;
         capacityBytes = 0;
         for (const it of exportItems) {
            if (!it.wav) continue;
            const dv = new DataView(it.wav);
            let off = 12, dataLen = 0, numChannels = 1, bitsPerSample = 16, sampleRate = 44100;
            while (off + 8 <= it.wav.byteLength) {
               const cid = dv.getUint32(off, true);
               const csz = dv.getUint32(off + 4, true);
               if (cid === 0x20746d66) { numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); sampleRate = dv.getUint32(off + 12, true); }
               else if (cid === 0x61746164) { dataLen = csz; break; }
               off += 8 + csz + (csz & 1);
            }
            const bps = (bitsPerSample / 8) * numChannels;
            let numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
            if (sampleRate !== emuTargetRate && sampleRate > 0) {
               numSamples = Math.floor(numSamples * emuTargetRate / sampleRate);
            }
            const pcmBytes = numSamples * 2;
            capacityBytes += 8 + EMU_E3S1_HDR_SIZE + pcmBytes;
         }
         // Add disk overhead (directory + data offset)
         capacityBytes += 0x1000; // EMU_DATA_OFFSET
      } else if (target === 'roland') {
         // Roland: S-50 family (12-bit packed) or S-7x0 (16-bit PCM)
         const rolandRateEl = document.getElementById('exp-roland-rate');
         const rolandTargetRate = rolandRateEl ? parseInt(rolandRateEl.value) : 30000;
         const rolandFmtEl = document.getElementById('exp-roland-format');
         const rolandFmt = rolandFmtEl ? rolandFmtEl.value : 'sdisk';
         capacityBytes = 0;
         for (const it of exportItems) {
            if (!it.wav) continue;
            const dv = new DataView(it.wav);
            let off = 12, dataLen = 0, numChannels = 1, bitsPerSample = 16, sampleRate = rolandFmt === 's7' ? 44100 : 30000;
            while (off + 8 <= it.wav.byteLength) {
               const cid = dv.getUint32(off, true);
               const csz = dv.getUint32(off + 4, true);
               if (cid === 0x20746d66) { numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); sampleRate = dv.getUint32(off + 12, true); }
               else if (cid === 0x61746164) { dataLen = csz; break; }
               off += 8 + csz + (csz & 1);
            }
            const bps = (bitsPerSample / 8) * numChannels;
            let numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
            if (sampleRate !== rolandTargetRate && sampleRate > 0) {
               numSamples = Math.floor(numSamples * rolandTargetRate / sampleRate);
            }
            if (rolandFmt === 's7') {
               // S-770/S-760/S-750: 16-bit LE PCM, 8192-byte aligned units
               const pcmBytes = numSamples * 2;
               capacityBytes += Math.ceil(pcmBytes / 8192) * 8192;
            } else {
               // S-50 family: 12-bit packed, 18432 bytes per block (12288 samples)
               const numBlocks = Math.ceil(numSamples / 12288);
               capacityBytes += numBlocks * 18432;
            }
         }
      } else if (target === 'mpc') {
         // MPC: 42-byte SND header + 16-bit LE PCM, 512-byte cluster aligned
         capacityBytes = 0;
         for (const it of exportItems) {
            if (!it.wav) continue;
            const dv = new DataView(it.wav);
            let off = 12, dataLen = 0, numChannels = 1, bitsPerSample = 16, sampleRate = 44100;
            while (off + 8 <= it.wav.byteLength) {
               const cid = dv.getUint32(off, true);
               const csz = dv.getUint32(off + 4, true);
               if (cid === 0x20746d66) { numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); sampleRate = dv.getUint32(off + 12, true); }
               else if (cid === 0x61746164) { dataLen = csz; break; }
               off += 8 + csz + (csz & 1);
            }
            const bps = (bitsPerSample / 8) * numChannels;
            let numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
            if (sampleRate !== MPC_SAMPLE_RATE && sampleRate > 0) {
               numSamples = Math.floor(numSamples * MPC_SAMPLE_RATE / sampleRate);
            }
            const sndBytes = MPC_SND_HEADER_SIZE + numSamples * 2;
            capacityBytes += Math.ceil(sndBytes / MPC_SECTOR_SIZE) * MPC_SECTOR_SIZE;
         }
      } else if (target === 'yamaha') {
         // Yamaha: 512-byte SMPL header + 16-bit BE PCM + 356-byte SBNK, cluster aligned
         capacityBytes = 0;
         for (const it of exportItems) {
            if (!it.wav) continue;
            const dv = new DataView(it.wav);
            let off = 12, dataLen = 0, numChannels = 1, bitsPerSample = 16, sampleRate = 44100;
            while (off + 8 <= it.wav.byteLength) {
               const cid = dv.getUint32(off, true);
               const csz = dv.getUint32(off + 4, true);
               if (cid === 0x20746d66) { numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); sampleRate = dv.getUint32(off + 12, true); }
               else if (cid === 0x61746164) { dataLen = csz; break; }
               off += 8 + csz + (csz & 1);
            }
            const bps = (bitsPerSample / 8) * numChannels;
            let numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
            if (sampleRate !== 44100 && sampleRate > 0) {
               numSamples = Math.floor(numSamples * 44100 / sampleRate);
            }
            // SMPL file: 512-byte header + 2 bytes/sample, cluster aligned
            const smplBytes = Math.ceil((512 + numSamples * 2) / 512) * 512;
            // SBNK file: 356 bytes, 1 cluster
            capacityBytes += smplBytes + 512;
         }
         // Directory overhead: disk dir + vol dir + SBNK dir + SMPL dir + 0000 files + disk name
         const dirOverhead = 8 * 512 + Math.ceil(exportItems.length / 16) * 512 * 2;
         capacityBytes += dirOverhead;
      }

      if (tgt && tgt.limit > 0 && capacityBytes > tgt.limit) {
         // EMU: use media-specific limit for error message
         let effectiveLimit = tgt.limit;
         if (target === 'emu') {
            const emuMediaEl = document.getElementById('exp-emu-media');
            const emuMedia = emuMediaEl ? emuMediaEl.value : 'floppy';
            effectiveLimit = emuMedia === 'disk' ? EMU_DISK_SIZE : EMU_FLOPPY_SIZE;
         }
         if (target === 'roland') {
            const rolandFmtEl2 = document.getElementById('exp-roland-format');
            const rolandFmt2 = rolandFmtEl2 ? rolandFmtEl2.value : 'sdisk';
            effectiveLimit = rolandFmt2 === 's7'
               ? S7_DISK_SIZE - S7_OFFSETS.AUDIO_START
               : 2 * SDISK_BLOCKS_PER_BANK * SDISK_BLOCK_BYTES;
         }
         if (target === 'mpc') {
            effectiveLimit = MPC_DISK_SIZE - 0x4200;
         }
         if (target === 'yamaha') {
            effectiveLimit = 1474560 - 0x4200;
         }
         if (capacityBytes > effectiveLimit) {
            const msg = `over capacity by ${fmtBytes(capacityBytes - effectiveLimit)} — remove some samples, use a lower sample rate, or switch to ${target === 'emu' ? 'hard disk' : 'HD'} format`;
            setStatus(msg, true);
            if (typeof showToast === 'function') showToast(msg, 'Export capacity exceeded', 'error');
            return;
         }
      }

      setStatus('exporting…');
      runBtn.disabled = true;
      try {
         const items = exportItems.slice();

         if (target === 'zip') {
            const prefixEl = document.getElementById('exp-zip-prefix');
            const formatEl = document.getElementById('exp-zip-format');
            const normEl   = document.getElementById('exp-zip-normalize');
            const prefix    = prefixEl ? prefixEl.value : 'xtrc_export';
            const formatKey = formatEl ? formatEl.value : 'wav';
            const normalize = normEl ? normEl.checked : true;
            const format    = AUDIO_FORMATS[formatKey] || AUDIO_FORMATS.wav;

            // Process files one by one with progress
            const total = items.length;
            const processedFiles = [];
            
            showProgress();
            
            for (let i = 0; i < total; i++) {
               const it = items[i];
               const raw  = (it.name || 'sample').replace(/\.wav$/i, '');
               const safe = normalize ? raw.replace(/[^a-zA-Z0-9_-]/g, '_') : raw;
               
               let audioData = it.wav;
               
               // Convert audio if format is not WAV
               if (formatKey !== 'wav') {
                  updateProgress(`Converting ${safe} to ${format.label}…`, Math.round((i / total) * 80), `${i + 1}/${total}`);
                  audioData = await convertAudio(it.wav, format, i, total);
               } else {
                  updateProgress(`Processing ${safe}.wav`, Math.round(((i + 1) / total) * 80), `${i + 1}/${total}`);
               }
               
               processedFiles.push({
                  name: `${prefix || 'xtrc_export'}/${safe}.${format.ext}`,
                  lastModified: new Date(),
                  input: new Blob([audioData], { type: `audio/${format.container}` }),
               });
            }
            
            updateProgress('Creating ZIP archive…', 90, 'Compressing files');
            
            async function* gen() {
               for (const file of processedFiles) {
                  yield file;
               }
            }
            
            const blob = await downloadZip(gen()).blob();
            updateProgress('Downloading…', 95, 'Starting download');
            triggerDownload(blob, `${prefix || 'xtrc_export'}.zip`);

         } else if (target === 'allzip') {
            // All-files ZIP: zip everything as-is, preserving original file types
            const prefixEl  = document.getElementById('exp-allzip-prefix');
            const normEl    = document.getElementById('exp-allzip-normalize');
            const prefix    = (prefixEl ? prefixEl.value : 'xtrc_export').trim() || 'xtrc_export';
            const normalize = normEl ? normEl.checked : true;

            showProgress();

            const total = items.length;
            const processedFiles = [];

            // MIME type lookup by extension
            const mimeByExt = {
               wav: 'audio/wav', mp3: 'audio/mpeg', ogg: 'audio/ogg', flac: 'audio/flac',
               aac: 'audio/aac', aiff: 'audio/aiff', m4a: 'audio/mp4', opus: 'audio/opus',
               mid: 'audio/midi', midi: 'audio/midi', mod: 'audio/mod', xm: 'audio/xm',
               s3m: 'audio/s3m', it: 'audio/it',
               png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
               bmp: 'image/bmp', webp: 'image/webp', tga: 'image/x-tga', tiff: 'image/tiff',
               tif: 'image/tiff', pcx: 'image/x-pcx', psd: 'image/x-psd', ico: 'image/x-icon',
               mp4: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg', mov: 'video/quicktime',
               avi: 'video/x-msvideo', mkv: 'video/x-matroska', flv: 'video/x-flv',
               wmv: 'video/x-ms-wmv', m4v: 'video/mp4',
            };

            for (let i = 0; i < total; i++) {
               const it = items[i];
               const raw  = it.name || `file_${i}`;
               const safe = normalize ? raw.replace(/[^a-zA-Z0-9._-]/g, '_') : raw;
               const ext  = (raw.split('.').pop() || '').toLowerCase();
               const mime = mimeByExt[ext] || 'application/octet-stream';

               updateProgress(`Adding ${safe}…`, Math.round(((i + 1) / total) * 80), `${i + 1}/${total}`);

               processedFiles.push({
                  name: `${prefix}/${safe}`,
                  lastModified: new Date(),
                  input: new Blob([it.wav], { type: mime }),
               });
            }

            updateProgress('Creating ZIP archive…', 90, 'Compressing files');

            async function* genAll() {
               for (const file of processedFiles) {
                  yield file;
               }
            }

            const allBlob = await downloadZip(genAll()).blob();
            updateProgress('Downloading…', 95, 'Starting download');
            triggerDownload(allBlob, `${prefix}.zip`);

         } else if (target === 'midi') {
            // MIDI folder export: zip the user-selected MIDI files into a folder.
            const prefixEl  = document.getElementById('exp-midi-prefix');
            const normEl    = document.getElementById('exp-midi-normalize');
            const prefix    = (prefixEl ? prefixEl.value : 'xtrc_midi').trim() || 'xtrc_midi';
            const normalize = normEl ? normEl.checked : true;

            showProgress();

            const total = items.length;
            const processedFiles = [];
            for (let i = 0; i < total; i++) {
               const it = items[i];
               const raw  = (it.name || 'track').replace(/\.mid$/i, '');
               const safe = normalize ? raw.replace(/[^a-zA-Z0-9_-]/g, '_') : raw;
               updateProgress(`Adding ${safe}.mid…`, Math.round(((i + 1) / total) * 80), `${i + 1}/${total}`);
               processedFiles.push({
                  name: `${prefix}/${safe}.mid`,
                  lastModified: new Date(),
                  input: new Blob([it.wav], { type: 'audio/midi' }),
               });
            }

            updateProgress('Creating ZIP archive…', 90, 'Compressing files');

            async function* gen() {
               for (const file of processedFiles) {
                  yield file;
               }
            }

            const blob = await downloadZip(gen()).blob();
            updateProgress('Downloading…', 95, 'Starting download');
            triggerDownload(blob, `${prefix}.zip`);

         } else if (target === 'musicxmlpdf') {
            // Sheet Music PDF export: render each MIDI/MusicXML file as a PDF score.
            // Uses OpenSheetMusicDisplay to render MusicXML to SVG, then converts
            // each page's SVG to a canvas image and composes a multi-page PDF.
            const sizeEl    = document.getElementById('exp-musicxmlpdf-size');
            const normEl    = document.getElementById('exp-musicxmlpdf-normalize');
            const paperSize = sizeEl ? sizeEl.value : 'a4';
            const normalize = normEl ? normEl.checked : true;

            showProgress();
            setStatus('Initializing PDF export…');
            await new Promise(r => setTimeout(r, 0));

            const total = items.length;
            const pdfFiles = [];

            for (let i = 0; i < total; i++) {
               const it = items[i];
               const raw  = (it.name || 'score').replace(/\.(mid|midi|xml|musicxml|mxl)$/i, '');
               const safe = normalize ? raw.replace(/[^a-zA-Z0-9_-]/g, '_') : raw;

               updateProgress(`Rendering ${safe} (${i + 1}/${total})…`, Math.round(((i + 1) / total) * 80), `${i + 1}/${total}`);
               if (i > 0) await new Promise(r => setTimeout(r, 200));

               let renderDiv = null;
               try {
                  let musicXml;

                  // Convert MIDI to MusicXML, or use existing MusicXML as-is
                  if (it.type === 'midi') {
                     const midi = new Midi(it.wav);
                     musicXml = midiToMusicXml(midi, safe);
                  } else if (it.type === 'musicxml') {
                     musicXml = new TextDecoder().decode(it.wav);
                  } else {
                     continue;
                  }

                  await new Promise(r => setTimeout(r, 0));

                  // Create a hidden render container with EXPLICIT pixel dimensions.
                  // OSMD's layout engine needs a container with real width/height —
                  // an off-screen div with no explicit size renders at 0×0, producing
                  // "width not > 0 in measure N" warnings and blank SVGs.
                  // A4 = 210×297mm → 794×1123px @ 96dpi; Letter = 216×279mm → 816×1056px.
                  //
                  // CRITICAL: The container height must be EXACTLY one page height.
                  // OSMD uses the container height to decide when to break to a new
                  // page. If the container is taller than one page (or has no fixed
                  // height), OSMD puts everything on a single page. With a fixed
                  // one-page height, OSMD creates multiple osmdCanvasPageN divs
                  // stacked vertically — they overflow the container, but without
                  // overflow:hidden they remain in the DOM and are queryable.
                  const pagePxW = paperSize === 'a4' ? 794 : 816;
                  const pagePxH = paperSize === 'a4' ? 1123 : 1056;
                  renderDiv = document.createElement('div');
                  renderDiv.style.position = 'absolute';
                  renderDiv.style.left = '-9999px';
                  renderDiv.style.top = '0';
                  renderDiv.style.width = pagePxW + 'px';
                  renderDiv.style.height = pagePxH + 'px';
                  renderDiv.style.background = 'white';
                  // NO overflow:hidden — pages beyond the first must remain in the DOM.
                  renderDiv.style.overflow = 'visible';
                  document.body.appendChild(renderDiv);

                  await new Promise(r => setTimeout(r, 0));

                  // Render with OpenSheetMusicDisplay.
                  // pageFormat tells OSMD the target paper size for pagination.
                  // OSMD's StringToPageFormat expects keys like "A4_P", "A4_L",
                  // "Letter_P", "Letter_L" — NOT "a4" or "letter". If the format
                  // string doesn't match, OSMD falls back to UndefinedPageFormat
                  // (Endless), rendering everything on a single endless page.
                  const osmdPageFormat = paperSize === 'a4' ? 'A4_P' : 'Letter_P';
                  const osmd = new OpenSheetMusicDisplay(renderDiv, {
                     autoResize: false,
                     drawTitle: true,
                     drawSubtitle: true,
                     drawComposer: true,
                     drawCredits: true,
                     drawPartNames: true,
                     drawMeasureNumbers: false,
                     drawFingerings: true,
                     defaultFontFamily: 'Arial',
                     defaultFontSize: 14,
                     pageFormat: osmdPageFormat,
                     stretchLastPage: false,
                  });

                  await osmd.load(musicXml);
                  osmd.render();

                  // OSMD renders one <svg> per page inside wrapper divs
                  // (id="osmdCanvasPageN"). Each SVG has id="osmdSvgPageN".
                  const svgPages = renderDiv.querySelectorAll('svg');
                  if (svgPages.length === 0) throw new Error('OSMD produced no pages');

                  // Create the PDF
                  const pdf = new jsPDF({
                     orientation: 'portrait',
                     unit: 'pt',
                     format: paperSize === 'a4' ? 'a4' : 'letter',
                  });
                  const pdfW = pdf.internal.pageSize.getWidth();
                  const pdfH = pdf.internal.pageSize.getHeight();

                  for (let p = 0; p < svgPages.length; p++) {
                     updateProgress(`Page ${p + 1}/${svgPages.length} of ${safe}…`, Math.round(((i + 1) / total) * 80), `${i + 1}/${total}`);

                     const svg = svgPages[p];

                     // ---- Determine SVG pixel dimensions ----
                     // Try width/height attributes, then viewBox, then bounding rect
                     let svgW = 0, svgH = 0;
                     const wAttr = svg.getAttribute('width');
                     const hAttr = svg.getAttribute('height');
                     if (wAttr) svgW = parseFloat(wAttr);
                     if (hAttr) svgH = parseFloat(hAttr);
                     if (!svgW || !svgH) {
                        // Try SVGAnimatedLength API
                        if (svg.width && svg.width.baseVal && svg.width.baseVal.unitType !== 0) svgW = svg.width.baseVal.value;
                        if (svg.height && svg.height.baseVal && svg.height.baseVal.unitType !== 0) svgH = svg.height.baseVal.value;
                     }
                     if (!svgW || !svgH) {
                        const vb = svg.getAttribute('viewBox');
                        if (vb) {
                           const parts = vb.split(/[\s,]+/);
                           svgW = parseFloat(parts[2]) || 0;
                           svgH = parseFloat(parts[3]) || 0;
                        }
                     }
                     if (!svgW || !svgH) {
                        const rect = svg.getBoundingClientRect();
                        svgW = rect.width || 800;
                        svgH = rect.height || 1100;
                     }

                     // ---- Clone SVG and prepare for standalone serialization ----
                     // XMLSerializer on SVG elements inside HTML documents does NOT
                     // emit the xmlns attribute. Without it, Image.src = svgData
                     // loads but renders blank. We must inject it explicitly.
                     const svgClone = svg.cloneNode(true);
                     svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                     svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
                     // Set explicit pixel dimensions so the Image has intrinsic size
                     svgClone.setAttribute('width', svgW);
                     svgClone.setAttribute('height', svgH);

                     let svgData = new XMLSerializer().serializeToString(svgClone);
                     // Safety: ensure xmlns is present (some serializers strip it)
                     if (svgData.indexOf('xmlns') === -1) {
                        svgData = svgData.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
                     }

                     // Use a data URL — blob URLs can have dimension issues in some browsers
                     const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);

                     // ---- Load SVG as Image → draw to canvas ----
                     const img = new Image();
                     img.width = svgW;
                     img.height = svgH;
                     await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = () => reject(new Error(`Failed to render page ${p + 1} as image`));
                        img.src = svgDataUrl;
                     });

                     const scale = 2; // 2x for crisp output
                     const canvas = document.createElement('canvas');
                     canvas.width = Math.ceil(svgW * scale);
                     canvas.height = Math.ceil(svgH * scale);
                     const ctx = canvas.getContext('2d');
                     ctx.fillStyle = '#ffffff';
                     ctx.fillRect(0, 0, canvas.width, canvas.height);
                     ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                     // ---- Add canvas image to PDF ----
                     const imgData = canvas.toDataURL('image/png');
                     if (p > 0) pdf.addPage();

                     // Fit image to page preserving aspect ratio
                     const imgRatio = canvas.height / canvas.width;
                     let drawW = pdfW;
                     let drawH = pdfW * imgRatio;
                     if (drawH > pdfH) {
                        drawH = pdfH;
                        drawW = pdfH / imgRatio;
                     }
                     pdf.addImage(imgData, 'PNG', (pdfW - drawW) / 2, 0, drawW, drawH);
                  }

                  // Clean up render div
                  document.body.removeChild(renderDiv);
                  renderDiv = null;

                  const pdfBlob = pdf.output('blob');
                  pdfFiles.push({
                     name: `${safe}.pdf`,
                     lastModified: new Date(),
                     input: pdfBlob,
                  });

               } catch (err) {
                  console.error(`[export-modal] Failed to render ${safe} as PDF:`, err);
                  setStatus(`Failed to render ${safe}: ${err.message}`, true);
                  if (renderDiv && document.body.contains(renderDiv)) {
                     document.body.removeChild(renderDiv);
                  }
               }
            }

            if (pdfFiles.length === 0) {
               setStatus('No PDF files were generated. Try exporting as MIDI instead.', true);
               runBtn.disabled = false;
               return;
            }

            if (pdfFiles.length > 1) {
               updateProgress('Creating ZIP archive…', 90, 'Compressing files');
               async function* gen() {
                  for (const file of pdfFiles) yield file;
               }
               const blob = await downloadZip(gen()).blob();
               updateProgress('Downloading…', 95, 'Starting download');
               triggerDownload(blob, 'scores.zip');
            } else {
               updateProgress('Downloading…', 95, 'Starting download');
               triggerDownload(pdfFiles[0].input, pdfFiles[0].name);
            }

         } else if (target === 'rbm') {
            // RBM export: assemble a full standard-structure mod from the packer
            // slots (samples/images/song) + text fields, with proper encryption.
            const modNameEl = document.getElementById('exp-rbm-name');
            const modName   = (modNameEl ? modNameEl.value : 'My Mod').trim() || 'My Mod';

            showProgress();

            // Make sure the default files are loaded (in case the tab was never opened).
            updateProgress('Loading mod content…', 10, 'Preparing samples, images, song');
            await loadDefaultSamples();
            await loadDefaultImages();
            await loadDefaultTexts();
            await loadDefaultRbs();

            // Text fields (INFO chunk): map text slots by name.
            const textByName = {};
            for (const t of rbmTextSlots) textByName[t.slotName.toLowerCase()] = t.content || '';
            const comments = textByName['comments'] || '';
            const www      = textByName['www'] || '';
            const ftp      = textByName['ftp'] || '';
            const infoModName = (textByName['modname'] || modName).trim() || modName;

            // Fetch the CD data required for encryption (cached in IndexedDB).
            updateProgress('Loading encryption data…', 20, 'RB20FUL.DAT');
            const cdBytes = await ensureRbmCdData((pct, msg) => updateProgress(msg, 20 + Math.round(pct * 0.4), ''));

            updateProgress('Building RBM file…', 65, 'Assembling embedded files');

            const exporter = new RBMExporter({
               modName: infoModName,
               cdBytes,
               comments, www, ftp,
            });

            // Images (JPEG). Use the slot filename (RB-338 references images by name).
            for (const slot of rbmImageSlots) {
               if (slot.data) exporter.addFile(slot.fileName, new Uint8Array(slot.data), 'image');
            }
            // Song/patch bank (.rbs).
            if (rbmFiles.rbs && rbmFiles.rbs.data) {
               exporter.addFile(rbmFiles.rbs.name || 'Demo.rbs', new Uint8Array(rbmFiles.rbs.data), 'rbs');
            }
            // Samples (AIFF; WAV is auto-converted by the exporter).
            for (const slot of rbmSampleSlots) {
               if (slot.data) exporter.addFile(slot.fileName, new Uint8Array(slot.data), 'audio');
            }

            updateProgress('Encrypting & finalizing…', 85, 'Building container');

            const rbmData = exporter.build();
            const blob = new Blob([rbmData], { type: 'application/octet-stream' });
            // Keep the original mod name verbatim for the filename (RB-338 lists
            // mods by filename). Only strip characters illegal in filenames;
            // spaces, apostrophes, etc. are preserved.
            const filename = (modName.replace(/[\\/:*?"<>|]/g, '').trim() || 'mod') + '.rbm';
            updateProgress('Downloading…', 95, 'Starting download');
            triggerDownload(blob, filename);

         } else if (target === 'electribe') {
            await runElectribeExport(target);

         } else if (target === 'sf2') {
            await runSF2Export(target);

         } else if (target === 'dss1') {
            await runDss1Export(target);

         } else if (target === 'triton') {
            await runTritonExport(target);

         } else if (target === 'casio') {
            await runCasioExport(target);

         } else if (target === 'emu') {
            await runEmuExport(target);

         } else if (target === 'emue4b') {
            await runEmuE4bExport(target);

         } else if (target === 'roland') {
            await runRolandExport(target);

         } else if (target === 'mpc') {
            await runMpcExport(target);

         } else if (target === 'yamaha') {
            await runYamahaExport(target);

         } else if (target === 'rx2') {
            await runRex2Export(target);

         } else {
            // Hardware stub: WAV zip with target-specific naming
            const volEl = document.getElementById('exp-akai-vol') || document.getElementById('exp-roland-vol') || document.getElementById('exp-emu-bank') || document.getElementById('exp-gig-name');
            const prefix = (volEl ? volEl.value : 'XTRC').replace(/[^a-zA-Z0-9_]/g, '_') || 'XTRC';

            showProgress();
            updateProgress('Preparing samples…', 30, 'Processing');

            async function* gen() {
               for (let i = 0; i < items.length; i++) {
                  const it   = items[i];
                  const safe = (it.name || `sample_${i}`).replace(/\.wav$/i, '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 20).toUpperCase();
                  const fname = target === 'rex2'
                     ? `${String(i).padStart(3, '0')}_${safe}.wav`
                     : `${safe}.WAV`;
                  updateProgress('Creating archive…', 30 + Math.round(((i + 1) / items.length) * 50), `${i + 1}/${items.length}`);
                  yield {
                     name: `${prefix}/${fname}`,
                     lastModified: new Date(),
                     input: new Blob([it.wav], { type: 'audio/wav' }),
                  };
               }
            }
            updateProgress('Compressing…', 85, 'Creating ZIP');
            const blob = await downloadZip(gen()).blob();
            updateProgress('Downloading…', 95, 'Starting download');
            triggerDownload(blob, `xtrc_${target}_${prefix.toLowerCase()}.zip`);
         }

         // SF2/RBM/Electribe handle their own status messages
         if (target !== 'rbm' && target !== 'electribe' && target !== 'sf2' && target !== 'dss1' && target !== 'triton' && target !== 'casio' && target !== 'emu' && target !== 'emue4b' && target !== 'roland' && target !== 'mpc' && target !== 'yamaha' && target !== 'rx2') {
            setStatus(`done — ${items.length} sample(s) exported`);
         }
      } catch (err) {
         console.error('[export-panel]', err);
         setStatus('export failed: ' + (err.message || err), true);
         hideProgress();
      } finally {
         runBtn.disabled = false;
      }
   }

   function triggerDownload(blob, filename) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => {
         URL.revokeObjectURL(a.href);
         hideProgress();
      }, 200);
   }

   // ---- Panel open / close --------------------------------------------------
   const panel = {
      open() {
         panelEl.removeAttribute('hidden');
         if (workspace) workspace.classList.add('show-export');
         if (dlBucketBtn) dlBucketBtn.classList.add('export-panel-open');
         setStatus('');
         const target = targetSel.value;
         // [export] panel.open
         renderConfig(target);
         // Show/hide RBM tabs based on target
         updateRbmVisibility(target);
         // Show/hide Electribe tabs based on target
         updateElectribeVisibility(target);
         // Show/hide SF2 sample grid based on target
         updateSF2Visibility(target);
         // ZIP / allzip: auto-prefill; hardware: keep existing list (or empty on first open)
         if (target === 'zip' || target === 'allzip') {
            prefillAll();
         } else if (exportItems.length === 0) {
            renderList();
         }
         // (Re)build the accessible picker for the current target/bucket state.
         // prefillAll/renderList above call syncPickerChecked, but the picker
         // itself must be rebuilt when the target or bucket contents change.
         renderPicker();
      },

      close() {
         panelEl.setAttribute('hidden', '');
         if (workspace) workspace.classList.remove('show-export');
         if (dlBucketBtn) dlBucketBtn.classList.remove('export-panel-open');
      },

      addItem,
      playItem,
   };

   // ---- Event wiring --------------------------------------------------------

   targetSel.addEventListener('change', () => {
      const target = targetSel.value;
      // [export] target changed
      renderConfig(target);
      if (target === 'zip' || target === 'allzip') {
         prefillAll();
      } else {
         clearItems();
      }
      // Show/hide RBM tabs based on target
      updateRbmVisibility(target);
      // Show/hide Electribe tabs based on target
      updateElectribeVisibility(target);
      // Show/hide SF2 sample grid based on target
      updateSF2Visibility(target);
      // Rebuild the accessible picker for the new target's accepted item types
      renderPicker();
   });

   if (clearBtn) clearBtn.addEventListener('click', () => clearItems());
   if (runBtn)   runBtn.addEventListener('click',   () => run());

   // "Select all" button for the accessible picker — adds every accepted
   // bucket item to the export list (respects slot limits via addItem).
   if (pickerSelectAllBtn) {
      pickerSelectAllBtn.addEventListener('click', () => {
         const targetKey = targetSel.value;
         const accepted = bucket.items.filter((it) => itemAcceptedForTarget(it, targetKey));
         let added = 0;
         for (const it of accepted) {
            if (addItem(it)) added++;
         }
         if (added === 0) {
            setStatus('no new samples added (all already in export list or slot limit reached)', true);
         }
      });
   }
   
   const closeBtn = document.getElementById('xep-close');
   if (closeBtn) closeBtn.addEventListener('click', () => panel.close());

   document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !panelEl.hidden) panel.close();
   });

   // Listen for stop-all events (e.g., when MIDI playback starts)
   window.addEventListener('bucket-stop-all', () => {
      stopPlayback();
   });

   // Rebuild the accessible picker when the bucket changes (items added/removed
   // or cross-tab sync), but only while the export panel is visible.
   window.addEventListener('bucket-changed', () => {
      if (!panelEl.hidden) renderPicker();
   });

   // Listen for bucket-wav-stop events to clear playing states
   window.addEventListener('bucket-wav-stop', () => {
      if (activeCanvas) {
         activeCanvas.classList.remove('xep-canvas-playing');
         activeCanvas = null;
      }
   });

   // Note: We do NOT auto-sync export list with bucket anymore
   // The export list should remain independent of bucket tab switching

   // ---- RBM Packer UI ----------------------------------------------------
   let rbmFiles = {
      images: [],
      text: [],
      rbs: null
   };

   // RB-338 default sample slots with slot names (all available samples)
   const RBM_DEFAULT_SLOTS = [
      { slotName: 'TR808BD', defaultFile: 'TR808BD.aif' },
      { slotName: 'TR808CB', defaultFile: 'TR808CB.aif' },
      { slotName: 'TR808CH', defaultFile: 'TR808CH.aif' },
      { slotName: 'TR808CL', defaultFile: 'TR808CL.aif' },
      { slotName: 'TR808CP', defaultFile: 'TR808CP.aif' },
      { slotName: 'TR808CY', defaultFile: 'TR808CY.aif' },
      { slotName: 'TR808HC', defaultFile: 'TR808HC.aif' },
      { slotName: 'TR808HT', defaultFile: 'TR808HT.aif' },
      { slotName: 'TR808LC', defaultFile: 'TR808LC.aif' },
      { slotName: 'TR808LT', defaultFile: 'TR808LT.aif' },
      { slotName: 'TR808MA', defaultFile: 'TR808MA.aif' },
      { slotName: 'TR808MC', defaultFile: 'TR808MC.aif' },
      { slotName: 'TR808MT', defaultFile: 'TR808MT.aif' },
      { slotName: 'TR808N1', defaultFile: 'TR808N1.aif' },
      { slotName: 'TR808N2', defaultFile: 'TR808N2.aif' },
      { slotName: 'TR808N3', defaultFile: 'TR808N3.aif' },
      { slotName: 'TR808N4', defaultFile: 'TR808N4.aif' },
      { slotName: 'TR808OH', defaultFile: 'TR808OH.aif' },
      { slotName: 'TR808RS', defaultFile: 'TR808RS.aif' },
      { slotName: 'TR808SD1', defaultFile: 'TR808SD1.aif' },
      { slotName: 'TR808SD2', defaultFile: 'TR808SD2.aif' },
      { slotName: 'TR808SD3', defaultFile: 'TR808SD3.aif' },
      { slotName: 'TR808SD4', defaultFile: 'TR808SD4.aif' },
      { slotName: 'TR808SD5', defaultFile: 'TR808SD5.aif' },
      { slotName: 'TR909BD1', defaultFile: 'TR909BD1.aif' },
      { slotName: 'TR909BD2', defaultFile: 'TR909BD2.aif' },
      { slotName: 'TR909BD3', defaultFile: 'TR909BD3.aif' },
      { slotName: 'TR909BD4', defaultFile: 'TR909BD4.aif' },
      { slotName: 'TR909CCY', defaultFile: 'TR909CCY.aif' },
      { slotName: 'TR909CHH', defaultFile: 'TR909CHH.aif' },
      { slotName: 'TR909HC', defaultFile: 'TR909HC.aif' },
      { slotName: 'TR909HT', defaultFile: 'TR909HT.aif' },
      { slotName: 'TR909LT', defaultFile: 'TR909LT.aif' },
      { slotName: 'TR909MT', defaultFile: 'TR909MT.aif' },
      { slotName: 'TR909OHH', defaultFile: 'TR909OHH.aif' },
      { slotName: 'TR909RCY', defaultFile: 'TR909RCY.aif' },
      { slotName: 'TR909RS', defaultFile: 'TR909RS.aif' },
      { slotName: 'TR909SD1', defaultFile: 'TR909SD1.aif' },
      { slotName: 'TR909SD2', defaultFile: 'TR909SD2.aif' },
      { slotName: 'TR909SD3', defaultFile: 'TR909SD3.aif' },
      { slotName: 'TR909SD4', defaultFile: 'TR909SD4.aif' },
      { slotName: 'TR909SD5', defaultFile: 'TR909SD5.aif' },
      { slotName: 'TR909SDN1', defaultFile: 'TR909SDN1.aif' },
      { slotName: 'TR909SDN2', defaultFile: 'TR909SDN2.aif' },
      { slotName: 'TR909SDN3', defaultFile: 'TR909SDN3.aif' },
      { slotName: 'Startup', defaultFile: 'Startup.aif' },
   ];

   // Current sample slots (initialized with defaults)
   let rbmSampleSlots = RBM_DEFAULT_SLOTS.map(slot => ({
      slotName: slot.slotName,
      fileName: slot.defaultFile,
      data: null // Will be loaded from default files
   }));

   // RB-338 default image slots with slot names (all available images)
   const RBM_DEFAULT_IMAGES = [
      { slotName: '12015', defaultFile: '12015.jpg' },
      { slotName: '12016', defaultFile: '12016.jpg' },
      { slotName: '12017', defaultFile: '12017.jpg' },
      { slotName: '12019', defaultFile: '12019.jpg' },
      { slotName: '12020', defaultFile: '12020.jpg' },
      { slotName: '12030', defaultFile: '12030.jpg' },
      { slotName: '12031', defaultFile: '12031.jpg' },
      { slotName: '12032', defaultFile: '12032.jpg' },
      { slotName: '12040', defaultFile: '12040.jpg' },
      { slotName: '12041', defaultFile: '12041.jpg' },
      { slotName: 'TBAR', defaultFile: '12057.jpg' },
      { slotName: 'PAT', defaultFile: '12058.jpg' },
      { slotName: 'MIX_SM', defaultFile: '12060.jpg' },
      { slotName: 'MASTER', defaultFile: '12061.jpg' },
      { slotName: 'WOOD', defaultFile: '12062.jpg' },
      { slotName: 'DELAY', defaultFile: '12063.jpg' },
      { slotName: 'PCF', defaultFile: '12064.jpg' },
      { slotName: 'DIST', defaultFile: '12065.jpg' },
      { slotName: '12066', defaultFile: '12066.jpg' },
      { slotName: '12067', defaultFile: '12067.jpg' },
      { slotName: 'DIV4', defaultFile: '12068.jpg' },
      { slotName: 'TB303', defaultFile: '12500.jpg' },
      { slotName: '12501', defaultFile: '12501.jpg' },
      { slotName: '12502', defaultFile: '12502.jpg' },
      { slotName: '12503', defaultFile: '12503.jpg' },
      { slotName: '12504', defaultFile: '12504.jpg' },
      { slotName: '12505', defaultFile: '12505.jpg' },
      { slotName: '12506', defaultFile: '12506.jpg' },
      { slotName: '12507', defaultFile: '12507.jpg' },
      { slotName: '12509', defaultFile: '12509.jpg' },
      { slotName: '12510', defaultFile: '12510.jpg' },
      { slotName: 'TR808', defaultFile: '12520.jpg' },
      { slotName: '12521', defaultFile: '12521.jpg' },
      { slotName: '12522', defaultFile: '12522.jpg' },
      { slotName: '12523', defaultFile: '12523.jpg' },
      { slotName: '12524', defaultFile: '12524.jpg' },
      { slotName: '12525', defaultFile: '12525.jpg' },
      { slotName: '12550', defaultFile: '12550.jpg' },
      { slotName: '12551', defaultFile: '12551.jpg' },
      { slotName: '12552', defaultFile: '12552.jpg' },
      { slotName: '12554', defaultFile: '12554.jpg' },
      { slotName: '12555', defaultFile: '12555.jpg' },
      { slotName: '12556', defaultFile: '12556.jpg' },
      { slotName: '12557', defaultFile: '12557.jpg' },
      { slotName: '12558', defaultFile: '12558.jpg' },
      { slotName: '12559', defaultFile: '12559.jpg' },
      { slotName: '12560', defaultFile: '12560.jpg' },
      { slotName: '12561', defaultFile: '12561.jpg' },
      { slotName: '12562', defaultFile: '12562.jpg' },
      { slotName: '12563', defaultFile: '12563.jpg' },
      { slotName: 'TR909', defaultFile: '12564.jpg' },
      { slotName: '12565', defaultFile: '12565.jpg' },
      { slotName: '12566', defaultFile: '12566.jpg' },
      { slotName: '12567', defaultFile: '12567.jpg' },
      { slotName: '12568', defaultFile: '12568.jpg' },
      { slotName: 'COMP', defaultFile: '12569.jpg' },
      { slotName: '12570', defaultFile: '12570.jpg' },
      { slotName: '12571', defaultFile: '12571.jpg' },
      { slotName: 'Splash', defaultFile: 'Splash.jpg' },
   ];

   // RB-338 preview canvas sprite data
   const RBM_PREVIEW_SPRITES = {
      wood: '12062.jpg',
      tbar: '12057.jpg',
      pat: '12058.jpg',
      mix_sm: '12060.jpg',
      div4: '12068.jpg',
      tb303: '12500.jpg',
      tr808: '12520.jpg',
      tr909: '12564.jpg',
      master: '12061.jpg',
      pcf: '12064.jpg',
      delay: '12063.jpg',
      dist: '12065.jpg',
      comp: '12569.jpg',
   };

   const RBM_PREVIEW_LAYOUT = [
      // wood borders
      ['wood', 0, 0, 16, 510],
      ['wood', 608, 0, 16, 510],
      // transport
      ['tbar', 16, 0, 592, 50],
      // 4 rows: pat + divider + mix strip
      ['pat', 16, 50, 70, 115], ['div4', 86, 50, 4, 115], ['mix_sm', 456, 50, 70, 115],
      ['pat', 16, 165, 70, 115], ['div4', 86, 165, 4, 115], ['mix_sm', 456, 165, 70, 115],
      ['pat', 16, 280, 70, 115], ['div4', 86, 280, 4, 115], ['mix_sm', 456, 280, 70, 115],
      ['pat', 16, 395, 70, 115], ['div4', 86, 395, 4, 115], ['mix_sm', 456, 395, 70, 115],
      // main panels
      ['tb303', 90, 50, 366, 115],
      ['tb303', 90, 165, 366, 115],
      ['tr808', 90, 280, 366, 115],
      ['tr909', 90, 395, 366, 115],
      // right column effects
      ['master', 526, 50, 82, 98],
      ['pcf', 526, 148, 82, 111],
      ['delay', 526, 259, 82, 93],
      ['dist', 526, 352, 82, 65],
      ['comp', 526, 417, 82, 93],
   ];

   let rbmPreviewSprites = {};
   let rbmPreviewLoaded = false;

   // Load RB-338 preview sprites
   async function loadRbmPreviewSprites() {
      if (rbmPreviewLoaded) return;
      
      // Use http://127.0.0.1:8765/ for Electron (detected via window.electron), /rb338/ for web
      const isElectron = typeof window !== 'undefined' && window.electron;
      const base = isElectron ? 'http://127.0.0.1:8765/rb338/' : '/rb338/';
      const loadPromises = Object.entries(RBM_PREVIEW_SPRITES).map(([key, file]) =>
         new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
               rbmPreviewSprites[key] = img;
               resolve();
            };
            img.onerror = () => resolve(); // Continue even if some sprites fail
            img.src = base + file;
         })
      );
      
      await Promise.all(loadPromises);
      rbmPreviewLoaded = true;
   }

   // Render RB-338 preview canvas
   function renderRbmPreview() {
      const canvas = document.getElementById('xep-rbm-preview-canvas');
      if (!canvas) return;
      
      // Set canvas dimensions to RB-338 size (640x480)
      canvas.width = 640;
      canvas.height = 480;
      
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = false;
      
      // Fill canvas with black background
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw sprites according to layout
      for (const [spriteKey, x, y, w, h] of RBM_PREVIEW_LAYOUT) {
         const img = rbmPreviewSprites[spriteKey];
         if (img) {
            ctx.drawImage(img, x, y, w, h);
         }
      }
   }

   // Current image slots (initialized with defaults)
   let rbmImageSlots = RBM_DEFAULT_IMAGES.map(slot => ({
      slotName: slot.slotName,
      fileName: slot.defaultFile,
      data: null // Will be loaded from default files
   }));

   // RB-338 default text slots
   const RBM_DEFAULT_TEXTS = [
      { slotName: 'ModName', defaultFile: 'ModName.txt' },
      { slotName: 'Comments', defaultFile: 'Comments.txt' },
      { slotName: 'WWW', defaultFile: 'WWW.txt' },
      { slotName: 'FTP', defaultFile: 'FTP.txt' },
   ];

   // Current text slots (initialized with defaults)
   let rbmTextSlots = RBM_DEFAULT_TEXTS.map(slot => ({
      slotName: slot.slotName,
      fileName: slot.defaultFile,
      content: '' // Will be loaded from default files
   }));

   async function updateRbmVisibility(target) {
      const rbmTabs = document.getElementById('xep-rbm-tabs');
      const standardList = document.getElementById('xep-standard-list');
      const sampleSlots = document.getElementById('xep-rbm-sample-slots');
      const imagesTab = document.getElementById('xep-rbm-images');
      const textTab = document.getElementById('xep-rbm-text');
      const songTab = document.getElementById('xep-rbm-song');

      if (target === 'rbm') {
         if (rbmTabs) rbmTabs.hidden = false;
         if (standardList) standardList.hidden = true;
         initRbmPackerUI();
         // Show loading spinner while default audio & images are fetched
         const loadingEl = showRbmLoading();
         try {
            await loadDefaultSamples();
            await loadDefaultImages();
            await loadDefaultTexts();
            await loadDefaultRbs();
            await loadRbmPreviewSprites();
            renderRbmSampleSlots();
            renderRbmImageSlots();
            renderRbmTextSlots();
            // Activate Samples tab by default (query after initRbmPackerUI which clones tabs)
            const samplesTab = panelEl.querySelector('.xep-rbm-tab[data-tab="samples"]');
            if (samplesTab) {
               panelEl.querySelectorAll('.xep-rbm-tab').forEach(t => t.classList.remove('active'));
               samplesTab.classList.add('active');
            }
            if (sampleSlots) sampleSlots.hidden = false;
            // Hide other content areas
            if (imagesTab) imagesTab.hidden = true;
            if (textTab) textTab.hidden = true;
            if (songTab) songTab.hidden = true;
         } finally {
            hideRbmLoading(loadingEl);
         }
      } else {
         if (rbmTabs) rbmTabs.hidden = true;
         if (sampleSlots) sampleSlots.hidden = true;
         if (imagesTab) imagesTab.hidden = true;
         if (textTab) textTab.hidden = true;
         if (songTab) songTab.hidden = true;
         if (standardList && target !== 'sf2' && target !== 'electribe') standardList.hidden = false;
      }
   }

   // Show an overlay spinner inside the export panel while RBM defaults load
   function showRbmLoading() {
      if (!panelEl) return null;
      const el = document.createElement('div');
      el.className = 'xep-rbm-loading';
      el.innerHTML = '<div class="xep-rbm-spinner"></div><span>Loading default audio &amp; images…</span>';
      panelEl.appendChild(el);
      return el;
   }

   function hideRbmLoading(el) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
   }

   async function loadDefaultSamples() {
      // Use http://127.0.0.1:8765/ for Electron (detected via window.electron), /rb338/ for web
      const isElectron = typeof window !== 'undefined' && window.electron;
      const baseUrl = isElectron ? 'http://127.0.0.1:8765/rb338/' : '/rb338/';
      for (const slot of rbmSampleSlots) {
         if (slot.data === null) {
            try {
               const response = await fetch(baseUrl + slot.fileName);
               if (response.ok) {
                  const arrayBuffer = await response.arrayBuffer();
                  slot.data = new Uint8Array(arrayBuffer);
               }
            } catch (err) {
            }
         }
      }
   }

   async function loadDefaultImages() {
      // Use http://127.0.0.1:8765/ for Electron (detected via window.electron), /rb338/ for web
      const isElectron = typeof window !== 'undefined' && window.electron;
      const baseUrl = isElectron ? 'http://127.0.0.1:8765/rb338/' : '/rb338/';
      for (const slot of rbmImageSlots) {
         if (slot.data === null) {
            try {
               const response = await fetch(baseUrl + slot.fileName);
               if (response.ok) {
                  const arrayBuffer = await response.arrayBuffer();
                  slot.data = new Uint8Array(arrayBuffer);
               }
            } catch (err) {
            }
         }
      }
   }

   async function loadDefaultTexts() {
      // Use http://127.0.0.1:8765/ for Electron (detected via window.electron), /rb338/ for web
      const isElectron = typeof window !== 'undefined' && window.electron;
      const baseUrl = isElectron ? 'http://127.0.0.1:8765/rb338/' : '/rb338/';
      for (const slot of rbmTextSlots) {
         if (slot.content === '') {
            try {
               const response = await fetch(baseUrl + slot.fileName);
               if (response.ok) {
                  slot.content = await response.text();
               }
            } catch (err) {
            }
         }
      }
   }

   async function loadDefaultRbs() {
      // Use http://127.0.0.1:8765/ for Electron (detected via window.electron), /rb338/ for web
      const isElectron = typeof window !== 'undefined' && window.electron;
      const baseUrl = isElectron ? 'http://127.0.0.1:8765/rb338/' : '/rb338/';
      if (!rbmFiles.rbs) {
         try {
            const response = await fetch(baseUrl + 'Demo.rbs');
            if (response.ok) {
               const arrayBuffer = await response.arrayBuffer();
               rbmFiles.rbs = {
                  name: 'Demo.rbs',
                  data: new Uint8Array(arrayBuffer)
               };
               const rbsInfo = document.getElementById('exp-rbm-rbs-info');
               if (rbsInfo) {
                  rbsInfo.textContent = `Loaded: Demo.rbs (${fmtBytes(arrayBuffer.byteLength)})`;
               }
            }
         } catch (err) {
         }
      }
   }

   async function renderRbmSampleSlots() {
      const slotsContainer = document.getElementById('xep-rbm-sample-slots');
      if (!slotsContainer) return;

      slotsContainer.innerHTML = '';
      for (const [index, slot] of rbmSampleSlots.entries()) {
         const slotEl = document.createElement('div');
         slotEl.className = 'sample';
         slotEl.dataset.sampleName = slot.fileName;

         // Label (filename)
         const label = document.createElement('label');
         label.textContent = slot.sourceName ? `${slot.fileName} \u2190 ${slot.sourceName}` : slot.fileName;
         label.title = slot.sourceName ? `Replaced with "${slot.sourceName}" (converted to AIFF on export)` : slot.fileName;
         slotEl.appendChild(label);

         // Pick-from-bucket button
         const pickBtn = document.createElement('button');
         pickBtn.className = 'xep-slot-pick-btn';
         pickBtn.type = 'button';
         pickBtn.textContent = 'pick';
         pickBtn.setAttribute('aria-label', `Pick sample from bucket for ${slot.fileName}`);
         pickBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            openBucketPicker(async (bItem) => {
               if (!bItem || !bItem.wav) { setStatus('selected item has no audio data', true); return; }
               const wavBuf = bItem.wav instanceof ArrayBuffer ? bItem.wav
                  : (bItem.wav.buffer ? bItem.wav.buffer.slice(bItem.wav.byteOffset || 0, (bItem.wav.byteOffset || 0) + bItem.wav.byteLength) : bItem.wav);
               rbmSampleSlots[index].data = await normalizeRbmSample(wavBuf);
               rbmSampleSlots[index].pcm = null;
               rbmSampleSlots[index].sampleRate = null;
               rbmSampleSlots[index].channels = null;
               rbmSampleSlots[index].bitDepth = null;
               rbmSampleSlots[index].sourceName = bItem.name;
               renderRbmSampleSlots();
            }, { title: `Pick sample for ${slot.fileName}`, filter: (it) => !!it.wav && it.type !== 'image' });
         });
         slotEl.appendChild(pickBtn);

         // Waveform canvas (same size as bucket cards)
         const canvas = document.createElement('canvas');
         canvas.className = 'samplecanvas';
         canvas.width = 200;
         canvas.height = 80;
         if (slot.data) {
            // Use the same drawing logic as bucket cards: prefer PCM data if available
            if (slot.pcm && slot.pcm.length) {
               const bitDepthToUse = slot.bitDepth || (slot.pcm instanceof Int32Array ? 24 : 16);
               drawWaveformFromInt16(canvas, slot.pcm, slot.channels || 1, bitDepthToUse);
            } else {
               // Check format: AIFF (FORM) vs WAV (RIFF)
               const dv = new DataView(slot.data instanceof ArrayBuffer ? slot.data : slot.data.buffer);
               const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
               if (magic === 'FORM') {
                  // AIFF — use fallback which has AIFF parsing
                  await drawWaveformFromWavFallback(canvas, slot.data);
               } else {
                  // WAV — use same algorithm as bucket (min/max per column + auto-scale)
                  drawWaveformFromWavBucket(canvas, slot.data, slot.pcm, slot.sampleRate, slot.channels, slot.bitDepth);
               }
            }
         }
         slotEl.appendChild(canvas);

         slotsContainer.appendChild(slotEl);

         // Click to play
         canvas.addEventListener('click', () => {
            if (slot.data) {
               playItem(slot.data, canvas, slot.pcm, slot.sampleRate, slot.channels, slot.bitDepth);
            }
         });

         // Enable drag-to-bucket: make the sample slot draggable if it has data
         if (slot.data) {
            slotEl.draggable = true;
            slotEl.addEventListener('dragstart', (e) => {
               e.dataTransfer.effectAllowed = 'copy';
               const blobUrl = URL.createObjectURL(new Blob([slot.data], { type: 'audio/wav' }));
               e.dataTransfer.setData('application/x-module-url', blobUrl);
               e.dataTransfer.setData('application/x-module-sample-name', slot.fileName);
               e.dataTransfer.setData('text/plain', slot.fileName);
               setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            });
         }

         // Enable drag-and-drop to update sample
         slotEl.addEventListener('dragover', (e) => {
            e.preventDefault();
            slotEl.classList.add('xep-rbm-slot-dragover');
         });

         slotEl.addEventListener('dragleave', () => {
            slotEl.classList.remove('xep-rbm-slot-dragover');
         });

         slotEl.addEventListener('drop', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            slotEl.classList.remove('xep-rbm-slot-dragover');

            // Case 1: OS file dropped from disk.
            const file = e.dataTransfer.files[0];
            if (file) {
               const data = await fileToUint8Array(file);
               // Normalize to mono WAV (ReBirth only supports mono)
               rbmSampleSlots[index].data = await normalizeRbmSample(data);
               rbmSampleSlots[index].pcm = null;
               rbmSampleSlots[index].sampleRate = null;
               rbmSampleSlots[index].channels = null;
               rbmSampleSlots[index].bitDepth = null;
               rbmSampleSlots[index].sourceName = file.name;
               renderRbmSampleSlots();
               return;
            }

            // Case 2: wavedit tab dragged (carries tab id, dispatch event to get WAV data)
            const waveditTabId = e.dataTransfer.getData('application/x-wavedit-tab');
            if (waveditTabId) {
               // Dispatch event so wavedit can convert the tab's AudioBuffer to WAV
               const resp = await new Promise((resolve) => {
                  const handler = (ev) => {
                     window.removeEventListener('wavedit-tab-wav-data', handler);
                     resolve(ev.detail);
                  };
                  window.addEventListener('wavedit-tab-wav-data', handler);
                  window.dispatchEvent(new CustomEvent('wavedit-tab-request-wav', {
                     detail: { tabId: waveditTabId }
                  }));
                  // Timeout after 3s
                  setTimeout(() => {
                     window.removeEventListener('wavedit-tab-wav-data', handler);
                     resolve(null);
                  }, 3000);
               });
               if (resp && resp.wav) {
                  const wavBuf = resp.wav instanceof ArrayBuffer ? resp.wav
                     : (resp.wav.buffer ? resp.wav.buffer.slice(resp.wav.byteOffset || 0, (resp.wav.byteOffset || 0) + resp.wav.byteLength) : resp.wav);
                  // Normalize to mono WAV (ReBirth only supports mono)
                  rbmSampleSlots[index].data = await normalizeRbmSample(wavBuf);
                  rbmSampleSlots[index].pcm = null;
                  rbmSampleSlots[index].sampleRate = null;
                  rbmSampleSlots[index].channels = null;
                  rbmSampleSlots[index].bitDepth = null;
                  rbmSampleSlots[index].sourceName = resp.name || 'wavedit tab';
                  renderRbmSampleSlots();
               }
               return;
            }

            // Case 3: item dragged from the bucket (carries the bucket item id).
            let bid = e.dataTransfer.getData('application/x-bucket-item-id');
            if (!bid) bid = e.dataTransfer.getData('text/plain');
            if (!bid) return;
            const bItem = bucket.items.find((i) => i.id === bid);
            if (!bItem || !bItem.wav) {
               setStatus('dropped item has no audio data', true);
               return;
            }
            // Store the bucket sample's WAV bytes as ArrayBuffer for consistent waveform drawing
            const wavBuf = bItem.wav instanceof ArrayBuffer ? bItem.wav
               : (bItem.wav.buffer ? bItem.wav.buffer.slice(bItem.wav.byteOffset || 0, (bItem.wav.byteOffset || 0) + bItem.wav.byteLength) : bItem.wav);
            // Normalize to mono WAV (ReBirth only supports mono)
            rbmSampleSlots[index].data = await normalizeRbmSample(wavBuf);
            rbmSampleSlots[index].pcm = null;
            rbmSampleSlots[index].sampleRate = null;
            rbmSampleSlots[index].channels = null;
            rbmSampleSlots[index].bitDepth = null;
            rbmSampleSlots[index].sourceName = bItem.name;
            renderRbmSampleSlots();
         });
      }
   }

   function renderRbmTextSlots() {
      const slotsContainer = document.getElementById('xep-rbm-text');
      if (!slotsContainer) return;

      // Keep the upload UI, clear only the list
      const existingList = document.getElementById('exp-rbm-text-list');
      if (existingList) existingList.innerHTML = '';

      for (const [index, slot] of rbmTextSlots.entries()) {
         const slotEl = document.createElement('div');
         slotEl.className = 'xep-rbm-text-slot';

         const slotName = document.createElement('div');
         slotName.className = 'xep-rbm-slot-name';
         slotName.textContent = slot.slotName;

         const textArea = document.createElement('textarea');
         textArea.className = 'xep-rbm-text-editor';
         textArea.value = slot.content;
         textArea.rows = 3;
         textArea.placeholder = `${slot.slotName}...`;
         textArea.dataset.slotName = slot.slotName;
         textArea.addEventListener('input', (e) => {
            rbmTextSlots[index].content = e.target.value;
            // Sync ModName textarea with the mod name input field
            if (slot.slotName === 'ModName') {
               const modNameInput = document.getElementById('exp-rbm-name');
               if (modNameInput) modNameInput.value = e.target.value;
            }
         });

         slotEl.appendChild(slotName);
         slotEl.appendChild(textArea);
         existingList.appendChild(slotEl);
      }

      // Sync the mod name input field with the ModName textarea
      const modNameInput = document.getElementById('exp-rbm-name');
      if (modNameInput) {
         // Initialize the input with the current ModName content
         const modNameSlot = rbmTextSlots.find(s => s.slotName === 'ModName');
         if (modNameSlot && modNameSlot.content) {
            modNameInput.value = modNameSlot.content;
         }
         // When the input changes, update the ModName textarea
         modNameInput.addEventListener('input', (e) => {
            const modNameSlot = rbmTextSlots.find(s => s.slotName === 'ModName');
            if (modNameSlot) {
               modNameSlot.content = e.target.value;
               // Update the textarea value if it exists
               const modNameTextarea = existingList.querySelector('.xep-rbm-text-editor[data-slot-name="ModName"]');
               if (modNameTextarea) modNameTextarea.value = e.target.value;
            }
         });
      }
   }

   function renderRbmImageSlots() {
      const slotsContainer = document.getElementById('xep-rbm-image-slots');
      if (!slotsContainer) return;

      slotsContainer.innerHTML = '';
      rbmImageSlots.forEach((slot, index) => {
         const slotEl = document.createElement('div');
         slotEl.className = 'xep-rbm-image-slot';
         slotEl.dataset.index = index;

         const slotName = document.createElement('div');
         slotName.className = 'xep-rbm-slot-name';
         slotName.textContent = slot.slotName;

         const fileName = document.createElement('div');
         fileName.className = 'xep-rbm-slot-filename';
         fileName.textContent = slot.fileName;

         const canvas = document.createElement('canvas');
         canvas.className = 'xep-rbm-image-slot-canvas';
         canvas.width = 120;
         canvas.height = 80;
         canvas.style.cursor = 'pointer';
         canvas.title = 'Click to replace image';
         
         let originalWidth = 0;
         let originalHeight = 0;
         
         if (slot.data) {
            const img = new Image();
            img.onload = () => {
               originalWidth = img.width;
               originalHeight = img.height;
               const ctx = canvas.getContext('2d');
               // Center image without stretching
               const x = (canvas.width - img.width) / 2;
               const y = (canvas.height - img.height) / 2;
               ctx.drawImage(img, x, y);
            };
            img.src = URL.createObjectURL(new Blob([slot.data]));
         }

         // Download button
         const downloadBtn = document.createElement('button');
         downloadBtn.className = 'xep-rbm-slot-download';
         downloadBtn.textContent = '↓';
         downloadBtn.title = 'Download image';
         downloadBtn.onclick = (e) => {
            e.stopPropagation();
            if (slot.data) {
               const blob = new Blob([slot.data], { type: 'image/jpeg' });
               const url = URL.createObjectURL(blob);
               const a = document.createElement('a');
               a.href = url;
               a.download = slot.fileName;
               a.click();
               URL.revokeObjectURL(url);
            }
         };

         // Hidden file input for click-to-replace
         const fileInput = document.createElement('input');
         fileInput.type = 'file';
         fileInput.accept = 'image/*';
         fileInput.style.display = 'none';
         fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
               await handleImageDrop(file, originalWidth, originalHeight, index, slot);
            };
         };

         // Click canvas to trigger file input
         canvas.onclick = () => fileInput.click();

         // Enable drag-to-bucket: make the image slot draggable if it has data
         if (slot.data) {
            slotEl.draggable = true;
            slotEl.addEventListener('dragstart', (e) => {
               e.dataTransfer.effectAllowed = 'copy';
               // Set image data so bucket can receive it
               const ext = (slot.fileName.split('.').pop() || '').toLowerCase();
               const imgMime = {
                  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                  gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp',
                  tiff: 'image/tiff', tga: 'image/x-tga', pcx: 'image/x-pcx',
               }[ext] || 'image/png';
               const blobUrl = URL.createObjectURL(new Blob([slot.data], { type: imgMime }));
               e.dataTransfer.setData('application/x-image-url', blobUrl);
               e.dataTransfer.setData('application/x-image-name', slot.fileName);
               e.dataTransfer.setData('text/plain', slot.fileName);
               setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            });
         }

         slotEl.appendChild(slotName);
         slotEl.appendChild(fileName);
         slotEl.appendChild(canvas);
         slotEl.appendChild(downloadBtn);
         slotEl.appendChild(fileInput);
         slotsContainer.appendChild(slotEl);

         // Enable drag-and-drop to update image (from OS file or bucket)
         slotEl.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            slotEl.classList.add('xep-rbm-slot-dragover');
         });

         slotEl.addEventListener('dragleave', () => {
            slotEl.classList.remove('xep-rbm-slot-dragover');
         });

         slotEl.addEventListener('drop', async (e) => {
            e.preventDefault();
            slotEl.classList.remove('xep-rbm-slot-dragover');

            // 1. Try bucket drag: application/x-image-url (blob URL to image data)
            const imageUrl = e.dataTransfer.getData('application/x-image-url');
            if (imageUrl) {
               try {
                  const resp = await fetch(imageUrl);
                  const blob = await resp.blob();
                  await handleImageDrop(blob, originalWidth, originalHeight, index, slot);
               } catch (err) {
                  showImageErrorModal('Failed to load image from bucket', err.message);
               }
               return;
            }

            // 2. Try OS file drop
            const file = e.dataTransfer.files[0];
            if (file) {
               await handleImageDrop(file, originalWidth, originalHeight, index, slot);
            }
         });
      });
   }

   // Helper: show a modal error dialog for image size mismatch
   function showImageErrorModal(title, detail) {
      // Remove any existing error modal
      const existing = document.getElementById('xep-image-error-modal');
      if (existing) existing.remove();

      const modal = document.createElement('div');
      modal.id = 'xep-image-error-modal';
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.6);z-index:100000;display:flex;align-items:center;justify-content:center;';
      modal.innerHTML = `
         <div style="background:var(--c1,#1e1e1e);border:1px solid var(--c8,#e74c3c);border-radius:8px;padding:24px 32px;max-width:420px;text-align:center;font-family:monospace;">
            <div style="font-size:18px;font-weight:bold;color:var(--c8,#e74c3c);margin-bottom:12px;">⚠ ${title}</div>
            <div style="font-size:13px;color:var(--c5,#aaa);margin-bottom:20px;">${detail}</div>
            <button id="xep-image-error-close" style="background:var(--c3,#333);color:var(--c6,#ddd);border:1px solid var(--c4,#555);border-radius:4px;padding:8px 24px;cursor:pointer;font-family:monospace;">OK</button>
         </div>
      `;
      document.body.appendChild(modal);
      const closeBtn = modal.querySelector('#xep-image-error-close');
      closeBtn.onclick = () => modal.remove();
      modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
   }

   // Helper: load image data (File or Blob), validate dimensions, update slot
   async function handleImageDrop(imageData, expectedWidth, expectedHeight, index, slot) {
      // Load image to check dimensions
      const imgUrl = URL.createObjectURL(imageData);
      try {
         const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = () => reject(new Error('Cannot decode image'));
            i.src = imgUrl;
         });

         // Validate size matches original (if we have a reference)
         if (expectedWidth > 0 && expectedHeight > 0) {
            if (img.width !== expectedWidth || img.height !== expectedHeight) {
               showImageErrorModal(
                  'Image Size Mismatch',
                  `Expected <b>${expectedWidth}×${expectedHeight}</b> but got <b>${img.width}×${img.height}</b>.<br>Please use an image with the correct dimensions.`
               );
               return;
            }
         }

         // Read the raw data
         const data = new Uint8Array(await imageData.arrayBuffer());
         rbmImageSlots[index].data = data;

         // Update preview sprite if this slot corresponds to a preview sprite
         const spriteKey = Object.keys(RBM_PREVIEW_SPRITES).find(key => key.toLowerCase() === slot.slotName.toLowerCase());
         if (spriteKey) {
            const previewImg = new Image();
            previewImg.onload = () => {
               rbmPreviewSprites[spriteKey] = previewImg;
               renderRbmPreview();
            };
            previewImg.src = URL.createObjectURL(new Blob([data]));
         }

         renderRbmImageSlots();
      } catch (err) {
         showImageErrorModal('Failed to load image', err.message);
      } finally {
         URL.revokeObjectURL(imgUrl);
      }
   }

   function initRbmPackerUI() {
      // Tab switching
      const tabs = panelEl.querySelectorAll('.xep-rbm-tab');
      
      tabs.forEach(tab => {
         // Remove existing listeners to avoid duplicates
         const newTab = tab.cloneNode(true);
         tab.parentNode.replaceChild(newTab, tab);
         newTab.addEventListener('click', () => {
            const tabName = newTab.dataset.tab;
            
            // Remove active from all tabs (re-query to get current elements)
            panelEl.querySelectorAll('.xep-rbm-tab').forEach(t => t.classList.remove('active'));
            
            // Add active to clicked tab
            newTab.classList.add('active');
            
            // Hide all RBM content areas
            const sampleSlots = document.getElementById('xep-rbm-sample-slots');
            const imagesTab = document.getElementById('xep-rbm-images');
            const textTab = document.getElementById('xep-rbm-text');
            const songTab = document.getElementById('xep-rbm-song');
            
            if (sampleSlots) sampleSlots.hidden = true;
            if (imagesTab) imagesTab.hidden = true;
            if (textTab) textTab.hidden = true;
            if (songTab) songTab.hidden = true;
            
            // Show selected tab content
            if (tabName === 'samples') {
               if (sampleSlots) sampleSlots.hidden = false;
            } else if (tabName === 'images') {
               if (imagesTab) imagesTab.hidden = false;
               renderRbmPreview();
            } else if (tabName === 'text') {
               if (textTab) textTab.hidden = false;
            } else if (tabName === 'song') {
               if (songTab) songTab.hidden = false;
            }
         });
      });

      // Image upload
      const addImageBtn = document.getElementById('exp-rbm-add-image');
      const imageInput = document.getElementById('exp-rbm-image-input');
      if (addImageBtn && imageInput) {
         addImageBtn.onclick = () => imageInput.click();
         imageInput.onchange = (e) => {
            handleRbmFileUpload(e.target.files, 'images');
            imageInput.value = '';
         };
      }

      // Text upload
      const addTextBtn = document.getElementById('exp-rbm-add-text');
      const textInput = document.getElementById('exp-rbm-text-input');
      if (addTextBtn && textInput) {
         addTextBtn.onclick = () => textInput.click();
         textInput.onchange = (e) => {
            handleRbmFileUpload(e.target.files, 'text');
            textInput.value = '';
         };
      }

      // RBS upload
      const addRbsBtn = document.getElementById('exp-rbm-add-rbs');
      const rbsInput = document.getElementById('exp-rbm-rbs-input');
      if (addRbsBtn && rbsInput) {
         addRbsBtn.onclick = () => rbsInput.click();
         rbsInput.onchange = (e) => {
            if (e.target.files.length > 0) {
               handleRbsUpload(e.target.files[0]);
            }
            rbsInput.value = '';
         };
      }
   }

   async function handleRbmFileUpload(files, type) {
      for (const file of files) {
         const data = await fileToUint8Array(file);
         rbmFiles[type].push({
            name: file.name,
            data: data,
            type: type
         });
      }
      renderRbmFileList(type);
      // Auto-preview first image if this is the images tab
      if (type === 'images' && rbmFiles.images.length > 0) {
         previewRbmImage(0);
      }
   }

   async function handleRbsUpload(file) {
      const data = await fileToUint8Array(file);
      rbmFiles.rbs = {
         name: file.name,
         data: data
      };
      const rbsInfo = document.getElementById('exp-rbm-rbs-info');
      if (rbsInfo) {
         rbsInfo.textContent = `Loaded: ${file.name} (${fmtBytes(data.byteLength)})`;
      }
   }

   function renderRbmFileList(type) {
      const listEl = document.getElementById(`exp-rbm-${type}-list`);
      if (!listEl) return;
      
      listEl.innerHTML = '';
      rbmFiles[type].forEach((file, index) => {
         const item = document.createElement('div');
         item.className = 'xep-rbm-file-item';
         item.dataset.index = index;
         item.innerHTML = `
            <span class="xep-rbm-file-name">${file.name}</span>
            <span class="xep-rbm-file-type">${type.toUpperCase()}</span>
            <button class="xep-rbm-file-remove" data-type="${type}" data-index="${index}">×</button>
         `;
         listEl.appendChild(item);
      });

      // Wire up remove buttons
      listEl.querySelectorAll('.xep-rbm-file-remove').forEach(btn => {
         btn.onclick = (e) => {
            e.stopPropagation();
            const type = btn.dataset.type;
            const index = parseInt(btn.dataset.index);
            rbmFiles[type].splice(index, 1);
            renderRbmFileList(type);
            if (type === 'images' && rbmFiles.images.length > 0) {
               previewRbmImage(0);
            } else {
               clearRbmImagePreview();
            }
         };
      });

      // Wire up click to preview for images
      if (type === 'images') {
         listEl.querySelectorAll('.xep-rbm-file-item').forEach(item => {
            item.onclick = () => {
               const index = parseInt(item.dataset.index);
               previewRbmImage(index);
            };
         });
      }
   }

   function previewRbmImage(index) {
      const file = rbmFiles.images[index];
      if (!file) return;

      const canvas = document.getElementById('xep-rbm-image-canvas');
      const infoEl = document.getElementById('xep-rbm-image-info');
      if (!canvas || !infoEl) return;

      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
         canvas.width = img.width;
         canvas.height = img.height;
         ctx.drawImage(img, 0, 0);
         
         infoEl.innerHTML = `
            <strong>${file.name}</strong><br>
            Size: ${img.width}x${img.height}px<br>
            File: ${fmtBytes(file.data.byteLength)}
         `;
      };
      img.onerror = () => {
         infoEl.textContent = 'Failed to load image';
      };
      img.src = URL.createObjectURL(new Blob([file.data], { type: 'image/jpeg' }));
   }

   function clearRbmImagePreview() {
      const canvas = document.getElementById('xep-rbm-image-canvas');
      const infoEl = document.getElementById('xep-rbm-image-info');
      if (canvas) {
         const ctx = canvas.getContext('2d');
         ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      if (infoEl) {
         infoEl.textContent = '';
      }
   }

   // NOTE: RBM export is fully handled by the primary run() implementation
   // (the `target === 'rbm'` branch), which loads the CD data (RB20FUL.DAT),
   // default mod content, text fields and performs proper encryption. The
   // earlier override that rebuilt a stripped-down mod here has been removed
   // because it bypassed CD-data loading and produced unencrypted output.

   // ---- Electribe Packer UI ------------------------------------------------
   const ESX_MAX_MONO   = 256;
   const ESX_MAX_STEREO = 128;
   const ES1_MAX_MONO   = 100;
   const ES1_MAX_STEREO = 50;

   // electribeMonoSlots[i] / electribeStereoSlots[i]:
   //   null  → empty slot
   //   { name, data, pcm, sampleRate, channels, bitDepth, sourceName } → filled
   let electribeMonoSlots   = new Array(ESX_MAX_MONO).fill(null);
   let electribeStereoSlots = new Array(ESX_MAX_STEREO).fill(null);

   function getElectribeMaxSlots() {
      const fmtEl = document.getElementById('exp-electribe-fmt');
      const fmt = fmtEl ? fmtEl.value : 'esx';
      if (fmt === 'es1') return { mono: ES1_MAX_MONO, stereo: ES1_MAX_STEREO };
      return { mono: ESX_MAX_MONO, stereo: ESX_MAX_STEREO };
   }

   async function updateElectribeVisibility(target) {
      const etTabs       = document.getElementById('xep-electribe-tabs');
      const monoSlots    = document.getElementById('xep-electribe-mono-slots');
      const stereoSlots  = document.getElementById('xep-electribe-stereo-slots');
      const standardList = document.getElementById('xep-standard-list');
      const rbmTabs      = document.getElementById('xep-rbm-tabs');

      if (target === 'electribe') {
         if (etTabs) etTabs.hidden = false;
         if (standardList) standardList.hidden = true;
         if (rbmTabs) rbmTabs.hidden = true;
         initElectribeTabUI();
         renderElectribeSlots('mono');
         renderElectribeSlots('stereo');
         // Auto-click mono tab to ensure only mono grid is visible
         const monoTab = panelEl.querySelector('.xep-rbm-tab[data-etab="mono"]');
         if (monoTab) monoTab.click();
         // Wire format dropdown to update stereo tab visibility
         const fmtEl = document.getElementById('exp-electribe-fmt');
         if (fmtEl) {
            const newFmt = fmtEl.cloneNode(true);
            fmtEl.parentNode.replaceChild(newFmt, fmtEl);
            newFmt.addEventListener('change', () => {
               applyElectribeFormatVisibility(newFmt.value);
            });
            applyElectribeFormatVisibility(newFmt.value);
         }
      } else {
         if (etTabs) etTabs.hidden = true;
         if (monoSlots)   { monoSlots.hidden = true;   monoSlots.style.display = 'none'; }
         if (stereoSlots) { stereoSlots.hidden = true; stereoSlots.style.display = 'none'; }
         if (standardList && target !== 'rbm' && target !== 'sf2') standardList.hidden = false;
      }
   }

   // ES1 is mono-only (32 kHz). Hide stereo tab when ES1 is selected.
   // Only controls the tab button — slot grid visibility is managed by the tab click handler.
   function applyElectribeFormatVisibility(fmt) {
      const stereoTab = panelEl.querySelector('.xep-rbm-tab[data-etab="stereo"]');
      const isES1 = fmt === 'es1';
      if (stereoTab) stereoTab.hidden = isES1;
      // If currently on stereo tab and switching to ES1, switch to mono
      if (isES1) {
         const activeTab = panelEl.querySelector('.xep-rbm-tab[data-etab].active');
         if (activeTab && activeTab.dataset.etab === 'stereo') {
            const monoTab = panelEl.querySelector('.xep-rbm-tab[data-etab="mono"]');
            if (monoTab) monoTab.click();
         }
      }
   }

   function initElectribeTabUI() {
      const tabs = panelEl.querySelectorAll('.xep-rbm-tab[data-etab]');
      tabs.forEach(tab => {
         const newTab = tab.cloneNode(true);
         tab.parentNode.replaceChild(newTab, tab);
         newTab.addEventListener('click', () => {
            panelEl.querySelectorAll('.xep-rbm-tab[data-etab]').forEach(t => t.classList.remove('active'));
            newTab.classList.add('active');
            const tabName = newTab.dataset.etab;
            const monoSlots   = document.getElementById('xep-electribe-mono-slots');
            const stereoSlots = document.getElementById('xep-electribe-stereo-slots');
            if (monoSlots)   { monoSlots.hidden   = tabName !== 'mono';   monoSlots.style.display   = tabName === 'mono'   ? '' : 'none'; }
            if (stereoSlots) { stereoSlots.hidden = tabName !== 'stereo'; stereoSlots.style.display = tabName === 'stereo' ? '' : 'none'; }
         });
      });
   }

   async function renderElectribeSlots(kind) {
      const containerId = kind === 'mono'
         ? 'xep-electribe-mono-slots'
         : 'xep-electribe-stereo-slots';
      const container = document.getElementById(containerId);
      if (!container) return;

      const max = getElectribeMaxSlots();
      const maxSlots = kind === 'mono' ? max.mono : max.stereo;
      const slots = kind === 'mono' ? electribeMonoSlots : electribeStereoSlots;

      container.innerHTML = '';
      for (let i = 0; i < maxSlots; i++) {
         const slot = slots[i];
         const slotEl = document.createElement('div');
         slotEl.className = 'sample';
         slotEl.dataset.slotIndex = i;
         slotEl.dataset.slotKind = kind;

         const label = document.createElement('label');
         if (slot) {
            // Show standard sample name (001, 002s, etc.) + original name
            const stdName = kind === 'mono'
               ? String(i).padStart(3, '0')
               : String(i).padStart(3, '0') + 's';
            label.textContent = `${stdName}: ${slot.sourceName || slot.name || 'sample'}`;
         } else {
            label.textContent = `${String(i).padStart(3, '0')}: (empty)`;
         }
         slotEl.appendChild(label);

         // Pick-from-bucket button
         const pickBtn = document.createElement('button');
         pickBtn.className = 'xep-slot-pick-btn';
         pickBtn.type = 'button';
         pickBtn.textContent = 'pick';
         pickBtn.setAttribute('aria-label', `Pick sample from bucket for slot ${String(i).padStart(3, '0')}`);
         pickBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const stdName = kind === 'mono'
               ? String(i).padStart(3, '0')
               : String(i).padStart(3, '0') + 's';
            openBucketPicker(async (bItem) => {
               if (!bItem || !bItem.wav) { setStatus('selected item has no audio data', true); return; }
               const wavBuf = bItem.wav instanceof ArrayBuffer ? bItem.wav
                  : (bItem.wav.buffer ? bItem.wav.buffer.slice(bItem.wav.byteOffset || 0, (bItem.wav.byteOffset || 0) + bItem.wav.byteLength) : bItem.wav);
               let pcm = bItem.pcm, channels = bItem.channels, sampleRate = bItem.sampleRate, bitDepth = bItem.bitDepth;
               if (!pcm) {
                  try {
                     const decoded = decodeWavToPcm(new Uint8Array(wavBuf));
                     pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate; bitDepth = 16;
                  } catch (_) { /* drawWaveformFromWav will still parse it */ }
               }
               slots[i] = {
                  name: (bItem.name || 'sample').replace(/\.wav$/i, '').slice(0, 8).toUpperCase(),
                  data: wavBuf,
                  pcm,
                  sampleRate,
                  channels,
                  bitDepth,
                  sourceName: bItem.name,
               };
               renderElectribeSlots(kind);
            }, { title: `Pick sample for slot ${stdName}`, filter: (it) => !!it.wav && it.type !== 'image' });
         });
         slotEl.appendChild(pickBtn);

         const canvas = document.createElement('canvas');
         canvas.className = 'samplecanvas';
         canvas.width = 200;
         canvas.height = 80;
         if (slot && slot.data) {
            // Use drawWaveformFromWav which falls back to WAV parsing if PCM is missing
            drawWaveformFromWavBucket(canvas, slot.data, slot.pcm, slot.sampleRate, slot.channels, slot.bitDepth);
         }
         slotEl.appendChild(canvas);
         container.appendChild(slotEl);

         // Click to play
         canvas.addEventListener('click', () => {
            if (slot && slot.data) {
               playItem(slot.data, canvas, slot.pcm, slot.sampleRate, slot.channels, slot.bitDepth);
            }
         });

         // Drag-to-bucket (if slot has data)
         if (slot && slot.data) {
            slotEl.draggable = true;
            slotEl.addEventListener('dragstart', (e) => {
               e.dataTransfer.effectAllowed = 'copy';
               const blobUrl = URL.createObjectURL(new Blob([slot.data], { type: 'audio/wav' }));
               e.dataTransfer.setData('application/x-module-url', blobUrl);
               e.dataTransfer.setData('application/x-module-sample-name', slot.name || `slot_${i}`);
               e.dataTransfer.setData('text/plain', slot.name || `slot_${i}`);
               setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            });
         }

         // Right-click to clear slot
         slotEl.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            if (slots[i]) {
               slots[i] = null;
               renderElectribeSlots(kind);
            }
         });

         // Drop handling
         slotEl.addEventListener('dragover', (e) => {
            e.preventDefault();
            slotEl.classList.add('xep-rbm-slot-dragover');
         });
         slotEl.addEventListener('dragleave', () => {
            slotEl.classList.remove('xep-rbm-slot-dragover');
         });
         slotEl.addEventListener('drop', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            slotEl.classList.remove('xep-rbm-slot-dragover');
            await handleElectribeDrop(e, i, kind, slots);
         });
      }
   }

   async function handleElectribeDrop(e, index, kind, slots) {
      // Case 1: OS file dropped from disk
      const file = e.dataTransfer.files[0];
      if (file) {
         const data = await fileToUint8Array(file);
         try {
            const decoded = decodeWavToPcm(data);
            slots[index] = {
               name: file.name.replace(/\.[^.]+$/, '').slice(0, 8).toUpperCase(),
               data: data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength),
               pcm: decoded.pcm,
               sampleRate: decoded.sampleRate,
               channels: decoded.channels,
               bitDepth: 16,
               sourceName: file.name,
            };
         } catch (err) {
            setStatus(`Cannot decode WAV: ${err.message}`, true);
            return;
         }
         renderElectribeSlots(kind);
         return;
      }

      // Case 2: wavedit tab dragged
      const waveditTabId = e.dataTransfer.getData('application/x-wavedit-tab');
      if (waveditTabId) {
         const resp = await new Promise((resolve) => {
            const handler = (ev) => {
               window.removeEventListener('wavedit-tab-wav-data', handler);
               resolve(ev.detail);
            };
            window.addEventListener('wavedit-tab-wav-data', handler);
            window.dispatchEvent(new CustomEvent('wavedit-tab-request-wav', {
               detail: { tabId: waveditTabId }
            }));
            setTimeout(() => {
               window.removeEventListener('wavedit-tab-wav-data', handler);
               resolve(null);
            }, 3000);
         });
         if (resp && resp.wav) {
            const wavBuf = resp.wav instanceof ArrayBuffer ? resp.wav
               : (resp.wav.buffer ? resp.wav.buffer.slice(resp.wav.byteOffset || 0, (resp.wav.byteOffset || 0) + resp.wav.byteLength) : resp.wav);
            slots[index] = {
               name: (resp.name || 'wavedit').slice(0, 8).toUpperCase(),
               data: wavBuf,
               pcm: resp.pcm || null,
               sampleRate: resp.sampleRate || null,
               channels: resp.channels || 1,
               bitDepth: resp.bitDepth || 16,
               sourceName: resp.name || 'wavedit tab',
            };
            renderElectribeSlots(kind);
         }
         return;
      }

      // Case 3: bucket item dragged
      let bid = e.dataTransfer.getData('application/x-bucket-item-id');
      if (!bid) bid = e.dataTransfer.getData('text/plain');
      if (!bid) return;
      const bItem = bucket.items.find((i) => i.id === bid);
      if (!bItem || !bItem.wav) {
         setStatus('dropped item has no audio data', true);
         return;
      }
      const wavBuf = bItem.wav instanceof ArrayBuffer ? bItem.wav
         : (bItem.wav.buffer ? bItem.wav.buffer.slice(bItem.wav.byteOffset || 0, (bItem.wav.byteOffset || 0) + bItem.wav.byteLength) : bItem.wav);
      // Decode PCM from WAV if the bucket item doesn't have pre-decoded PCM
      let pcm = bItem.pcm, channels = bItem.channels, sampleRate = bItem.sampleRate, bitDepth = bItem.bitDepth;
      if (!pcm) {
         try {
            const decoded = decodeWavToPcm(new Uint8Array(wavBuf));
            pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate; bitDepth = 16;
         } catch (_) { /* drawWaveformFromWav will still parse it */ }
      }
      slots[index] = {
         name: (bItem.name || 'sample').replace(/\.wav$/i, '').slice(0, 8).toUpperCase(),
         data: wavBuf,
         pcm,
         sampleRate,
         channels,
         bitDepth,
         sourceName: bItem.name,
      };
      renderElectribeSlots(kind);
   }

   // Build the electribe file from the current slot state
   async function runElectribeExport(target) {
      const nameEl = document.getElementById('exp-electribe-name');
      const fmtEl  = document.getElementById('exp-electribe-fmt');
      const bankName = (nameEl ? nameEl.value : 'XTRC').trim() || 'XTRC';
      const fmt = fmtEl ? fmtEl.value : 'esx';

      // Collect non-empty slots
      const monoSamples = electribeMonoSlots.filter(s => s);
      const stereoSamples = fmt === 'es1' ? [] : electribeStereoSlots.filter(s => s);

      if (!monoSamples.length && !stereoSamples.length) {
         setStatus('no samples in slot grid — drag audio from the bucket', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Preparing PCM');

      // Decode all samples to PCM
      const allSamples = [];
      let monoIdx = 0, stereoIdx = 0;
      for (let i = 0; i < monoSamples.length; i++) {
         const s = monoSamples[i];
         updateProgress('Decoding samples…', 10 + Math.round((i / monoSamples.length) * 20), `mono ${i+1}/${monoSamples.length}`);
         let pcm = s.pcm, channels = s.channels, sampleRate = s.sampleRate;
         if (!pcm) {
            try {
               const decoded = decodeWavToPcm(s.data);
               pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate;
            } catch (e) { continue; }
         }
         // For mono slots, force mono (downmix if stereo)
         if (channels === 2) {
            const numFrames = pcm.length / 2;
            const mono = new Int16Array(numFrames);
            for (let j = 0; j < numFrames; j++) {
               mono[j] = Math.round((pcm[j * 2] + pcm[j * 2 + 1]) / 2);
            }
            pcm = mono; channels = 1;
         }
         // Standard ESX/ES1 mono sample name: zero-padded 3-digit slot number
         allSamples.push({ name: String(monoIdx).padStart(3, '0'), pcm, channels: 1, sampleRate: sampleRate || 44100 });
         monoIdx++;
      }

      for (let i = 0; i < stereoSamples.length; i++) {
         const s = stereoSamples[i];
         updateProgress('Decoding samples…', 30 + Math.round((i / stereoSamples.length) * 20), `stereo ${i+1}/${stereoSamples.length}`);
         let pcm = s.pcm, channels = s.channels, sampleRate = s.sampleRate;
         if (!pcm) {
            try {
               const decoded = decodeWavToPcm(s.data);
               pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate;
            } catch (e) { continue; }
         }
         // For stereo slots, force stereo (duplicate if mono)
         if (channels === 1) {
            const stereo = new Int16Array(pcm.length * 2);
            for (let j = 0; j < pcm.length; j++) {
               stereo[j * 2] = pcm[j];
               stereo[j * 2 + 1] = pcm[j];
            }
            pcm = stereo; channels = 2;
         }
         // Standard ESX stereo sample name: zero-padded 3-digit slot number + 's'
         allSamples.push({ name: String(stereoIdx).padStart(3, '0') + 's', pcm, channels: 2, sampleRate: sampleRate || 44100 });
         stereoIdx++;
      }

      updateProgress('Building file…', 50, fmt === 'esx' ? 'ESX raw PCM' : 'ES1 ADPCM');

      let fileData;
      if (fmt === 'esx') {
         fileData = buildESX(allSamples);
      } else {
         fileData = buildES1(allSamples, (msg) => updateProgress('Compressing…', 60, msg));
      }

      // Check for overflow — samples didn't fit in the hardware's memory
      if (fileData._overflow) {
         const written = fileData._written;
         const total = fileData._total;
         const limit = fmt === 'es1'
            ? '3 MB audio zone (100 mono slots, ~196s at 32kHz)'
            : '32 MB (256 mono + 128 stereo slots)';
         hideProgress();
         setStatus(`Too many samples: only ${written}/${total} fit in the ${fmt.toUpperCase()} ${limit}. Remove some samples and try again.`, true);
         runBtn.disabled = false;
         return;
      }

      const blob = new Blob([fileData], { type: 'application/octet-stream' });
      const filename = bankName.replace(/[\\/:*?"<>|]/g, '').trim() + '.' + fmt;
      updateProgress('Downloading…', 95, filename);
      triggerDownload(blob, filename);
   }

   // ---- SF2 / DLS export (Phase 2+3: advanced editor) ----------------------
   // sf2Slots[i]: null or {
   //   name, data, pcm, sampleRate, channels, bitDepth, sourceName,
   //   originalKey, loopStart, loopEnd, loopMode,
   //   generators: { [genType]: value, ... },
   //   keyRange: [lo, hi], velRange: [lo, hi],
   //   sampleType: 1=mono, 2=right, 4=left, 8=link
   //   linkedSampleIndex: number
   // }
   const SF2_MAX_SAMPLES = 2048;
   let sf2Slots = new Array(SF2_MAX_SAMPLES).fill(null);
   let sf2DisplayCount = 1; // how many slots to show in the grid (grows progressively)
   let sf2SelectedSlot = -1;
   let sf2ActiveTab = 'generators';
   let sf2KeyboardOctave = 5;

   // Default generator values (SF2 spec defaults)
   const SF2_DEFAULT_GENS = {
      [SF2_GEN.pan]: 0,
      [SF2_GEN.initialAttenuation]: 0,
      [SF2_GEN.coarseTune]: 0,
      [SF2_GEN.fineTune]: 0,
      [SF2_GEN.delayVolEnv]: -12000,
      [SF2_GEN.attackVolEnv]: -12000,
      [SF2_GEN.holdVolEnv]: -12000,
      [SF2_GEN.decayVolEnv]: -12000,
      [SF2_GEN.sustainVolEnv]: 0,
      [SF2_GEN.releaseVolEnv]: -12000,
      [SF2_GEN.delayModEnv]: -12000,
      [SF2_GEN.attackModEnv]: -12000,
      [SF2_GEN.holdModEnv]: -12000,
      [SF2_GEN.decayModEnv]: -12000,
      [SF2_GEN.sustainModEnv]: 0,
      [SF2_GEN.releaseModEnv]: -12000,
      [SF2_GEN.initialFilterFc]: 13500,
      [SF2_GEN.initialFilterQ]: 0,
      [SF2_GEN.chorusEffectsSend]: 0,
      [SF2_GEN.reverbEffectsSend]: 0,
      [SF2_GEN.scaleTuning]: 100,
      [SF2_GEN.exclusiveClass]: 0,
      [SF2_GEN.sampleModes]: 0,
   };

   // Generator metadata for UI rendering
   const SF2_GEN_META = [
      { type: SF2_GEN.overridingRootKey, label: 'Root Key', min: 0, max: 127, step: 1, unit: 'MIDI' },
      { type: SF2_GEN.coarseTune, label: 'Coarse Tune', min: -127, max: 127, step: 1, unit: 'semi' },
      { type: SF2_GEN.fineTune, label: 'Fine Tune', min: -19999, max: 19999, step: 1, unit: 'cents' },
      { type: SF2_GEN.pan, label: 'Pan', min: -500, max: 500, step: 1, unit: '0.1%' },
      { type: SF2_GEN.initialAttenuation, label: 'Attenuation', min: 0, max: 960, step: 1, unit: '0.1dB' },
      { type: SF2_GEN.scaleTuning, label: 'Scale Tuning', min: 0, max: 1200, step: 1, unit: 'cents/key' },
      { type: SF2_GEN.exclusiveClass, label: 'Exclusive Class', min: 0, max: 127, step: 1, unit: '' },
      { type: SF2_GEN.sampleModes, label: 'Loop Mode', min: 0, max: 3, step: 1, unit: '0=off,1=loop,2=oneshot,3=loop+rel' },
      { type: SF2_GEN.initialFilterFc, label: 'Filter Cutoff', min: 1500, max: 13500, step: 1, unit: 'cents' },
      { type: SF2_GEN.initialFilterQ, label: 'Filter Q', min: 0, max: 960, step: 1, unit: '0.1dB' },
      { type: SF2_GEN.delayVolEnv, label: 'Vol Env Delay', min: -12000, max: 5000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.attackVolEnv, label: 'Vol Env Attack', min: -12000, max: 8000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.holdVolEnv, label: 'Vol Env Hold', min: -12000, max: 5000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.decayVolEnv, label: 'Vol Env Decay', min: -12000, max: 8000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.sustainVolEnv, label: 'Vol Env Sustain', min: 0, max: 1440, step: 1, unit: '0.1dB' },
      { type: SF2_GEN.releaseVolEnv, label: 'Vol Env Release', min: -12000, max: 8000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.delayModEnv, label: 'Mod Env Delay', min: -12000, max: 5000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.attackModEnv, label: 'Mod Env Attack', min: -12000, max: 8000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.holdModEnv, label: 'Mod Env Hold', min: -12000, max: 5000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.decayModEnv, label: 'Mod Env Decay', min: -12000, max: 8000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.sustainModEnv, label: 'Mod Env Sustain', min: 0, max: 1000, step: 1, unit: '0.1%' },
      { type: SF2_GEN.releaseModEnv, label: 'Mod Env Release', min: -12000, max: 8000, step: 1, unit: 'timecents' },
      { type: SF2_GEN.chorusEffectsSend, label: 'Chorus Send', min: 0, max: 1000, step: 1, unit: '0.1%' },
      { type: SF2_GEN.reverbEffectsSend, label: 'Reverb Send', min: 0, max: 1000, step: 1, unit: '0.1%' },
   ];

   function updateSF2Visibility(target) {
      // [SF2] updateSF2Visibility
      const sf2Layout  = document.getElementById('xep-sf2-layout');
      const standardList = document.getElementById('xep-standard-list');
      const rbmTabs   = document.getElementById('xep-rbm-tabs');
      const etTabs    = document.getElementById('xep-electribe-tabs');
      const etMono    = document.getElementById('xep-electribe-mono-slots');
      const etStereo  = document.getElementById('xep-electribe-stereo-slots');
      const rex2Layout = document.getElementById('xep-rex2-layout');

      if (target === 'sf2') {
         // [SF2] activating SF2 mode
         if (sf2Layout)  { sf2Layout.hidden = false; sf2Layout.classList.add('xep-sf2-active'); }
         if (standardList) { standardList.hidden = true; standardList.style.display = 'none'; }
         if (rbmTabs) rbmTabs.hidden = true;
         if (etTabs) etTabs.hidden = true;
         if (etMono)   { etMono.hidden = true;   etMono.style.display = 'none'; }
         if (etStereo) { etStereo.hidden = true; etStereo.style.display = 'none'; }
         if (rex2Layout) rex2Layout.hidden = true;
         try {
            renderSF2Slots();
            renderSF2Detail();
            renderSF2Keyboard();
         } catch (e) {
            console.error('[SF2] render error:', e);
         }
         // Wire up import button
         const importBtn = document.getElementById('exp-sf2-import-btn');
         if (importBtn && !importBtn._sf2wired) {
            importBtn._sf2wired = true;
            importBtn.addEventListener('click', () => {
               const input = document.createElement('input');
               input.type = 'file';
               input.accept = '.sf2';
               input.onchange = async () => {
                  const file = input.files[0];
                  if (!file) return;
                  try {
                     const buf = await file.arrayBuffer();
                     const bank = parseSF2(buf);
                     loadSF2Bank(bank);
                  } catch (e) {
                     setStatus(`SF2 import failed: ${e.message}`, true);
                  }
               };
               input.click();
            });
         }
         // Wire up splitter drag
         wireSf2Splitter();
      } else if (target === 'rx2') {
         // [REX2] activating REX2 mode
         if (sf2Layout)  { sf2Layout.hidden = true; sf2Layout.classList.remove('xep-sf2-active'); }
         if (rex2Layout) rex2Layout.hidden = false;
         if (standardList) { standardList.hidden = true; standardList.style.display = 'none'; }
         if (rbmTabs) rbmTabs.hidden = true;
         if (etTabs) etTabs.hidden = true;
         if (etMono)   { etMono.hidden = true;   etMono.style.display = 'none'; }
         if (etStereo) { etStereo.hidden = true; etStereo.style.display = 'none'; }
         wireRex2Editor();
         // Ensure pick button is wired even if wireRex2Editor was already called
         const pickBtn = document.getElementById('xep-rex2-pick-sample');
         if (pickBtn && !pickBtn.dataset.wired) {
            pickBtn.dataset.wired = '1';
            pickBtn.addEventListener('click', () => {
               // [REX2] pick sample button clicked (fallback)
               openBucketPicker((bItem) => {
                  if (!bItem || !bItem.wav) { setStatus('selected item has no audio data', true); return; }
                  let wavBuf = bItem.wav;
                  if (ArrayBuffer.isView(wavBuf)) {
                     wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
                  } else if (!(wavBuf instanceof ArrayBuffer)) {
                     setStatus('selected item has invalid audio data', true);
                     return;
                  }
                  rex2LoadFromWav(wavBuf, bItem.name);
               }, { title: 'Pick sample for REX2', filter: (it) => !!it.wav && it.type !== 'image' });
            });
         }
         // Render waveform if we have items
         rex2RenderWaveform();
      } else {
         // [SF2] deactivating SF2 mode
         if (sf2Layout)  { sf2Layout.hidden = true; sf2Layout.classList.remove('xep-sf2-active'); }
         if (rex2Layout) rex2Layout.hidden = true;
         if (standardList && target !== 'rbm' && target !== 'electribe') {
            standardList.hidden = false;
            standardList.style.display = '';
         }
      }
   }

   // ---- REX2 waveform editor state ----
   const rex2State = {
      audioBuffer: null,     // Float32Array (mono mix) for display + detection
      channels: null,        // Float32Array[] per original channel (for export)
      sampleRate: 0,
      numChannels: 1,
      frameCount: 0,
      fileName: '',
      slices: [],            // [{ start, end, name }] in samples
      viewStart: 0,          // 0..1
      viewEnd: 1,            // 0..1
      wired: false,
   };

   // REX2 auto-detect + re-slice (same as breakchop detectTransients)
   function rex2AutoSlice() {
      if (!rex2State.audioBuffer) return;
      const sensInput = document.getElementById('xep-rex2-sensitivity');
      const minSliceInput = document.getElementById('xep-rex2-minslice');
      const sens = sensInput ? parseFloat(sensInput.value) : 1.5;
      const minMs = minSliceInput ? parseInt(minSliceInput.value) : 80;
      const onsets = detectTransients(rex2State.audioBuffer, rex2State.sampleRate, { sensitivity: sens, minSliceMs: minMs });
      rex2State.slices = slicesFromOnsets(onsets, rex2State.frameCount);
      rex2RenderSliceMarkers();
      rex2UpdateSliceCount();
   }

   function wireRex2Editor() {
      if (rex2State.wired) return;
      rex2State.wired = true;

      const autoBtn = document.getElementById('xep-rex2-auto-slice');
      const clearBtn = document.getElementById('xep-rex2-clear-slices');
      const pickBtn = document.getElementById('xep-rex2-pick-sample');
      const sensInput = document.getElementById('xep-rex2-sensitivity');
      const minSliceInput = document.getElementById('xep-rex2-minslice');
      const container = document.getElementById('xep-rex2-waveform-container');
      const canvas = document.getElementById('xep-rex2-waveform-canvas');
      const scrollbar = document.getElementById('xep-rex2-scrollbar');
      const scrollHandle = document.getElementById('xep-rex2-scroll-handle');

      // Pick Sample button — opens bucket picker to select a sample
      if (pickBtn) pickBtn.addEventListener('click', () => {
         // [REX2] pick sample button clicked
         openBucketPicker((bItem) => {
            if (!bItem || !bItem.wav) { setStatus('selected item has no audio data', true); return; }
            let wavBuf = bItem.wav;
            if (ArrayBuffer.isView(wavBuf)) {
               wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
            } else if (!(wavBuf instanceof ArrayBuffer)) {
               setStatus('selected item has invalid audio data', true);
               return;
            }
            rex2LoadFromWav(wavBuf, bItem.name);
         }, { title: 'Pick sample for REX2', filter: (it) => !!it.wav && it.type !== 'image' });
      });
      // if (!pickBtn) [REX2] pick sample button not found in DOM

      // Auto Slice button
      if (autoBtn) autoBtn.addEventListener('click', () => {
         if (!rex2State.audioBuffer) { setStatus('drag an audio file into the export list first', true); return; }
         rex2AutoSlice();
      });

      // Clear button
      if (clearBtn) clearBtn.addEventListener('click', () => {
         rex2State.slices = [];
         rex2RenderSliceMarkers();
         rex2UpdateSliceCount();
      });

      // Live re-detect when sensitivity/minslice sliders change (like breakchop)
      if (sensInput) sensInput.addEventListener('input', () => {
         if (rex2State.audioBuffer) rex2AutoSlice();
      });
      if (minSliceInput) minSliceInput.addEventListener('input', () => {
         if (rex2State.audioBuffer) rex2AutoSlice();
      });

      // Prevent system context menu on the waveform canvas (like breakchop)
      if (canvas) canvas.addEventListener('contextmenu', (e) => e.preventDefault());

      // Click on waveform to add slice marker (like breakchop setupWaveformClick)
      if (container) container.addEventListener('click', (e) => {
         if (!rex2State.audioBuffer) return;
         if (e.target.classList.contains('slice-marker')) return;
         const rect = container.getBoundingClientRect();
         const x = (e.clientX - rect.left) / rect.width;
         const totalSamples = rex2State.frameCount;
         const visibleStart = rex2State.viewStart * totalSamples;
         const visibleEnd = rex2State.viewEnd * totalSamples;
         const samplePos = Math.floor(visibleStart + x * (visibleEnd - visibleStart));

         // Insert sorted (like breakchop)
         let insertIdx = rex2State.slices.findIndex((s) => s.start > samplePos);
         if (insertIdx === -1) insertIdx = rex2State.slices.length;
         const end = insertIdx < rex2State.slices.length ? rex2State.slices[insertIdx].start : totalSamples;
         if (insertIdx > 0) rex2State.slices[insertIdx - 1].end = samplePos;
         rex2State.slices.splice(insertIdx, 0, { start: samplePos, end, name: `slice_${insertIdx + 1}` });
         // Renumber
         rex2State.slices.forEach((s, i) => (s.name = `slice_${i + 1}`));

         rex2RenderSliceMarkers();
         rex2UpdateSliceCount();
      });

      // Scrollbar drag (like breakchop setupScrollbar — delta-based)
      if (scrollbar && scrollHandle) {
         let dragging = false;
         let dragStartX = 0;
         let dragStartViewStart = 0;
         let dragStartViewEnd = 0;

         scrollHandle.style.left = '0%';
         scrollHandle.style.width = '100%';

         scrollHandle.addEventListener('mousedown', (e) => {
            e.preventDefault();
            dragging = true;
            dragStartX = e.clientX;
            dragStartViewStart = rex2State.viewStart;
            dragStartViewEnd = rex2State.viewEnd;
         });

         document.addEventListener('mousemove', (e) => {
            if (!dragging) return;
            const rect = scrollbar.getBoundingClientRect();
            const dx = (e.clientX - dragStartX) / rect.width;
            const range = dragStartViewEnd - dragStartViewStart;
            let newStart = dragStartViewStart + dx;
            let newEnd = dragStartViewEnd + dx;
            if (newStart < 0) { newStart = 0; newEnd = range; }
            if (newEnd > 1) { newEnd = 1; newStart = 1 - range; }
            rex2State.viewStart = newStart;
            rex2State.viewEnd = newEnd;
            rex2UpdateScrollHandle();
            rex2RenderWaveform();
         });

         document.addEventListener('mouseup', () => { dragging = false; });
      }

      // Wheel to zoom (like breakchop)
      if (container) container.addEventListener('wheel', (e) => {
         if (!rex2State.audioBuffer) return;
         e.preventDefault();
         const rect = container.getBoundingClientRect();
         const x = (e.clientX - rect.left) / rect.width;
         const center = rex2State.viewStart + x * (rex2State.viewEnd - rex2State.viewStart);
         const zoomFactor = e.deltaY > 0 ? 1.2 : 0.8;
         const range = (rex2State.viewEnd - rex2State.viewStart) * zoomFactor;
         rex2State.viewStart = Math.max(0, center - range / 2);
         rex2State.viewEnd = Math.min(1, rex2State.viewStart + range);
         rex2State.viewStart = rex2State.viewEnd - range;
         if (rex2State.viewStart < 0) rex2State.viewStart = 0;
         rex2UpdateScrollHandle();
         rex2RenderWaveform();
      }, { passive: false });
   }

   function rex2UpdateSliceCount() {
      const el = document.getElementById('xep-rex2-slice-count');
      if (el) el.textContent = `${rex2State.slices.length} slice${rex2State.slices.length !== 1 ? 's' : ''}`;
   }

   function rex2UpdateScrollHandle() {
      const handle = document.getElementById('xep-rex2-scroll-handle');
      if (!handle) return;
      const leftPct = rex2State.viewStart * 100;
      const widthPct = (rex2State.viewEnd - rex2State.viewStart) * 100;
      handle.style.left = leftPct + '%';
      handle.style.width = widthPct + '%';
   }

   function rex2RenderWaveform() {
      const canvas = document.getElementById('xep-rex2-waveform-canvas');
      const container = document.getElementById('xep-rex2-waveform-container');
      if (!canvas || !container) return;
      if (!rex2State.audioBuffer) return;

      // Wait for layout (container must be visible with real dimensions)
      let drawWidth = container.clientWidth;
      let drawHeight = container.clientHeight;
      if (drawWidth < 2 || drawHeight < 2) {
         requestAnimationFrame(() => rex2RenderWaveform());
         return;
      }

      canvas.width = drawWidth;
      canvas.height = drawHeight;

      const totalSamples = rex2State.frameCount;
      if (totalSamples === 0) return;

      const startSample = Math.floor(rex2State.viewStart * totalSamples);
      const endSample = Math.floor(rex2State.viewEnd * totalSamples);
      if (endSample <= startSample) return;

      const mono = rex2State.audioBuffer;
      const visibleSamples = endSample - startSample;
      const amp = drawHeight / 2;

      // Direct canvas drawing — min/max per pixel column (same algorithm as
      // breakchop's renderWaveform from shared/ui/waveform.js)
      const ctx = canvas.getContext('2d');
      const bgColor = (window.themeColors && window.themeColors[0]) || '#000000';
      const waveColor = (window.themeColors && window.themeColors[9]) || '#e1e1e1';

      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, drawWidth, drawHeight);

      const samplesPerPixel = Math.max(1, visibleSamples / drawWidth);
      ctx.fillStyle = waveColor;
      for (let x = 0; x < drawWidth; x++) {
         const sampleStart = startSample + Math.floor(x * samplesPerPixel);
         if (sampleStart >= endSample) break;
         const sampleEnd = Math.min(endSample, Math.floor(sampleStart + samplesPerPixel));

         let min = 1.0, max = -1.0;
         for (let j = sampleStart; j < sampleEnd; j++) {
            const datum = mono[j];
            if (datum < min) min = datum;
            if (datum > max) max = datum;
         }

         const y = (1 + min) * amp;
         const h = Math.max(1, (max - min) * amp);
         ctx.fillRect(x, y, 1, h);
      }

      rex2RenderSliceMarkers();
      rex2UpdateScrollHandle();
   }

   function rex2RenderSliceMarkers() {
      const markers = document.getElementById('xep-rex2-slice-markers');
      const container = document.getElementById('xep-rex2-waveform-container');
      if (!markers || !container) return;
      markers.innerHTML = '';

      if (!rex2State.audioBuffer) return;

      const totalSamples = rex2State.frameCount;
      const visibleStart = rex2State.viewStart * totalSamples;
      const visibleEnd = rex2State.viewEnd * totalSamples;
      const visibleRange = visibleEnd - visibleStart;

      rex2State.slices.forEach((slice, i) => {
         const marker = document.createElement('div');
         marker.className = 'slice-marker';
         const pct = ((slice.start - visibleStart) / visibleRange) * 100;
         if (pct < 0 || pct > 100) return;
         marker.style.left = pct + '%';
         marker.dataset.sliceIndex = i;
         marker.title = `Slice ${i + 1}`;

         // Right-click to delete (like breakchop)
         marker.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            rex2State.slices.splice(i, 1);
            rex2State.slices.forEach((s, idx) => (s.name = `slice_${idx + 1}`));
            rex2RenderSliceMarkers();
            rex2UpdateSliceCount();
         });

         // Drag to move (like breakchop — captures visibleStart/visibleRange at mousedown)
         let dragging = false;
         marker.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            dragging = true;
         });
         document.addEventListener('mousemove', (e) => {
            if (!dragging) return;
            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const samplePos = visibleStart + (x / rect.width) * visibleRange;
            rex2State.slices[i].start = Math.max(0, Math.min(totalSamples - 1, Math.floor(samplePos)));
            rex2RenderSliceMarkers();
         });
         document.addEventListener('mouseup', () => {
            if (dragging) dragging = false;
         });

         markers.appendChild(marker);
      });
   }

   // Called when items are added/removed from the export list in REX2 mode
   function rex2LoadFromExportItems() {
      const items = exportItems;
      if (!items.length || !items[0].wav) {
         rex2State.audioBuffer = null;
         rex2State.slices = [];
         rex2RenderWaveform();
         rex2UpdateSliceCount();
         return;
      }

      // Use first item for REX2 auto mode
      const it = items[0];
      let wavBuf = it.wav;
      if (ArrayBuffer.isView(wavBuf)) {
         wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
      } else if (!(wavBuf instanceof ArrayBuffer)) {
         rex2State.audioBuffer = null;
         return;
      }

      rex2LoadFromWav(wavBuf, it.name);
   }

   // Core: parse a WAV ArrayBuffer and load it into the REX2 waveform editor
   function rex2LoadFromWav(wavBuf, name) {
      const dv = new DataView(wavBuf);
      let off = 12, sampleRate = 44100, numChannels = 1, bitsPerSample = 16, dataOff = 0, dataLen = 0;
      while (off + 8 <= wavBuf.byteLength) {
         const cid = dv.getUint32(off, true);
         const csz = dv.getUint32(off + 4, true);
         if (cid === 0x20746d66) { sampleRate = dv.getUint32(off + 12, true); numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); }
         else if (cid === 0x61746164) { dataOff = off + 8; dataLen = csz; break; }
         off += 8 + csz + (csz & 1);
      }
      if (!dataOff) { rex2State.audioBuffer = null; rex2RenderWaveform(); rex2UpdateSliceCount(); return; }

      const bps = (bitsPerSample / 8) * numChannels;
      const numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
      if (numSamples <= 0) { rex2State.audioBuffer = null; rex2RenderWaveform(); rex2UpdateSliceCount(); return; }

      // Extract channels as Float32
      const channels = [];
      for (let ch = 0; ch < numChannels; ch++) channels.push(new Float32Array(numSamples));
      if (bitsPerSample === 16) {
         for (let j = 0; j < numSamples; j++)
            for (let ch = 0; ch < numChannels; ch++)
               channels[ch][j] = dv.getInt16(dataOff + j * bps + ch * 2, true) / 32768;
      } else if (bitsPerSample === 24) {
         for (let j = 0; j < numSamples; j++)
            for (let ch = 0; ch < numChannels; ch++) {
               const bo = dataOff + j * bps + ch * 3;
               const v = (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               channels[ch][j] = v / 32768;
            }
      } else if (bitsPerSample === 32) {
         for (let j = 0; j < numSamples; j++)
            for (let ch = 0; ch < numChannels; ch++)
               channels[ch][j] = dv.getFloat32(dataOff + j * bps + ch * 4, true);
      } else if (bitsPerSample === 8) {
         for (let j = 0; j < numSamples; j++)
            for (let ch = 0; ch < numChannels; ch++)
               channels[ch][j] = (dv.getUint8(dataOff + j * bps + ch) - 128) / 128;
      }

      // Mix to mono for waveform display + transient detection
      const mono = new Float32Array(numSamples);
      for (let ch = 0; ch < numChannels; ch++)
         for (let j = 0; j < numSamples; j++)
            mono[j] += channels[ch][j] / numChannels;

      rex2State.audioBuffer = mono; // store mono mix (Float32Array, like breakchop)
      rex2State.channels = channels; // keep per-channel for export
      rex2State.sampleRate = sampleRate;
      rex2State.numChannels = numChannels;
      rex2State.frameCount = numSamples;
      rex2State.fileName = (name || 'xtrc').replace(/\.wav$/i, '');
      // Populate filename input
      const fnInput = document.getElementById('xep-rex2-filename');
      if (fnInput) fnInput.value = rex2State.fileName.substring(0, 11);
      rex2State.viewStart = 0;
      rex2State.viewEnd = 1;
      rex2State.slices = [];

      // Auto-detect transients on load (like breakchop)
      const sensInput = document.getElementById('xep-rex2-sensitivity');
      const minSliceInput = document.getElementById('xep-rex2-minslice');
      const sens = sensInput ? parseFloat(sensInput.value) : 1.5;
      const minMs = minSliceInput ? parseInt(minSliceInput.value) : 80;
      const onsets = detectTransients(mono, sampleRate, { sensitivity: sens, minSliceMs: minMs });
      rex2State.slices = slicesFromOnsets(onsets, numSamples);

      rex2RenderWaveform();
      rex2UpdateSliceCount();
   }

   // Splitter drag for SF2 layout
   let _sf2SplitterWired = false;
   function wireSf2Splitter() {
      if (_sf2SplitterWired) return;
      const splitter = document.getElementById('xep-sf2-splitter');
      const left = document.getElementById('xep-sf2-left');
      if (!splitter || !left) return;
      _sf2SplitterWired = true;
      let dragging = false;
      let startX = 0, startW = 0;
      splitter.addEventListener('mousedown', (e) => {
         dragging = true;
         startX = e.clientX;
         startW = left.offsetWidth;
         document.body.style.cursor = 'col-resize';
         document.body.style.userSelect = 'none';
         e.preventDefault();
      });
      document.addEventListener('mousemove', (e) => {
         if (!dragging) return;
         const dw = e.clientX - startX;
         const newW = Math.max(180, Math.min(600, startW + dw));
         left.style.flex = `0 0 ${newW}px`;
      });
      document.addEventListener('mouseup', () => {
         if (dragging) {
            dragging = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
         }
      });
   }

   // Load a parsed SF2 bank into the slot grid
   function loadSF2Bank(bank) {
      sf2Slots = new Array(SF2_MAX_SAMPLES).fill(null);
      let slotIdx = 0;
      for (const s of bank.samples) {
         if (slotIdx >= SF2_MAX_SAMPLES) break;
         if (!s.pcm) continue;
         // Find the instrument zone that references this sample
         let zoneGens = {};
         let keyRange = [0, 127], velRange = [0, 127];
         for (const inst of bank.instruments) {
            for (const z of inst.zones) {
               if (z.sampleIndex === slotIdx) {
                  zoneGens = { ...z.generators };
                  if (z.keyRange) keyRange = z.keyRange;
                  if (z.velRange) velRange = z.velRange;
                  break;
               }
            }
         }
         sf2Slots[slotIdx] = {
            name: s.name,
            pcm: s.pcm,
            sampleRate: s.sampleRate,
            channels: (s.sampleType === 1) ? 1 : 1, // SF2 samples are always mono
            bitDepth: 16,
            sourceName: s.name,
            originalKey: s.originalKey,
            loopStart: s.loopStart || 0,
            loopEnd: s.loopEnd || 0,
            loopMode: zoneGens[SF2_GEN.sampleModes] || 0,
            generators: { ...SF2_DEFAULT_GENS, ...zoneGens, [SF2_GEN.overridingRootKey]: s.originalKey },
            keyRange, velRange,
            sampleType: s.sampleType || 1,
            linkedSampleIndex: s.linkedSampleIndex || 0,
         };
         slotIdx++;
      }
      // Fill in bank name
      const nameEl = document.getElementById('exp-sf2-name');
      if (nameEl && bank.info.name) nameEl.value = bank.info.name.slice(0, 20);
      const engEl = document.getElementById('exp-sf2-engineer');
      if (engEl && bank.info.engineer) engEl.value = bank.info.engineer;
      const cmtEl = document.getElementById('exp-sf2-comment');
      if (cmtEl && bank.info.comment) cmtEl.value = bank.info.comment;
      sf2SelectedSlot = -1;
      renderSF2Slots();
      renderSF2Detail();
      setStatus(`Imported ${slotIdx} samples from SF2`);
   }

   function renderSF2Slots() {
      const container = document.getElementById('xep-sf2-sample-slots');
      // [SF2] renderSF2Slots
      if (!container) { console.error('[SF2] xep-sf2-sample-slots not found!'); return; }
      container.innerHTML = '';

      const filled = sf2Slots.filter(s => s);
      // Auto-expand: always show 1 more empty slot than filled
      if (filled.length >= sf2DisplayCount) {
         sf2DisplayCount = Math.min(filled.length + 1, SF2_MAX_SAMPLES);
      }
      const maxDisplay = sf2DisplayCount;

      for (let i = 0; i < Math.min(maxDisplay, SF2_MAX_SAMPLES); i++) {
         const slot = sf2Slots[i];
         const slotEl = document.createElement('div');
         slotEl.className = 'sample';
         if (i === sf2SelectedSlot) slotEl.classList.add('xep-sf2-selected');
         slotEl.dataset.slotIndex = i;

         const label = document.createElement('label');
         if (slot) {
            const noteName = midiToNoteName(slot.originalKey || 60);
            const loopIcon = slot.loopMode > 0 ? ' ⟲' : '';
            label.textContent = `${String(i).padStart(3, '0')}: ${slot.sourceName || slot.name || 'sample'} [${noteName}]${loopIcon}`;
         } else {
            label.textContent = `${String(i).padStart(3, '0')}: (empty)`;
         }
         slotEl.appendChild(label);

         // Pick-from-bucket button (for empty slots)
         if (!slot) {
            const pickBtn = document.createElement('button');
            pickBtn.className = 'xep-slot-pick-btn';
            pickBtn.type = 'button';
            pickBtn.textContent = 'pick';
            pickBtn.setAttribute('aria-label', `Pick sample from bucket for slot ${String(i).padStart(3, '0')}`);
            pickBtn.addEventListener('click', (e) => {
               e.stopPropagation();
               openBucketPicker(async (bItem) => {
                  if (!bItem || !bItem.wav) { setStatus('selected item has no audio data', true); return; }
                  const wavBuf = bItem.wav instanceof ArrayBuffer ? bItem.wav
                     : (bItem.wav.buffer ? bItem.wav.buffer.slice(bItem.wav.byteOffset || 0, (bItem.wav.byteOffset || 0) + bItem.wav.byteLength) : bItem.wav);
                  let pcm = bItem.pcm, channels = bItem.channels, sampleRate = bItem.sampleRate, bitDepth = bItem.bitDepth;
                  if (!pcm) {
                     try {
                        const decoded = decodeWavToPcm(new Uint8Array(wavBuf));
                        pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate; bitDepth = 16;
                     } catch (_) { /* drawWaveformFromWav will still parse it */ }
                  }
                  sf2Slots[i] = createSF2Slot(bItem.name || 'sample', pcm, sampleRate, channels, wavBuf);
                  sf2Slots[i].sourceName = bItem.name;
                  renderSF2Slots();
                  renderSF2Detail();
               }, { title: `Pick sample for slot ${String(i).padStart(3, '0')}`, filter: (it) => !!it.wav && it.type !== 'image' });
            });
            slotEl.appendChild(pickBtn);
         }

         // Delete button (only for filled slots)
         if (slot) {
            const delBtn = document.createElement('button');
            delBtn.className = 'xep-sf2-slot-del';
            delBtn.textContent = '×';
            delBtn.title = 'Remove sample';
            delBtn.addEventListener('click', (e) => {
               e.stopPropagation();
               sf2Slots[i] = null;
               if (sf2SelectedSlot === i) sf2SelectedSlot = -1;
               renderSF2Slots();
               renderSF2Detail();
            });
            slotEl.appendChild(delBtn);
         }

         const canvas = document.createElement('canvas');
         canvas.className = 'samplecanvas';
         // Use clientWidth/clientHeight after append; set reasonable defaults
         canvas.width = 140;
         canvas.height = 78;
         slotEl.appendChild(canvas);

         // Draw waveform — always use drawWaveformFromWavBucket (same as bucket/adapters)
         // Pass null for pcm so it re-parses the WAV from scratch, handling all bit depths
         const drawSlot = () => {
            if (slot && slot.data) {
               drawWaveformFromWavBucket(canvas, slot.data, null, null, null, null);
            }
         };
         drawSlot();

         // Resize canvas to fill container after layout
         requestAnimationFrame(() => {
            const r = slotEl.getBoundingClientRect();
            if (r.width > 0 && r.height > 0) {
               canvas.width = Math.floor(r.width);
               canvas.height = Math.floor(r.height - 16); // minus label row
               drawSlot();
            }
         });

         // Click to select (and play)
         slotEl.addEventListener('click', (e) => {
            e.stopPropagation();
            sf2SelectedSlot = i;
            renderSF2Slots();
            renderSF2Detail();
            if (slot && slot.pcm) {
               playSF2Preview(i, 60);
            }
         });

         // Right-click to clear
         slotEl.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            if (sf2Slots[i]) {
               sf2Slots[i] = null;
               if (sf2SelectedSlot === i) sf2SelectedSlot = -1;
               renderSF2Slots();
               renderSF2Detail();
            }
         });

         // Drop handling
         slotEl.addEventListener('dragover', (e) => {
            e.preventDefault();
            slotEl.classList.add('xep-rbm-slot-dragover');
         });
         slotEl.addEventListener('dragleave', () => {
            slotEl.classList.remove('xep-rbm-slot-dragover');
         });
         slotEl.addEventListener('drop', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            slotEl.classList.remove('xep-rbm-slot-dragover');
            await handleSF2Drop(e, i);
         });

         container.appendChild(slotEl);
      }

      // "Add more slots" button
      if (sf2DisplayCount < SF2_MAX_SAMPLES) {
         const addBtn = document.createElement('div');
         addBtn.className = 'xep-sf2-add-slots';
         addBtn.textContent = `+ Add slot (${sf2DisplayCount}/${SF2_MAX_SAMPLES})`;
         addBtn.addEventListener('click', () => {
            sf2DisplayCount = Math.min(sf2DisplayCount + 1, SF2_MAX_SAMPLES);
            renderSF2Slots();
         });
         container.appendChild(addBtn);
      }
   }

   async function handleSF2Drop(e, index) {
      const file = e.dataTransfer.files[0];
      if (file) {
         const data = await fileToUint8Array(file);
         try {
            const decoded = decodeWavToPcm(data);
            sf2Slots[index] = createSF2Slot(file.name.replace(/\.[^.]+$/, ''), decoded.pcm, decoded.sampleRate, decoded.channels, data);
         } catch (err) {
            setStatus(`Cannot decode WAV: ${err.message}`, true);
            return;
         }
         sf2SelectedSlot = index;
         renderSF2Slots();
         renderSF2Detail();
         return;
      }

      // Case 2: wavedit tab
      const waveditTabId = e.dataTransfer.getData('application/x-wavedit-tab');
      if (waveditTabId) {
         const resp = await new Promise((resolve) => {
            const handler = (ev) => {
               window.removeEventListener('wavedit-tab-wav-data', handler);
               resolve(ev.detail);
            };
            window.addEventListener('wavedit-tab-wav-data', handler);
            window.dispatchEvent(new CustomEvent('wavedit-tab-request-wav', {
               detail: { tabId: waveditTabId }
            }));
            setTimeout(() => {
               window.removeEventListener('wavedit-tab-wav-data', handler);
               resolve(null);
            }, 3000);
         });
         if (resp && resp.wav) {
            const wavBuf = resp.wav instanceof ArrayBuffer ? resp.wav
               : (resp.wav.buffer ? resp.wav.buffer.slice(resp.wav.byteOffset || 0, (resp.wav.byteOffset || 0) + resp.wav.byteLength) : resp.wav);
            let pcm = resp.pcm, channels = resp.channels, sampleRate = resp.sampleRate;
            if (!pcm) {
               try {
                  const decoded = decodeWavToPcm(new Uint8Array(wavBuf));
                  pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate;
               } catch (_) {}
            }
            sf2Slots[index] = createSF2Slot(resp.name || 'wavedit', pcm, sampleRate, channels, wavBuf);
            sf2SelectedSlot = index;
            renderSF2Slots();
            renderSF2Detail();
         }
         return;
      }

      // Case 3: bucket item
      let bid = e.dataTransfer.getData('application/x-bucket-item-id');
      if (!bid) bid = e.dataTransfer.getData('text/plain');
      if (!bid) return;
      const bItem = bucket.items.find((i) => i.id === bid);
      if (!bItem || !bItem.wav) {
         setStatus('dropped item has no audio data', true);
         return;
      }
      const wavBuf = bItem.wav instanceof ArrayBuffer ? bItem.wav
         : (bItem.wav.buffer ? bItem.wav.buffer.slice(bItem.wav.byteOffset || 0, (bItem.wav.byteOffset || 0) + bItem.wav.byteLength) : bItem.wav);
      let pcm = bItem.pcm, channels = bItem.channels, sampleRate = bItem.sampleRate;
      if (!pcm) {
         try {
            const decoded = decodeWavToPcm(new Uint8Array(wavBuf));
            pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate;
         } catch (_) {}
      }
      sf2Slots[index] = createSF2Slot(bItem.name || 'sample', pcm, sampleRate, channels, wavBuf);
      sf2SelectedSlot = index;
      renderSF2Slots();
      renderSF2Detail();
   }

   // Create a new SF2 slot with default generators
   function createSF2Slot(name, pcm, sampleRate, channels, data) {
      const originalKey = detectPitchFromName(name || '');
      return {
         name: (name || 'sample').replace(/\.wav$/i, '').slice(0, 20),
         data: data instanceof ArrayBuffer ? data
            : (data && data.buffer ? data.buffer.slice(data.byteOffset || 0, (data.byteOffset || 0) + data.byteLength) : data),
         pcm,
         sampleRate: sampleRate || 44100,
         channels: channels || 1,
         bitDepth: 16,
         sourceName: name,
         originalKey,
         loopStart: 0,
         loopEnd: 0,
         loopMode: 0,
         generators: { ...SF2_DEFAULT_GENS, [SF2_GEN.overridingRootKey]: originalKey },
         keyRange: [0, 127],
         velRange: [0, 127],
         sampleType: 1,
         linkedSampleIndex: 0,
      };
   }

   // ---- Detail panel rendering ----
   function renderSF2Detail() {
      const container = document.getElementById('xep-sf2-detail-content');
      // [SF2] renderSF2Detail
      if (!container) { console.error('[SF2] xep-sf2-detail-content not found!'); return; }
      container.innerHTML = '';

      if (sf2SelectedSlot < 0 || !sf2Slots[sf2SelectedSlot]) {
         container.innerHTML = '<div class="xep-sf2-empty">Click a sample to edit its parameters</div>';
         return;
      }

      const slot = sf2Slots[sf2SelectedSlot];

      // Tab buttons
      const tabBtns = document.querySelectorAll('.xep-sf2-tab-btn');
      tabBtns.forEach(btn => {
         btn.classList.toggle('active', btn.dataset.tab === sf2ActiveTab);
         btn.onclick = () => {
            sf2ActiveTab = btn.dataset.tab;
            renderSF2Detail();
         };
      });

      if (sf2ActiveTab === 'generators') {
         renderSF2GeneratorsTab(container, slot);
      } else if (sf2ActiveTab === 'loop') {
         renderSF2LoopTab(container, slot);
      } else if (sf2ActiveTab === 'info') {
         renderSF2InfoTab(container, slot);
      }
   }

   // ---- Generators tab ----
   function renderSF2GeneratorsTab(container, slot) {
      const table = document.createElement('div');
      table.className = 'xep-sf2-gen-table';

      // Key/velocity range section
      const rangeSection = document.createElement('div');
      rangeSection.className = 'xep-sf2-gen-section';
      rangeSection.innerHTML = '<div class="xep-sf2-gen-section-title">Zone Ranges</div>';

      const keyLo = createGenInput('Key Range Low', slot.keyRange[0], 0, 127, 1, (v) => {
         slot.keyRange[0] = v;
      });
      const keyHi = createGenInput('Key Range High', slot.keyRange[1], 0, 127, 1, (v) => {
         slot.keyRange[1] = v;
      });
      const velLo = createGenInput('Vel Range Low', slot.velRange[0], 0, 127, 1, (v) => {
         slot.velRange[0] = v;
      });
      const velHi = createGenInput('Vel Range High', slot.velRange[1], 0, 127, 1, (v) => {
         slot.velRange[1] = v;
      });
      rangeSection.appendChild(keyLo.el);
      rangeSection.appendChild(keyHi.el);
      rangeSection.appendChild(velLo.el);
      rangeSection.appendChild(velHi.el);
      table.appendChild(rangeSection);

      // Generator rows
      const genSection = document.createElement('div');
      genSection.className = 'xep-sf2-gen-section';
      genSection.innerHTML = '<div class="xep-sf2-gen-section-title">Generators</div>';

      for (const meta of SF2_GEN_META) {
         const currentVal = slot.generators[meta.type] !== undefined
            ? slot.generators[meta.type] : SF2_DEFAULT_GENS[meta.type];
         const row = createGenInput(meta.label, currentVal, meta.min, meta.max, meta.step, (v) => {
            slot.generators[meta.type] = v;
            if (meta.type === SF2_GEN.overridingRootKey) {
               slot.originalKey = v;
               renderSF2Slots();
            }
            if (meta.type === SF2_GEN.sampleModes) {
               slot.loopMode = v;
               renderSF2Slots();
            }
         }, meta.unit);
         genSection.appendChild(row.el);
      }
      table.appendChild(genSection);

      container.appendChild(table);
   }

   function createGenInput(label, value, min, max, step, onChange, unit) {
      const row = document.createElement('div');
      row.className = 'xep-sf2-gen-row';
      const lbl = document.createElement('label');
      lbl.textContent = label;
      row.appendChild(lbl);
      const input = document.createElement('input');
      input.type = 'number';
      input.value = value;
      input.min = min;
      input.max = max;
      input.step = step;
      const handler = () => {
         let v = parseInt(input.value);
         if (isNaN(v)) v = value;
         v = Math.max(min, Math.min(max, v));
         input.value = v;
         onChange(v);
      };
      input.addEventListener('change', handler);
      input.addEventListener('input', handler);
      row.appendChild(input);
      if (unit) {
         const u = document.createElement('span');
         u.className = 'xep-sf2-gen-unit';
         u.textContent = unit;
         row.appendChild(u);
      }
      return { el: row, input };
   }

   // ---- Loop tab ----
   // Per-slot zoom state for the loop editor
   const _sf2LoopZoom = new WeakMap(); // slot -> { zoom, viewStart }
   function getLoopZoomState(slot) {
      let s = _sf2LoopZoom.get(slot);
      if (!s) { s = { zoom: 1, viewStart: 0 }; _sf2LoopZoom.set(slot, s); }
      return s;
   }

   function renderSF2LoopTab(container, slot) {
      const wrapper = document.createElement('div');
      wrapper.className = 'xep-sf2-loop-editor';

      // Loop mode selector
      const modeRow = document.createElement('div');
      modeRow.className = 'xep-sf2-gen-row';
      modeRow.innerHTML = '<label>Loop Mode</label>';
      const modeSel = document.createElement('select');
      modeSel.innerHTML = `
         <option value="0" ${slot.loopMode === 0 ? 'selected' : ''}>No loop</option>
         <option value="1" ${slot.loopMode === 1 ? 'selected' : ''}>Loop sustain</option>
         <option value="3" ${slot.loopMode === 3 ? 'selected' : ''}>Loop + release</option>
      `;
      modeSel.addEventListener('change', () => {
         slot.loopMode = parseInt(modeSel.value);
         slot.generators[SF2_GEN.sampleModes] = slot.loopMode;
         renderSF2Slots();
         container.innerHTML = '';
         renderSF2LoopTab(container, slot);
      });
      modeRow.appendChild(modeSel);
      wrapper.appendChild(modeRow);

      // Zoom controls row
      const zoomRow = document.createElement('div');
      zoomRow.className = 'xep-sf2-gen-row xep-sf2-loop-zoom-row';
      zoomRow.innerHTML = '<label>Zoom</label>';
      const zoomInfo = document.createElement('span');
      zoomInfo.className = 'xep-sf2-gen-unit';
      zoomInfo.style.flex = '1';
      zoomInfo.style.textAlign = 'left';
      const zState = getLoopZoomState(slot);
      const updateZoomInfo = () => { zoomInfo.textContent = `${zState.zoom.toFixed(1)}×`; };
      updateZoomInfo();

      const zInBtn = document.createElement('button');
      zInBtn.type = 'button';
      zInBtn.className = 'xep-sf2-zoom-btn';
      zInBtn.textContent = '+';
      zInBtn.addEventListener('click', () => {
         zState.zoom = Math.min(zState.zoom * 2, 256);
         clampViewStart(slot);
         drawSF2LoopWaveform(canvas, slot);
         updateScrollbar();
         updateZoomInfo();
      });

      const zOutBtn = document.createElement('button');
      zOutBtn.type = 'button';
      zOutBtn.className = 'xep-sf2-zoom-btn';
      zOutBtn.textContent = '−';
      zOutBtn.addEventListener('click', () => {
         zState.zoom = Math.max(zState.zoom / 2, 1);
         clampViewStart(slot);
         drawSF2LoopWaveform(canvas, slot);
         updateScrollbar();
         updateZoomInfo();
      });

      const zResetBtn = document.createElement('button');
      zResetBtn.type = 'button';
      zResetBtn.className = 'xep-sf2-zoom-btn';
      zResetBtn.textContent = '1:1';
      zResetBtn.addEventListener('click', () => {
         zState.zoom = 1;
         zState.viewStart = 0;
         drawSF2LoopWaveform(canvas, slot);
         updateScrollbar();
         updateZoomInfo();
      });

      zoomRow.appendChild(zInBtn);
      zoomRow.appendChild(zOutBtn);
      zoomRow.appendChild(zResetBtn);
      zoomRow.appendChild(zoomInfo);
      wrapper.appendChild(zoomRow);

      // Waveform canvas container (for scrollbar)
      const canvasContainer = document.createElement('div');
      canvasContainer.className = 'xep-sf2-loop-canvas-container';

      const canvas = document.createElement('canvas');
      canvas.className = 'xep-sf2-loop-canvas';
      canvas.width = 300;
      canvas.height = 120;
      canvasContainer.appendChild(canvas);

      // Scrollbar
      const scrollbar = document.createElement('div');
      scrollbar.className = 'xep-sf2-loop-scrollbar';
      const scrollHandle = document.createElement('div');
      scrollHandle.className = 'xep-sf2-loop-scroll-handle';
      scrollbar.appendChild(scrollHandle);
      canvasContainer.appendChild(scrollbar);

      wrapper.appendChild(canvasContainer);

      // Helper: clamp viewStart so we don't scroll past the end
      function clampViewStart(slot) {
         const total = slot.pcm.length;
         const visible = Math.floor(total / zState.zoom);
         const maxStart = Math.max(0, total - visible);
         zState.viewStart = Math.max(0, Math.min(maxStart, zState.viewStart));
      }

      // Helper: update scrollbar handle position/size
      function updateScrollbar() {
         const total = slot.pcm.length;
         if (!total) return;
         const visible = Math.floor(total / zState.zoom);
         if (zState.zoom <= 1) {
            scrollHandle.style.left = '0px';
            scrollHandle.style.width = scrollbar.clientWidth + 'px';
            scrollbar.style.display = 'none';
            return;
         }
         scrollbar.style.display = 'block';
         const pct = zState.viewStart / total;
         const visPct = visible / total;
         scrollHandle.style.left = (pct * scrollbar.clientWidth) + 'px';
         scrollHandle.style.width = Math.max(20, visPct * scrollbar.clientWidth) + 'px';
      }

      // Scrollbar drag
      let scrollDragging = false;
      let scrollDragStartX = 0;
      let scrollDragStartView = 0;
      scrollHandle.addEventListener('mousedown', (e) => {
         e.preventDefault();
         e.stopPropagation();
         scrollDragging = true;
         scrollDragStartX = e.clientX;
         scrollDragStartView = zState.viewStart;
      });
      document.addEventListener('mousemove', (e) => {
         if (!scrollDragging) return;
         const dx = e.clientX - scrollDragStartX;
         const total = slot.pcm.length;
         const visible = Math.floor(total / zState.zoom);
         const maxStart = Math.max(0, total - visible);
         const pxToSamples = total / scrollbar.clientWidth;
         zState.viewStart = Math.max(0, Math.min(maxStart, scrollDragStartView + dx * pxToSamples));
         drawSF2LoopWaveform(canvas, slot);
         updateScrollbar();
      });
      document.addEventListener('mouseup', () => { scrollDragging = false; });

      // Mouse wheel zoom on canvas
      canvas.addEventListener('wheel', (e) => {
         e.preventDefault();
         const canvasX = clientXToCanvasX(e.clientX);
         const w = canvas.width;
         if (w <= 0) return;
         const mousePct = canvasX / w;
         const total = slot.pcm.length;
         const oldVisible = Math.floor(total / zState.zoom);
         const oldCenter = zState.viewStart + mousePct * oldVisible;
         const delta = e.deltaY < 0 ? 1.3 : 1/1.3;
         zState.zoom = Math.max(1, Math.min(256, zState.zoom * delta));
         const newVisible = Math.floor(total / zState.zoom);
         // Keep mouse position stable
         zState.viewStart = Math.max(0, oldCenter - mousePct * newVisible);
         clampViewStart(slot);
         drawSF2LoopWaveform(canvas, slot);
         updateScrollbar();
         updateZoomInfo();
      }, { passive: false });

      // Draw waveform with loop markers (and resize after layout)
      // Use ResizeObserver for reliable canvas sizing that handles CSS vs canvas resolution
      const resizeCanvas = () => {
         const r = canvas.getBoundingClientRect();
         if (r.width > 0) {
            const newW = Math.floor(r.width);
            if (canvas.width !== newW) {
               canvas.width = newW;
            }
            canvas.height = 120;
         }
         drawSF2LoopWaveform(canvas, slot);
         updateScrollbar();
      };
      requestAnimationFrame(resizeCanvas);
      // Also resize on container resize (handles panel splitter changes)
      if (typeof ResizeObserver !== 'undefined') {
         const ro = new ResizeObserver(() => resizeCanvas());
         ro.observe(canvas);
      }

      // Helper: convert client X to canvas-internal X (handles CSS vs canvas resolution mismatch)
      function clientXToCanvasX(clientX) {
         const rect = canvas.getBoundingClientRect();
         // Map CSS pixel position to canvas internal pixel position
         const cssX = clientX - rect.left;
         const canvasX = (cssX / rect.width) * canvas.width;
         return Math.max(0, Math.min(canvas.width, canvasX));
      }

      // Helper: convert canvas X pixel to sample index (accounting for zoom/scroll)
      function pixelToSample(clientX) {
         const canvasX = clientXToCanvasX(clientX);
         const w = canvas.width;
         if (w <= 0) return 0;
         const x = canvasX / w;
         const total = slot.pcm.length;
         const visible = Math.floor(total / zState.zoom);
         return Math.floor(zState.viewStart + x * visible);
      }

      // Drag-to-select loop region (like wavedit)
      // - Click + drag: select loop region (start→end)
      // - Click without drag: set loop start to that point, end = sample end
      // - Shift+click: move loop end only
      // - When zoomed: drag on empty area pans the view
      let loopDragging = false;
      let loopDragStartSample = 0;
      let loopDragMoved = false;
      let panDragging = false;
      let panStartClientX = 0;
      let panStartView = 0;

      canvas.addEventListener('mousedown', (e) => {
         e.preventDefault();
         if (e.button !== 0) return; // left button only
         const sampleIdx = pixelToSample(e.clientX);
         const total = slot.pcm.length;

         // If zoomed and no modifier, pan
         if (zState.zoom > 1 && !e.shiftKey) {
            panDragging = true;
            panStartClientX = e.clientX;
            panStartView = zState.viewStart;
            canvas.style.cursor = 'grabbing';
            return;
         }

         // Start drag-select
         loopDragging = true;
         loopDragMoved = false;
         loopDragStartSample = Math.max(0, Math.min(total - 1, sampleIdx));

         if (e.shiftKey) {
            // Shift+drag: adjust only the loop end
            slot.loopEnd = loopDragStartSample;
            if (slot.loopStart >= slot.loopEnd) slot.loopStart = 0;
         } else {
            // Normal drag: set both start and end to same point initially
            slot.loopStart = loopDragStartSample;
            slot.loopEnd = loopDragStartSample;
         }
         slot.loopMode = slot.loopMode || 1;
         slot.generators[SF2_GEN.sampleModes] = slot.loopMode;
         if (lsInput.input) lsInput.input.value = slot.loopStart;
         if (leInput.input) leInput.input.value = slot.loopEnd;
         drawSF2LoopWaveform(canvas, slot);
         renderSF2Slots();
      });

      canvas.addEventListener('mousemove', (e) => {
         // Handle pan
         if (panDragging) {
            const total = slot.pcm.length;
            const visible = Math.floor(total / zState.zoom);
            const maxStart = Math.max(0, total - visible);
            const rect = canvas.getBoundingClientRect();
            const dx = panStartClientX - e.clientX;
            const dxSamples = rect.width > 0 ? (dx / rect.width) * visible : 0;
            zState.viewStart = Math.max(0, Math.min(maxStart, panStartView + dxSamples));
            drawSF2LoopWaveform(canvas, slot);
            updateScrollbar();
            return;
         }

         if (!loopDragging) return;
         const sampleIdx = pixelToSample(e.clientX);
         const total = slot.pcm.length;
         const clamped = Math.max(0, Math.min(total - 1, sampleIdx));
         loopDragMoved = true;

         if (e.shiftKey) {
            // Shift+drag: adjust loop end
            slot.loopEnd = clamped;
            if (slot.loopStart >= slot.loopEnd) slot.loopStart = Math.max(0, slot.loopEnd - 1);
         } else {
            // Normal drag: start = min, end = max of drag range
            slot.loopStart = Math.min(loopDragStartSample, clamped);
            slot.loopEnd = Math.max(loopDragStartSample, clamped);
         }
         slot.loopMode = slot.loopMode || 1;
         slot.generators[SF2_GEN.sampleModes] = slot.loopMode;
         if (lsInput.input) lsInput.input.value = slot.loopStart;
         if (leInput.input) leInput.input.value = slot.loopEnd;
         drawSF2LoopWaveform(canvas, slot);
         renderSF2Slots();
      });

      canvas.addEventListener('mouseup', (e) => {
         if (panDragging) {
            panDragging = false;
            canvas.style.cursor = 'crosshair';
            return;
         }
         if (!loopDragging) return;
         loopDragging = false;

         // If no drag movement (just a click), set loop start to end of sample
         if (!loopDragMoved) {
            slot.loopStart = loopDragStartSample;
            slot.loopEnd = slot.pcm.length - 1;
            slot.loopMode = slot.loopMode || 1;
            slot.generators[SF2_GEN.sampleModes] = slot.loopMode;
            if (lsInput.input) lsInput.input.value = slot.loopStart;
            if (leInput.input) leInput.input.value = slot.loopEnd;
            drawSF2LoopWaveform(canvas, slot);
            renderSF2Slots();
         }
      });

      canvas.addEventListener('mouseleave', () => {
         if (panDragging) { panDragging = false; canvas.style.cursor = 'crosshair'; }
         if (loopDragging) { loopDragging = false; }
      });

      // Prevent context menu (no longer needed for loop end)
      canvas.addEventListener('contextmenu', (e) => e.preventDefault());

      // Loop point numeric inputs
      const pointsRow = document.createElement('div');
      pointsRow.className = 'xep-sf2-gen-section';
      const lsInput = createGenInput('Loop Start', slot.loopStart, 0, slot.pcm.length - 1, 1, (v) => {
         slot.loopStart = v;
         if (slot.loopEnd <= slot.loopStart) slot.loopEnd = slot.pcm.length - 1;
         slot.generators[SF2_GEN.sampleModes] = slot.loopMode;
         drawSF2LoopWaveform(canvas, slot);
         renderSF2Slots();
      }, 'samples');
      const leInput = createGenInput('Loop End', slot.loopEnd, 0, slot.pcm.length - 1, 1, (v) => {
         slot.loopEnd = v;
         if (slot.loopStart >= slot.loopEnd) slot.loopStart = 0;
         slot.generators[SF2_GEN.sampleModes] = slot.loopMode;
         drawSF2LoopWaveform(canvas, slot);
         renderSF2Slots();
      }, 'samples');
      pointsRow.appendChild(lsInput.el);
      pointsRow.appendChild(leInput.el);
      wrapper.appendChild(pointsRow);

      // Instructions
      const help = document.createElement('div');
      help.className = 'xep-sf2-loop-help';
      help.textContent = 'Drag on waveform = select loop region. Click = set loop start. Shift+drag = adjust loop end. Wheel = zoom. Drag when zoomed = pan.';
      wrapper.appendChild(help);

      container.appendChild(wrapper);
   }

   // Extract PCM Int16Array from raw WAV data, handling all bit depths (8/16/24/32/float)
   // Same logic as drawWaveformFromWavBucket but returns PCM instead of drawing
   function decodeWavToInt16(wavBuffer) {
      if (!wavBuffer || wavBuffer.byteLength < 44) return null;
      try {
         const ab = wavBuffer instanceof ArrayBuffer ? wavBuffer
            : (wavBuffer.buffer ? wavBuffer.buffer.slice(wavBuffer.byteOffset || 0, (wavBuffer.byteOffset || 0) + wavBuffer.byteLength) : wavBuffer);
         const dv = new DataView(ab);
         const numChannels = dv.getUint16(22, true);
         const bitsPerSample = dv.getUint16(34, true);
         const formatTag = dv.getUint16(20, true);
         let dataOffset = 12;
         while (dataOffset + 8 < ab.byteLength) {
            const tag = String.fromCharCode(
               dv.getUint8(dataOffset), dv.getUint8(dataOffset + 1),
               dv.getUint8(dataOffset + 2), dv.getUint8(dataOffset + 3)
            );
            const chunkSize = dv.getUint32(dataOffset + 4, true);
            if (tag === 'data') { dataOffset += 8; break; }
            dataOffset += 8 + chunkSize;
         }
         const bytesPerSample = bitsPerSample / 8;
         const frameCount = Math.floor((ab.byteLength - dataOffset) / (bytesPerSample * numChannels));
         if (!frameCount) return null;
         const totalSamples = frameCount * numChannels;
         let pcm;
         if (bitsPerSample === 16) {
            pcm = new Int16Array(ab, dataOffset, totalSamples);
         } else if (bitsPerSample === 8) {
            pcm = new Int16Array(totalSamples);
            for (let i = 0; i < totalSamples; i++) {
               pcm[i] = (dv.getUint8(dataOffset + i) - 128) << 8;
            }
         } else if (bitsPerSample === 24) {
            pcm = new Int16Array(totalSamples);
            for (let i = 0; i < totalSamples; i++) {
               const byteIdx = dataOffset + i * 3;
               const b0 = dv.getUint8(byteIdx), b1 = dv.getUint8(byteIdx + 1), b2 = dv.getUint8(byteIdx + 2);
               let s = (b2 << 16) | (b1 << 8) | b0;
               if (s & 0x800000) s -= 0x1000000;
               pcm[i] = s >> 8;
            }
         } else if (bitsPerSample === 32 && formatTag === 3) {
            pcm = new Int16Array(totalSamples);
            for (let i = 0; i < totalSamples; i++) {
               const f = dv.getFloat32(dataOffset + i * 4, true);
               pcm[i] = Math.max(-32768, Math.min(32767, Math.round(f * 32767)));
            }
         } else if (bitsPerSample === 32) {
            pcm = new Int16Array(totalSamples);
            for (let i = 0; i < totalSamples; i++) {
               pcm[i] = dv.getInt32(dataOffset + i * 4, true) >> 16;
            }
         } else if (bitsPerSample === 64 && formatTag === 3) {
            pcm = new Int16Array(totalSamples);
            for (let i = 0; i < totalSamples; i++) {
               const f = dv.getFloat64(dataOffset + i * 8, true);
               pcm[i] = Math.max(-32768, Math.min(32767, Math.round(f * 32767)));
            }
         } else {
            return null;
         }
         // If stereo, downmix to mono for waveform display
         if (numChannels > 1) {
            const mono = new Int16Array(frameCount);
            for (let i = 0; i < frameCount; i++) {
               let sum = 0;
               for (let ch = 0; ch < numChannels; ch++) {
                  sum += pcm[i * numChannels + ch];
               }
               mono[i] = Math.max(-32768, Math.min(32767, Math.round(sum / numChannels)));
            }
            return mono;
         }
         return pcm;
      } catch (_) {
         return null;
      }
   }

   function drawSF2LoopWaveform(canvas, slot) {
      const ctx = canvas.getContext('2d');
      const w = canvas.width, h = canvas.height;
      // Decode PCM from raw WAV data (handles all bit depths: 8/16/24/32/float)
      // Same as bucket/adapters — don't use slot.pcm which is down-converted
      // Cache on slot to avoid re-parsing on every zoom/scroll redraw
      let pcm = slot._decodedPcm16;
      if (!pcm && slot.data) {
         pcm = decodeWavToInt16(slot.data);
         slot._decodedPcm16 = pcm;
      }
      if (!pcm) pcm = slot.pcm; // fallback
      if (!pcm || !pcm.length) return;

      // Theme colors from CSS variables
      const cs = getComputedStyle(document.documentElement);
      const c0 = cs.getPropertyValue('--c0').trim() || '#0a0a0a';
      const c6 = cs.getPropertyValue('--c6').trim() || '#444';
      const c8 = cs.getPropertyValue('--c8').trim() || '#666';
      const c9 = cs.getPropertyValue('--c9').trim() || '#777';
      const ce = cs.getPropertyValue('--ce').trim() || '#ccc';

      // Zoom state
      const zs = getLoopZoomState(slot);
      const total = pcm.length;
      const visible = Math.floor(total / zs.zoom);
      const viewStart = zs.viewStart;
      const viewEnd = Math.min(total, viewStart + visible);

      // Clear
      ctx.fillStyle = c0;
      ctx.fillRect(0, 0, w, h);

      // Draw center line
      const mid = h / 2;
      ctx.strokeStyle = c6;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, mid + 0.5);
      ctx.lineTo(w, mid + 0.5);
      ctx.stroke();

      // Draw waveform (only visible portion)
      const samplesPerPixel = (viewEnd - viewStart) / w;
      ctx.strokeStyle = c9;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x < w; x++) {
         const startSample = Math.floor(viewStart + x * samplesPerPixel);
         const endSample = Math.floor(viewStart + (x + 1) * samplesPerPixel);
         let max = 0, min = 0;
         for (let j = startSample; j < endSample && j < viewEnd; j++) {
            if (pcm[j] > max) max = pcm[j];
            if (pcm[j] < min) min = pcm[j];
         }
         const maxY = mid - (max / 32768) * mid * 0.9;
         const minY = mid - (min / 32768) * mid * 0.9;
         ctx.moveTo(x + 0.5, maxY);
         ctx.lineTo(x + 0.5, minY);
      }
      ctx.stroke();

      // Draw loop region (mapped to visible viewport)
      if (slot.loopMode > 0 && slot.loopEnd > slot.loopStart) {
         const lsSample = slot.loopStart;
         const leSample = slot.loopEnd;
         // Convert sample positions to pixel positions in the zoomed view
         const sampleToPx = (s) => ((s - viewStart) / (viewEnd - viewStart)) * w;
         const lsX = sampleToPx(lsSample);
         const leX = sampleToPx(leSample);

         // Only draw if within visible range
         if (leX > 0 && lsX < w) {
            const clipLsX = Math.max(0, lsX);
            const clipLeX = Math.min(w, leX);
            // Loop region fill
            ctx.save();
            ctx.globalAlpha = 0.12;
            ctx.fillStyle = ce;
            ctx.fillRect(clipLsX, 0, clipLeX - clipLsX, h);
            ctx.restore();
         }
         // Loop start line (bright) — only if visible
         if (lsX >= 0 && lsX < w) {
            ctx.strokeStyle = ce;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(lsX, 0);
            ctx.lineTo(lsX, h);
            ctx.stroke();
            ctx.fillStyle = ce;
            ctx.font = '10px "bit", monospace';
            ctx.fillText('L', lsX + 3, 12);
         }
         // Loop end line (darker) — only if visible
         if (leX >= 0 && leX < w) {
            ctx.strokeStyle = c8;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(leX, 0);
            ctx.lineTo(leX, h);
            ctx.stroke();
            ctx.fillStyle = c8;
            ctx.font = '10px "bit", monospace';
            ctx.fillText('R', leX + 3, 12);
         }
      }

      // Draw zoom indicator if zoomed in
      if (zs.zoom > 1) {
         ctx.fillStyle = c8;
         ctx.font = '9px "bit", monospace';
         ctx.textAlign = 'right';
         ctx.fillText(`${zs.zoom.toFixed(1)}×`, w - 4, h - 4);
         ctx.textAlign = 'left';
      }
   }

   // ---- Sample Info tab ----
   function renderSF2InfoTab(container, slot) {
      const info = document.createElement('div');
      info.className = 'xep-sf2-info-tab';

      const rows = [
         ['Name', slot.name || ''],
         ['Source', slot.sourceName || ''],
         ['Sample Rate', (slot.sampleRate || 44100) + ' Hz'],
         ['Channels', slot.channels || 1],
         ['Bit Depth', slot.bitDepth || 16],
         ['Sample Length', (slot.pcm ? slot.pcm.length : 0) + ' samples'],
         ['Duration', slot.pcm ? ((slot.pcm.length / (slot.sampleRate || 44100)).toFixed(3) + ' s') : ''],
         ['Root Key', `${slot.originalKey || 60} (${midiToNoteName(slot.originalKey || 60)})`],
         ['Loop Mode', ['No loop', 'Loop sustain', 'One-shot', 'Loop + release'][slot.loopMode || 0]],
         ['Loop Start', (slot.loopStart || 0) + ' samples'],
         ['Loop End', (slot.loopEnd || 0) + ' samples'],
         ['Key Range', `${slot.keyRange[0]} - ${slot.keyRange[1]} (${midiToNoteName(slot.keyRange[0])} - ${midiToNoteName(slot.keyRange[1])})`],
         ['Vel Range', `${slot.velRange[0]} - ${slot.velRange[1]}`],
         ['Sample Type', ['', 'Mono', 'Right', '', 'Left', '', '', '', 'Link'][slot.sampleType || 1] || `Type ${slot.sampleType}`],
      ];

      for (const [label, value] of rows) {
         const row = document.createElement('div');
         row.className = 'xep-sf2-info-row';
         row.innerHTML = `<label>${label}</label><span>${value}</span>`;
         info.appendChild(row);
      }

      // Stereo link controls
      const linkSection = document.createElement('div');
      linkSection.className = 'xep-sf2-gen-section';
      linkSection.innerHTML = '<div class="xep-sf2-gen-section-title">Stereo Link</div>';
      const linkRow = document.createElement('div');
      linkRow.className = 'xep-sf2-gen-row';
      linkRow.innerHTML = '<label>Link to sample</label>';
      const linkSel = document.createElement('select');
      linkSel.innerHTML = '<option value="0">None</option>';
      for (let i = 0; i < SF2_MAX_SAMPLES; i++) {
         if (sf2Slots[i] && i !== sf2SelectedSlot) {
            const opt = document.createElement('option');
            opt.value = i;
            opt.textContent = `${i}: ${sf2Slots[i].name}`;
            if (slot.linkedSampleIndex === i) opt.selected = true;
            linkSel.appendChild(opt);
         }
      }
      linkSel.addEventListener('change', () => {
         slot.linkedSampleIndex = parseInt(linkSel.value);
      });
      linkRow.appendChild(linkSel);
      linkSection.appendChild(linkRow);

      const typeRow = document.createElement('div');
      typeRow.className = 'xep-sf2-gen-row';
      typeRow.innerHTML = '<label>Sample Type</label>';
      const typeSel = document.createElement('select');
      typeSel.innerHTML = `
         <option value="1" ${slot.sampleType === 1 ? 'selected' : ''}>Mono</option>
         <option value="2" ${slot.sampleType === 2 ? 'selected' : ''}>Right channel</option>
         <option value="4" ${slot.sampleType === 4 ? 'selected' : ''}>Left channel</option>
         <option value="8" ${slot.sampleType === 8 ? 'selected' : ''}>Linked sample</option>
      `;
      typeSel.addEventListener('change', () => {
         slot.sampleType = parseInt(typeSel.value);
      });
      typeRow.appendChild(typeSel);
      linkSection.appendChild(typeRow);
      info.appendChild(linkSection);

      container.appendChild(info);
   }

   // ---- MIDI Keyboard ----
   function renderSF2Keyboard() {
      const container = document.getElementById('xep-sf2-keyboard');
      // [SF2] renderSF2Keyboard
      if (!container) { console.error('[SF2] xep-sf2-keyboard not found!'); return; }
      container.innerHTML = '';

      // Octave controls
      const octCtrl = document.createElement('div');
      octCtrl.className = 'xep-sf2-kbd-octave';
      octCtrl.innerHTML = `
         <button type="button" id="xep-sf2-kbd-prev">◀</button>
         <span>Octave ${sf2KeyboardOctave}</span>
         <button type="button" id="xep-sf2-kbd-next">▶</button>
      `;
      container.appendChild(octCtrl);

      document.getElementById('xep-sf2-kbd-prev').onclick = () => {
         if (sf2KeyboardOctave > 0) { sf2KeyboardOctave--; renderSF2Keyboard(); }
      };
      document.getElementById('xep-sf2-kbd-next').onclick = () => {
         if (sf2KeyboardOctave < 9) { sf2KeyboardOctave++; renderSF2Keyboard(); }
      };

      // Piano keys
      const kbd = document.createElement('div');
      kbd.className = 'xep-sf2-kbd-keys';
      const whiteNotes = [0, 2, 4, 5, 7, 9, 11]; // C D E F G A B
      // Black key positions: which white-key index they sit after, and offset fraction
      // C# after C(0), D# after D(1), F# after F(3), G# after G(4), A# after A(5)
      const blackKeyOffsets = { 1: 0.65, 3: 1.65, 6: 3.65, 8: 4.65, 10: 5.65 };
      const totalWhiteKeys = 14; // 2 octaves × 7 white keys

      // Render 2 octaves of white keys
      let whiteIdx = 0;
      for (let oct = 0; oct < 2; oct++) {
         const baseNote = (sf2KeyboardOctave + 1 + oct) * 12;
         for (const wn of whiteNotes) {
            const note = baseNote + wn;
            if (note > 127) continue;
            const key = document.createElement('div');
            key.className = 'xep-sf2-key-white';
            key.textContent = midiToNoteName(note);
            key.addEventListener('mousedown', () => playSF2Preview(sf2SelectedSlot, note));
            kbd.appendChild(key);
            whiteIdx++;
         }
      }

      // Render black keys (positioned absolutely using percentage)
      for (let oct = 0; oct < 2; oct++) {
         const baseNote = (sf2KeyboardOctave + 1 + oct) * 12;
         for (const bn of [1, 3, 6, 8, 10]) {
            const note = baseNote + bn;
            if (note > 127) continue;
            // Which white key index is this black key after?
            // C#=1 after C(whiteIdx 0), D#=3 after D(1), F#=6 after F(3), G#=8 after G(4), A#=10 after A(5)
            const whiteBefore = [0, 1, 3, 4, 5][[1, 3, 6, 8, 10].indexOf(bn)];
            const globalWhiteIdx = oct * 7 + whiteBefore;
            const leftPct = ((globalWhiteIdx + 0.7) / totalWhiteKeys) * 100;
            const key = document.createElement('div');
            key.className = 'xep-sf2-key-black';
            key.style.left = leftPct + '%';
            key.textContent = midiToNoteName(note);
            key.addEventListener('mousedown', () => playSF2Preview(sf2SelectedSlot, note));
            kbd.appendChild(key);
         }
      }

      container.appendChild(kbd);
   }

   // Play a sample at a given MIDI note (pitch-shifted from root key)
   function playSF2Preview(slotIndex, midiNote) {
      if (slotIndex < 0 || !sf2Slots[slotIndex]) return;
      const slot = sf2Slots[slotIndex];
      if (!slot.pcm) return;

      const ctx = getGlobalAudioContext();
      const rootKey = slot.originalKey || 60;
      const semitones = midiNote - rootKey;
      const playbackRate = Math.pow(2, semitones / 12);

      // Create audio buffer from PCM
      const pcm = slot.pcm;
      const audioBuf = ctx.createBuffer(1, pcm.length, slot.sampleRate || 44100);
      const channelData = audioBuf.getChannelData(0);

      // Peak-normalize to prevent loudness differences between bit depths
      // Find peak amplitude, then scale to a target of ~0.7 (-3dBFS)
      let peak = 0;
      for (let i = 0; i < pcm.length; i++) {
         const abs = Math.abs(pcm[i]);
         if (abs > peak) peak = abs;
      }
      const TARGET_PEAK = 0.7;
      const normGain = peak > 0 ? Math.min(1.0, (TARGET_PEAK * 32768) / peak) : 1.0;
      for (let i = 0; i < pcm.length; i++) {
         channelData[i] = (pcm[i] / 32768) * normGain;
      }

      const src = ctx.createBufferSource();
      src.buffer = audioBuf;
      src.playbackRate.value = playbackRate;

      // Apply gain from attenuation
      const atten = slot.generators[SF2_GEN.initialAttenuation] || 0;
      const gainNode = ctx.createGain();
      gainNode.gain.value = Math.pow(10, -atten / 200);

      // Apply pan
      const pan = slot.generators[SF2_GEN.pan] || 0;
      const panNode = ctx.createStereoPanner();
      panNode.pan.value = pan / 500;

      src.connect(gainNode);
      gainNode.connect(panNode);
      panNode.connect(ctx.destination);

      // Loop if loop mode is set
      if (slot.loopMode > 0 && slot.loopEnd > slot.loopStart) {
         src.loop = true;
         src.loopStart = slot.loopStart / (slot.sampleRate || 44100);
         src.loopEnd = slot.loopEnd / (slot.sampleRate || 44100);
      }

      src.start(0);

      // Stop after 5 seconds if looping
      if (src.loop) {
         setTimeout(() => { try { src.stop(); } catch (_) {} }, 5000);
      }
   }

   // Convert MIDI note number to note name (e.g. 60 → "C4")
   function midiToNoteName(midi) {
      const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      const octave = Math.floor(midi / 12) - 1;
      return names[midi % 12] + octave;
   }

   // Try to detect root pitch from sample name (e.g. "kick_C4.wav" → 60)
   function detectPitchFromName(name) {
      const m = name.match(/([A-G])(#|b)?(-?\d)/i);
      if (!m) return 60;
      const noteNames = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };
      let semis = noteNames[m[1].toLowerCase()] || 0;
      if (m[2] === '#') semis += 1;
      if (m[2] === 'b') semis -= 1;
      const octave = parseInt(m[3]);
      return semis + (octave + 1) * 12;
   }

   // ---- Export logic ----
   async function runSF2Export(target) {
      // [SF2] runSF2Export
      const nameEl = document.getElementById('exp-sf2-name');
      const engEl  = document.getElementById('exp-sf2-engineer');
      const cmtEl  = document.getElementById('exp-sf2-comment');
      const fmtEl  = document.getElementById('exp-sf2-fmt');
      const bankName = (nameEl ? nameEl.value : 'My SoundFont').trim() || 'My SoundFont';
      const fmt = fmtEl ? fmtEl.value : 'sf2';

      const filled = sf2Slots.filter(s => s);
      if (!filled.length) {
         setStatus('no samples in SF2 grid — drag audio from the bucket', true);
         console.error('[SF2] no samples in grid, aborting export');
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, `Processing ${filled.length} samples`);

      // Build samples array — each slot becomes one or two SF2 samples
      const samples = [];
      const slotToSampleIdx = []; // maps slot index → first sample index in samples[]
      for (let i = 0; i < filled.length; i++) {
         const s = filled[i];
         slotToSampleIdx[i] = samples.length;
         updateProgress('Decoding samples…', 10 + Math.round((i / filled.length) * 40), `sample ${i+1}/${filled.length}`);

         let pcm = s.pcm, channels = s.channels, sampleRate = s.sampleRate;
         if (!pcm) {
            try {
               const decoded = decodeWavToPcm(s.data);
               pcm = decoded.pcm; channels = decoded.channels; sampleRate = decoded.sampleRate;
            } catch (e) { continue; }
         }

         if (channels === 2) {
            // Split stereo into L/R pair
            const numFrames = pcm.length / 2;
            const leftPcm = new Int16Array(numFrames);
            const rightPcm = new Int16Array(numFrames);
            for (let j = 0; j < numFrames; j++) {
               leftPcm[j] = pcm[j * 2];
               rightPcm[j] = pcm[j * 2 + 1];
            }
            const li = samples.length;
            samples.push({
               name: (s.name || `sample_${i}`).slice(0, 18) + ' L',
               pcm: leftPcm,
               sampleRate: sampleRate || 44100,
               loopStart: s.loopStart || 0, loopEnd: s.loopEnd || 0,
               originalKey: s.originalKey || 60,
               pitchCorrection: s.generators[SF2_GEN.fineTune] || 0,
               sampleType: 4, // left
               linkedSampleIndex: li + 1,
            });
            samples.push({
               name: (s.name || `sample_${i}`).slice(0, 18) + ' R',
               pcm: rightPcm,
               sampleRate: sampleRate || 44100,
               loopStart: s.loopStart || 0, loopEnd: s.loopEnd || 0,
               originalKey: s.originalKey || 60,
               pitchCorrection: s.generators[SF2_GEN.fineTune] || 0,
               sampleType: 2, // right
               linkedSampleIndex: li,
            });
         } else {
            samples.push({
               name: (s.name || `sample_${i}`).slice(0, 20),
               pcm,
               sampleRate: sampleRate || 44100,
               loopStart: s.loopStart || 0, loopEnd: s.loopEnd || 0,
               originalKey: s.originalKey || 60,
               pitchCorrection: s.generators[SF2_GEN.fineTune] || 0,
               sampleType: s.sampleType || 1,
               linkedSampleIndex: s.linkedSampleIndex || 0,
            });
         }
      }

      if (!samples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building bank…', 55, `Writing ${fmt.toUpperCase()} structure`);

      // Build instrument zones — one per slot, using per-sample generators
      const instrZones = [];
      for (let i = 0; i < filled.length; i++) {
         const s = filled[i];
         const sampleIdx = slotToSampleIdx[i];
         const gens = { ...s.generators };
         // Remove generators that go in shdr, not igen
         delete gens[SF2_GEN.overridingRootKey]; // handled by originalKey in shdr

         instrZones.push({
            sampleIndex: sampleIdx,
            keyRange: s.keyRange || [0, 127],
            velRange: s.velRange || [0, 127],
            generators: gens,
         });

         // If stereo, add R zone too
         if (s.channels === 2) {
            instrZones.push({
               sampleIndex: sampleIdx + 1,
               keyRange: s.keyRange || [0, 127],
               velRange: s.velRange || [0, 127],
               generators: { ...gens },
            });
         }
      }

      const bank = {
         info: {
            name: bankName,
            engineer: engEl ? engEl.value : '',
            comment: cmtEl ? cmtEl.value : '',
            software: 'AZOPSOFT SF2 Export',
         },
         samples,
         instruments: [{
            name: bankName.slice(0, 20),
            zones: instrZones,
         }],
         presets: [{
            name: bankName.slice(0, 20),
            bank: 0,
            program: 0,
            zones: [{
               instrumentIndex: 0,
               keyRange: [0, 127],
               velRange: [0, 127],
               generators: {},
            }],
         }],
      };

      let fileData;
      try {
         if (fmt === 'sfz') {
            // SFZ is a text file + sample WAVs, packaged as ZIP
            updateProgress('Building SFZ…', 55, 'Generating SFZ text and sample WAVs');

            // Build SFZ text content
            let sfzText = `// SFZ file generated by AZOPSOFT\n`;
            sfzText += `// Bank: ${bankName}\n`;
            if (engEl && engEl.value) sfzText += `// Engineer: ${engEl.value}\n`;
            if (cmtEl && cmtEl.value) sfzText += `// Comment: ${cmtEl.value}\n`;
            sfzText += `\n`;

            for (let i = 0; i < filled.length; i++) {
               const s = filled[i];
               const sampleIdx = slotToSampleIdx[i];
               const sample = samples[sampleIdx];
               const sampleName = (s.name || `sample_${i}`).replace(/[^a-zA-Z0-9_-]/g, '_');
               const wavName = `${String(i).padStart(3, '0')}_${sampleName}.wav`;
               const rootKey = s.originalKey || 60;
               const keyLo = s.keyRange ? s.keyRange[0] : 0;
               const keyHi = s.keyRange ? s.keyRange[1] : 127;
               const velLo = s.velRange ? s.velRange[0] : 0;
               const velHi = s.velRange ? s.velRange[1] : 127;
               const fineTune = s.generators[SF2_GEN.fineTune] || 0;
               const atten = s.generators[SF2_GEN.initialAttenuation] || 0;
               const pan = s.generators[SF2_GEN.pan] || 0;
               const loopMode = s.loopMode || 0;

               // ADSR
               const volEnvAttack = (s.generators[SF2_GEN.attackVolEnv] || -12000) / 12000 * 100;
               const volEnvDecay = (s.generators[SF2_GEN.decayVolEnv] || -12000) / 12000 * 100;
               const volEnvSustain = s.generators[SF2_GEN.sustainVolEnv] !== undefined ? (100 - s.generators[SF2_GEN.sustainVolEnv] / 10) : 100;
               const volEnvRelease = (s.generators[SF2_GEN.releaseVolEnv] || -12000) / 12000 * 100;

               sfzText += `<region>\n`;
               sfzText += `sample=samples/${wavName}\n`;
               sfzText += `lokey=${keyLo} hikey=${keyHi} pitch_keycenter=${rootKey}\n`;
               sfzText += `lovel=${velLo} hivel=${velHi}\n`;
               if (fineTune) sfzText += `tune=${(fineTune / 100).toFixed(2)}\n`;
               if (atten) sfzText += `volume=${(-atten / 10).toFixed(1)}\n`;
               if (pan) sfzText += `pan=${(pan / 500 * 100).toFixed(1)}\n`;
               if (volEnvAttack > 0) sfzText += `ampeg_attack=${volEnvAttack.toFixed(3)}\n`;
               if (volEnvDecay > 0) sfzText += `ampeg_decay=${volEnvDecay.toFixed(3)}\n`;
               if (volEnvSustain < 100) sfzText += `ampeg_sustain=${volEnvSustain.toFixed(1)}\n`;
               if (volEnvRelease > 0) sfzText += `ampeg_release=${volEnvRelease.toFixed(3)}\n`;
               if (loopMode > 0 && s.loopEnd > s.loopStart) {
                  sfzText += `loop_mode=${loopMode === 1 ? 'loop_sustain' : 'loop_sustain'}\n`;
                  sfzText += `loop_start=${s.loopStart} loop_end=${s.loopEnd}\n`;
               }
               sfzText += `\n`;
            }

            // Build ZIP with SFZ file + sample WAVs
            const sfzFilename = bankName.replace(/[\\/:*?"<>|]/g, '').trim() + '.sfz';
            const zipEntries = [];

            // Add the .sfz text file
            zipEntries.push({
               name: sfzFilename,
               lastModified: new Date(),
               input: new Blob([sfzText], { type: 'text/plain' }),
            });

            // Add sample WAVs
            for (let i = 0; i < filled.length; i++) {
               const s = filled[i];
               const sampleName = (s.name || `sample_${i}`).replace(/[^a-zA-Z0-9_-]/g, '_');
               const wavName = `${String(i).padStart(3, '0')}_${sampleName}.wav`;
               updateProgress('Building SFZ…', 55 + Math.round((i / filled.length) * 30), `sample ${i+1}/${filled.length}`);
               zipEntries.push({
                  name: `samples/${wavName}`,
                  lastModified: new Date(),
                  input: new Blob([s.data], { type: 'audio/wav' }),
               });
            }

            updateProgress('Compressing…', 90, 'Creating ZIP');
            async function* sfzGen() {
               for (const entry of zipEntries) {
                  yield entry;
               }
            }
            const zipBlob = await downloadZip(sfzGen()).blob();
            updateProgress('Downloading…', 95, sfzFilename.replace('.sfz', '.zip'));
            const zipFilename = bankName.replace(/[\\/:*?"<>|]/g, '').trim() + '_sfz.zip';
            triggerDownload(zipBlob, zipFilename);
            setStatus(`done — ${filled.length} sample(s) exported as SFZ`);
            return;
         }
         fileData = (fmt === 'dls') ? buildDLS(bank) : buildSF2(bank);
      } catch (err) {
         hideProgress();
         setStatus(`${fmt.toUpperCase()} build failed: ${err.message}`, true);
         runBtn.disabled = false;
         return;
      }

      updateProgress('Downloading…', 95, bankName + '.' + fmt);
      const blob = new Blob([fileData], { type: 'application/octet-stream' });
      const filename = bankName.replace(/[\\/:*?"<>|]/g, '').trim() + '.' + fmt;
      triggerDownload(blob, filename);
   }

   // ---- DSS-1 export logic ----
   async function runDss1Export(target) {
      const nameEl = document.getElementById('exp-dss1-name');
      const fmtEl  = document.getElementById('exp-dss1-fmt');
      const diskName = (nameEl ? nameEl.value : 'DSS1DISK').trim() || 'DSS1DISK';
      const isDsm1 = fmtEl && fmtEl.value === 'dsm1';

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Decode all WAV items to mono Int16Array PCM
      const dss1Samples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         // Ensure we have an ArrayBuffer for DataView
         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
            // ok
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else {
            continue;
         }

         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         // Parse WAV header
         let off = 12;
         let sampleRate = 32000;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1); // pad to even
         }
         if (!dataOff) continue;

         // Convert to mono Int16Array
         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += (dv.getUint8(dataOff + j * bytesPerSmp + c) - 128) << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Snap sample rate to DSS-1 supported rate and resample
         // DSS-1 only supports 16/24/32/48 kHz
         const dss1Rates = [16000, 24000, 32000, 48000];
         const rateEl = document.getElementById('exp-dss1-rate');
         const userRate = rateEl ? parseInt(rateEl.value) : 0;
         let bestRate;
         if (userRate > 0) {
            bestRate = userRate; // user-selected rate
         } else {
            // Auto: snap to nearest supported rate
            bestRate = 32000;
            let bestDelta = Infinity;
            for (const r of dss1Rates) {
               const d = Math.abs(sampleRate - r);
               if (d < bestDelta) { bestDelta = d; bestRate = r; }
            }
         }

         // Resample PCM to the target DSS-1 rate to keep pitch intact
         let resampledPcm = pcm;
         if (sampleRate !== bestRate && sampleRate > 0 && isFinite(sampleRate)) {
            resampledPcm = resample(pcm, sampleRate, bestRate);
         }

         // Detect pitch from name
         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');
         const origKey = detectPitchFromName(smpName);

         dss1Samples.push({
            name: smpName,
            pcm: resampledPcm,
            sampleRate: bestRate,
            origKey: origKey,
            loopStart: 0,
            loopEnd: resampledPcm.length,
         });
      }

      if (!dss1Samples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      // Check capacity
      const capBytes = calcDss1Capacity(dss1Samples);
      const maxBytes = isDsm1 ? DSM1_DISK_SIZE : DSS1_DATA_SECTORS * DSS1_SECTOR_SIZE;
      if (capBytes > maxBytes) {
         hideProgress();
         const msg = `samples too large for ${isDsm1 ? 'DSM-1' : 'DSS-1'} disk: ${fmtBytes(capBytes)} > ${fmtBytes(maxBytes)} — remove some samples${!isDsm1 ? ' or switch to DSM-1 HD format' : ''}`;
         setStatus(msg, true);
         if (typeof showToast === 'function') showToast(msg, 'DSS-1 capacity exceeded', 'error');
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building disk image…', 60, isDsm1 ? 'Encoding DSM-1 HD disk' : 'Encoding 12-bit samples');

      try {
         const diskImage = buildDss1Disk(dss1Samples, { name: diskName, dsm1: isDsm1 });
         const ext = isDsm1 ? '.dsk' : '.img';
         updateProgress('Downloading…', 95, diskName + ext);
         const blob = new Blob([diskImage], { type: 'application/octet-stream' });
         const filename = diskName.replace(/[\\/:*?"<>|]/g, '').trim() + ext;
         triggerDownload(blob, filename);
         hideProgress();
         setStatus(`done — ${dss1Samples.length} sample(s) exported as ${isDsm1 ? 'DSM-1' : 'DSS-1'} disk image (${fmtBytes(diskImage.length)})`);
      } catch (err) {
         hideProgress();
         setStatus(`DSS-1 export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- Triton/M3 export logic ----
   async function runTritonExport(target) {
      const nameEl  = document.getElementById('exp-triton-name');
      const fmtEl   = document.getElementById('exp-triton-fmt');
      const rateEl  = document.getElementById('exp-triton-rate');
      const msName  = (nameEl ? nameEl.value : 'My Multisample').trim() || 'My Multisample';
      const fmt     = fmtEl ? fmtEl.value : 'ksf';
      const targetRate = rateEl ? parseInt(rateEl.value) : 44100;

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Decode all WAV items to mono Int16Array PCM and resample to target rate
      const tritonSamples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;
         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(it.wav);
         let off = 12;
         let sampleRate = 44100;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= it.wav.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         // Convert to mono Int16Array
         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += (dv.getUint8(dataOff + j * bytesPerSmp + c) - 128) << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Resample to target rate to preserve tonality
         let resampledPcm = pcm;
         if (sampleRate !== targetRate) {
            resampledPcm = resample(pcm, sampleRate, targetRate);
         }

         // Detect pitch from name
         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');
         const origKey = detectPitchFromName(smpName);

         // Generate KSF-safe filename (8.3-ish, uppercase, no special chars)
         const safeName = smpName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().substring(0, 8) || `SMP${String(i).padStart(3, '0')}`;

         tritonSamples.push({
            name: smpName,
            ksfName: safeName,
            pcm: resampledPcm,
            sampleRate: targetRate,
            originalKey: origKey,
            loopStart: 0,
            loopEnd: resampledPcm.length,
            sampleNumber: i,
         });
      }

      if (!tritonSamples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building KSF files…', 55, 'Encoding 16-bit BE PCM');

      try {
         // Build KSF files
         const ksfFiles = [];
         const ksfNames = [];
         for (let i = 0; i < tritonSamples.length; i++) {
            const s = tritonSamples[i];
            updateProgress('Building KSF files…', 55 + Math.round(((i + 1) / tritonSamples.length) * 25), `${i + 1}/${tritonSamples.length}`);
            const ksfData = buildKSF(s);
            ksfFiles.push({
               name: `${s.ksfName}.KSF`,
               lastModified: new Date(),
               input: new Blob([ksfData], { type: 'application/octet-stream' }),
            });
            ksfNames.push(s.ksfName);
         }

         // Build KMP if requested
         let kmpBlob = null;
         let kscText = null;
         if (fmt === 'kmp') {
            updateProgress('Building KMP multisample…', 85, 'Writing multisample');
            const kmpSamples = tritonSamples.map((s, i) => ({
               ksfName: s.ksfName,
               originalKey: s.originalKey,
               topKey: i < tritonSamples.length - 1 ? tritonSamples[i + 1].originalKey - 1 : 127,
               tune: 0,
               level: 0,
               pan: 64,
            }));
            const kmpData = buildKMP({
               name: msName,
               number: 0,
               samples: kmpSamples,
            });
            kmpBlob = new Blob([kmpData], { type: 'application/octet-stream' });
         }

         // Build KSC script
         kscText = buildKSC(ksfNames, fmt === 'kmp' ? [msName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().substring(0, 8) || 'MULTI'] : []);

         updateProgress('Creating ZIP…', 90, 'Packaging');

         // Assemble ZIP
         async function* gen() {
            if (kmpBlob) {
               const kmpSafe = msName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().substring(0, 8) || 'MULTI';
               yield { name: `${kmpSafe}.KMP`, lastModified: new Date(), input: kmpBlob };
            }
            for (const f of ksfFiles) {
               yield f;
            }
            yield { name: 'LOAD.KSC', lastModified: new Date(), input: new Blob([kscText], { type: 'text/plain' }) };
         }

         const blob = await downloadZip(gen()).blob();
         updateProgress('Downloading…', 95, msName + '.zip');
         const zipName = msName.replace(/[\\/:*?"<>|]/g, '').trim() + '_triton.zip';
         triggerDownload(blob, zipName);
         hideProgress();
         setStatus(`done — ${tritonSamples.length} sample(s) exported as Triton/M3 KSF (${fmt === 'kmp' ? 'with KMP multisample' : 'KSF only'})`);
      } catch (err) {
         hideProgress();
         setStatus(`Triton export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- Casio FZ-1 export logic ----
   async function runCasioExport(target) {
      const nameEl = document.getElementById('exp-casio-name');
      const rateEl = document.getElementById('exp-casio-rate');
      const diskName = (nameEl ? nameEl.value : 'FZ1 DISK').trim() || 'FZ1 DISK';
      const targetRate = rateEl ? parseInt(rateEl.value) : 36000;

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Find the FZ-1 rate code for the target rate
      const rateInfo = FZF_SAMPLE_RATES.find(r => r.rate === targetRate) || FZF_SAMPLE_RATES[0];

      // Decode all WAV items to mono Int16Array PCM and resample to target rate
      const casioSamples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
            // ok
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else {
            continue;
         }

         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         let off = 12;
         let sampleRate = 36000;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getUint8(dataOff + j * bytesPerSmp + c) - 128 << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Resample to target FZ-1 rate to preserve tonality
         let resampledPcm = pcm;
         if (sampleRate !== rateInfo.rate && sampleRate > 0 && isFinite(sampleRate)) {
            resampledPcm = resample(pcm, sampleRate, rateInfo.rate);
         }

         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');

         casioSamples.push({
            name: smpName,
            pcm: resampledPcm,
            rateCode: rateInfo.code,
         });
      }

      if (!casioSamples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      // Check capacity — FZ-1 floppy is 1.28 MB
      const capBytes = calcFzfCapacity(casioSamples);
      if (capBytes > FZF_FLOPPY_SIZE) {
         hideProgress();
         const msg = `samples too large for FZ-1 floppy: ${fmtBytes(capBytes)} > ${fmtBytes(FZF_FLOPPY_SIZE)} — remove some samples or use a lower sample rate`;
         setStatus(msg, true);
         if (typeof showToast === 'function') showToast(msg, 'FZ-1 capacity exceeded', 'error');
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building FZF file…', 60, 'Encoding 16-bit PCM voices');

      try {
         const diskImage = buildFzfDisk(casioSamples, { name: diskName });
         updateProgress('Downloading…', 95, diskName + '.fzf');
         const blob = new Blob([diskImage], { type: 'application/octet-stream' });
         const filename = diskName.replace(/[\\/:*?"<>|]/g, '').trim() + '.fzf';
         triggerDownload(blob, filename);
         hideProgress();
         setStatus(`done — ${casioSamples.length} sample(s) exported as Casio FZ-1 FZF (${fmtBytes(diskImage.length)})`);
      } catch (err) {
         hideProgress();
         setStatus(`Casio FZ-1 export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- Roland S-50 family export logic ----
   async function runRolandExport(target) {
      const volEl = document.getElementById('exp-roland-vol');
      const familyEl = document.getElementById('exp-roland-family');
      const rateEl = document.getElementById('exp-roland-rate');
      const fmtEl = document.getElementById('exp-roland-format');
      const volumeName = (volEl ? volEl.value : 'XTRC').trim() || 'XTRC';
      const family = familyEl ? familyEl.value : 'S-550';
      const rolandFormat = fmtEl ? fmtEl.value : 'sdisk';
      const targetRate = rateEl ? parseInt(rateEl.value) : (rolandFormat === 's7' ? 44100 : 30000);

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Decode all WAV items to mono Int16Array PCM and resample to target rate
      const rolandSamples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
            // ok
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else {
            continue;
         }

         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         let off = 12;
         let sampleRate = 44100;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getUint8(dataOff + j * bytesPerSmp + c) - 128 << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Resample to target Roland rate (15 or 30 kHz)
         let resampledPcm = pcm;
         if (sampleRate !== targetRate && sampleRate > 0 && isFinite(sampleRate)) {
            resampledPcm = resample(pcm, sampleRate, targetRate);
         }

         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');

         rolandSamples.push({
            name: smpName,
            pcm: resampledPcm,
            sampleRate: targetRate,
         });
      }

      if (!rolandSamples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      // Check capacity and build disk image
      if (rolandFormat === 's7') {
         // S-770/S-760/S-750: 16-bit LE PCM, 8192-byte units
         const layout = calcS7Layout(rolandSamples);
         if (!layout.fits) {
            hideProgress();
            const overUnits = layout.totalUnits - layout.maxUnits;
            const overBytes = overUnits * 8192;
            const overSecs = Math.ceil(overBytes / 2 / (targetRate / 1000));
            const msg = `too much sample data — needs ${layout.totalUnits} units but only ${layout.maxUnits} available (~${fmtBytes(layout.maxUnits * 8192)}). Remove ~${overSecs}s of audio or use a lower sample rate.`;
            setStatus(msg, true);
            if (typeof showToast === 'function') showToast(msg, 'Roland S-7x0 capacity exceeded', 'error');
            runBtn.disabled = false;
            return;
         }

         updateProgress('Building disk image…', 60, 'Encoding 16-bit PCM');

         try {
            const diskImage = buildS7Disk(rolandSamples, {
               family,
               volumeName,
            });
            updateProgress('Downloading…', 95, volumeName + '.img');
            const blob = new Blob([diskImage], { type: 'application/octet-stream' });
            const filename = volumeName.replace(/[\\/:*?"<>|]/g, '').trim() + '.img';
            triggerDownload(blob, filename);
            hideProgress();
            setStatus(`done — ${rolandSamples.length} sample(s) exported as ${family} floppy image (${fmtBytes(diskImage.length)})`);
         } catch (err) {
            hideProgress();
            setStatus(`Roland export failed: ${err.message}`, true);
         }
      } else {
         // S-50 family: 12-bit packed, max 36 blocks (2 banks × 18)
         const layout = calcSDiskLayout(rolandSamples);
         if (!layout.fits) {
            hideProgress();
            const overBlocks = layout.totalBlocks - SDISK_MAX_BLOCKS;
            const msg = `too much sample data — needs ${layout.totalBlocks} blocks but only ${SDISK_MAX_BLOCKS} available (2 banks × ${SDISK_BLOCKS_PER_BANK}). Remove ${Math.ceil(overBlocks * 12288 / (targetRate / 1000))}s of audio or use 15 kHz for longer capacity.`;
            setStatus(msg, true);
            if (typeof showToast === 'function') showToast(msg, 'Roland S-50 capacity exceeded', 'error');
            runBtn.disabled = false;
            return;
         }

         updateProgress('Building disk image…', 60, 'Encoding 12-bit packed PCM');

         try {
            const diskImage = buildSDiskDisk(rolandSamples, {
               family,
               volumeName,
               rate: targetRate,
            });
            updateProgress('Downloading…', 95, volumeName + '.img');
            const blob = new Blob([diskImage], { type: 'application/octet-stream' });
            const filename = volumeName.replace(/[\\/:*?"<>|]/g, '').trim() + '.img';
            triggerDownload(blob, filename);
            hideProgress();
            setStatus(`done — ${rolandSamples.length} sample(s) exported as ${family} floppy image (${fmtBytes(diskImage.length)})`);
         } catch (err) {
            hideProgress();
            setStatus(`Roland export failed: ${err.message}`, true);
         }
      }

      runBtn.disabled = false;
   }

   // ---- REX2 export logic ----
   async function runRex2Export(target) {
      const bpmEl = document.getElementById('exp-rex2-bpm');
      const modeEl = document.getElementById('exp-rex2-mode');
      const sensEl = document.getElementById('exp-rex2-sens');
      const minSliceEl = document.getElementById('exp-rex2-minslice');
      const bpm = bpmEl ? parseInt(bpmEl.value) : 120;
      const mode = modeEl ? modeEl.value : 'auto';
      const sensitivity = sensEl ? parseFloat(sensEl.value) : 1.5;
      const minSliceMs = minSliceEl ? parseInt(minSliceEl.value) : 80;

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding audio…', 10, 'Processing');

      // Decode all WAV items to Float32 PCM
      const decodedItems = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else continue;

         updateProgress('Decoding audio…', 10 + Math.round(((i + 1) / items.length) * 30), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         let off = 12, sampleRate = 44100, numChannels = 1, bitsPerSample = 16, dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { sampleRate = dv.getUint32(off + 12, true); numChannels = dv.getUint16(off + 10, true); bitsPerSample = dv.getUint16(off + 22, true); }
            else if (cid === 0x61746164) { dataOff = off + 8; dataLen = csz; break; }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         const bps = (bitsPerSample / 8) * numChannels;
         const numSamples = bps > 0 ? Math.floor(dataLen / bps) : 0;
         if (numSamples <= 0) continue;

         // Extract per-channel Float32
         const channels = [];
         for (let ch = 0; ch < numChannels; ch++) {
            channels.push(new Float32Array(numSamples));
         }
         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               for (let ch = 0; ch < numChannels; ch++) {
                  channels[ch][j] = dv.getInt16(dataOff + j * bps + ch * 2, true) / 32768;
               }
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               for (let ch = 0; ch < numChannels; ch++) {
                  const bo = dataOff + j * bps + ch * 3;
                  const v = (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
                  channels[ch][j] = v / 32768;
               }
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               for (let ch = 0; ch < numChannels; ch++) {
                  channels[ch][j] = dv.getFloat32(dataOff + j * bps + ch * 4, true);
               }
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               for (let ch = 0; ch < numChannels; ch++) {
                  channels[ch][j] = (dv.getUint8(dataOff + j * bps + ch) - 128) / 128;
               }
            }
         }

         decodedItems.push({ name: it.name, channels, sampleRate, numChannels, numSamples });
      }

      if (!decodedItems.length) {
         hideProgress();
         setStatus('no valid audio to export', true);
         runBtn.disabled = false;
         return;
      }

      let slices, pcm, sampleRate, numChannels, frameCount;

      if (mode === 'auto' && decodedItems.length >= 1) {
         // Auto mode: use first item, use slices from waveform editor if available
         const item = decodedItems[0];
         sampleRate = item.sampleRate;
         numChannels = item.numChannels;
         frameCount = item.numSamples;

         // Use slices from the waveform editor if they exist and match this audio
         if (rex2State.slices.length > 0 && rex2State.frameCount === frameCount) {
            slices = rex2State.slices;
            // [REX2] Auto mode: using slices from waveform editor
         } else {
            // Detect transients from scratch
            const mono = new Float32Array(frameCount);
            for (let ch = 0; ch < numChannels; ch++) {
               for (let i = 0; i < frameCount; i++) {
                  mono[i] += item.channels[ch][i] / numChannels;
               }
            }

            updateProgress('Detecting transients…', 45, 'Slicing');

            const onsets = detectTransients(mono, sampleRate, { sensitivity, minSliceMs });
            slices = slicesFromOnsets(onsets, frameCount);
            // [REX2] Auto mode: slices detected from samples
         }

         // Convert to Int32 PCM for DWOP encoding
         pcm = [];
         for (let ch = 0; ch < numChannels; ch++) {
            const intData = new Int32Array(frameCount);
            for (let i = 0; i < frameCount; i++) {
               intData[i] = Math.round(Math.max(-1, Math.min(1, item.channels[ch][i])) * 0x7fff);
            }
            pcm.push(intData);
         }
      } else {
         // Manual mode: concatenate all items, each is a slice
         sampleRate = decodedItems[0].sampleRate;
         numChannels = Math.max(...decodedItems.map(d => d.numChannels));

         // Calculate total length and slice positions
         let offset = 0;
         slices = [];
         for (const item of decodedItems) {
            slices.push({ start: offset, end: offset + item.numSamples, name: item.name });
            offset += item.numSamples;
         }
         frameCount = offset;

         // Build concatenated PCM
         pcm = [];
         for (let ch = 0; ch < numChannels; ch++) {
            const intData = new Int32Array(frameCount);
            let pos = 0;
            for (const item of decodedItems) {
               const srcCh = Math.min(ch, item.numChannels - 1);
               for (let i = 0; i < item.numSamples; i++) {
                  intData[pos + i] = Math.round(Math.max(-1, Math.min(1, item.channels[srcCh][i])) * 0x7fff);
               }
               pos += item.numSamples;
            }
            pcm.push(intData);
         }

         // [REX2] Manual mode: slices, total samples
      }

      updateProgress('Encoding DWOP audio…', 60, 'Compressing');

      try {
         // Use filename from the REX2 editor input if available, otherwise from first item
         const fnInput = document.getElementById('xep-rex2-filename');
         const fnValue = fnInput ? fnInput.value.trim() : '';
         const fileName = (fnValue || decodedItems[0].name || 'xtrc').replace(/\.wav$/i, '').replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 11) || 'xtrc';
         const rex2Bytes = buildRex2({ pcm, sampleRate, numChannels, frameCount }, slices, { bpm, fileName });

         updateProgress('Downloading…', 95, fileName + '.rx2');
         const blob = new Blob([rex2Bytes], { type: 'application/octet-stream' });
         triggerDownload(blob, fileName + '.rx2');
         hideProgress();
         setStatus(`done — ${slices.length} slice(s) exported as REX2 (${fmtBytes(rex2Bytes.length)})`);
      } catch (err) {
         hideProgress();
         setStatus(`REX2 export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- MPC2000/MPC3000 export logic ----
   async function runMpcExport(target) {
      const volEl = document.getElementById('exp-mpc-vol');
      const volumeLabel = (volEl ? volEl.value : 'MPC DISK').trim() || 'MPC DISK';

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Decode all WAV items to mono Int16Array PCM and resample to 44100 Hz
      const mpcSamples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
            // ok
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else {
            continue;
         }

         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         let off = 12;
         let sampleRate = 44100;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getUint8(dataOff + j * bytesPerSmp + c) - 128 << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Resample to 44100 Hz (MPC2000+ standard rate)
         let resampledPcm = pcm;
         if (sampleRate !== MPC_SAMPLE_RATE && sampleRate > 0 && isFinite(sampleRate)) {
            resampledPcm = resample(pcm, sampleRate, MPC_SAMPLE_RATE);
         }

         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');

         mpcSamples.push({
            name: smpName,
            pcm: resampledPcm,
            sampleRate: MPC_SAMPLE_RATE,
         });
      }

      if (!mpcSamples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      // Check capacity
      const layout = calcMpcLayout(mpcSamples);
      if (!layout.fits) {
         hideProgress();
         const overBytes = layout.totalBytes - layout.maxBytes;
         const overSecs = Math.ceil(overBytes / 2 / (MPC_SAMPLE_RATE / 1000));
         const msg = `too much sample data — needs ${fmtBytes(layout.totalBytes)} but only ${fmtBytes(layout.maxBytes)} available on a 1.44 MB floppy. Remove ~${overSecs}s of audio.`;
         setStatus(msg, true);
         if (typeof showToast === 'function') showToast(msg, 'MPC capacity exceeded', 'error');
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building disk image…', 60, 'Writing FAT12 + SND files');

      try {
         const diskImage = buildMpcDisk(mpcSamples, { volumeLabel });
         updateProgress('Downloading…', 95, volumeLabel + '.img');
         const blob = new Blob([diskImage], { type: 'application/octet-stream' });
         const filename = volumeLabel.replace(/[\\/:*?"<>|]/g, '').trim() + '.img';
         triggerDownload(blob, filename);
         hideProgress();
         setStatus(`done — ${mpcSamples.length} sample(s) exported as MPC SND floppy image (${fmtBytes(diskImage.length)})`);
      } catch (err) {
         hideProgress();
         setStatus(`MPC export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- Yamaha A3000/A5000 export logic ----
   async function runYamahaExport(target) {
      const diskNameEl = document.getElementById('exp-yamaha-diskname');
      const volNameEl = document.getElementById('exp-yamaha-volname');
      const diskName = (diskNameEl ? diskNameEl.value : 'YAMAHA DISK').trim() || 'YAMAHA DISK';
      const volumeName = (volNameEl ? volNameEl.value : 'VOLUME 1').trim() || 'VOLUME 1';

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Decode all WAV items to mono Int16Array PCM and resample to 44100 Hz
      const yamahaSamples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
            // ok
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else {
            continue;
         }

         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         let off = 12;
         let sampleRate = 44100;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getUint8(dataOff + j * bytesPerSmp + c) - 128 << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Resample to 44100 Hz (Yamaha A-series standard rate)
         let resampledPcm = pcm;
         if (sampleRate !== 44100 && sampleRate > 0 && isFinite(sampleRate)) {
            resampledPcm = resample(pcm, sampleRate, 44100);
         }

         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');

         yamahaSamples.push({
            name: smpName,
            pcm: resampledPcm,
            sampleRate: 44100,
         });
      }

      if (!yamahaSamples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      // Check capacity
      const layout = calcYamahaLayout(yamahaSamples);
      if (!layout.fits) {
         hideProgress();
         const overBytes = layout.totalBytes - layout.maxBytes;
         const overSecs = Math.ceil(overBytes / 2 / (44100 / 1000));
         const msg = `too much sample data — needs ${fmtBytes(layout.totalBytes)} but only ${fmtBytes(layout.maxBytes)} available on a 1.44 MB floppy. Remove ~${overSecs}s of audio.`;
         setStatus(msg, true);
         if (typeof showToast === 'function') showToast(msg, 'Yamaha capacity exceeded', 'error');
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building disk image…', 60, 'Writing FAT12 + SMPL/SBNK files');

      try {
         const diskImage = buildYamahaDisk(yamahaSamples, { diskName, volumeName });
         updateProgress('Downloading…', 95, diskName + '.img');
         const blob = new Blob([diskImage], { type: 'application/octet-stream' });
         const filename = diskName.replace(/[\\/:*?"<>|]/g, '').trim() + '.img';
         triggerDownload(blob, filename);
         hideProgress();
         setStatus(`done — ${yamahaSamples.length} sample(s) exported as Yamaha A-series floppy image (${fmtBytes(diskImage.length)})`);
      } catch (err) {
         hideProgress();
         setStatus(`Yamaha export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- EMU EOS export logic ----
   async function runEmuExport(target) {
      const nameEl = document.getElementById('exp-emu-name');
      const rateEl = document.getElementById('exp-emu-rate');
      const mediaEl = document.getElementById('exp-emu-media');
      const bankName = (nameEl ? nameEl.value : 'EMU Bank').trim() || 'EMU Bank';
      const targetRate = rateEl ? parseInt(rateEl.value) : 44100;
      const media = mediaEl ? mediaEl.value : 'floppy';
      const mediaLimit = media === 'disk' ? EMU_DISK_SIZE : EMU_FLOPPY_SIZE;

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Decode all WAV items to mono Int16Array PCM and resample to target rate
      const emuSamples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
            // ok
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else {
            continue;
         }

         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         let off = 12;
         let sampleRate = 44100;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getUint8(dataOff + j * bytesPerSmp + c) - 128 << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Resample to target EMU rate to preserve tonality
         let resampledPcm = pcm;
         if (sampleRate !== targetRate && sampleRate > 0 && isFinite(sampleRate)) {
            resampledPcm = resample(pcm, sampleRate, targetRate);
         }

         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');

         emuSamples.push({
            name: smpName,
            pcm: resampledPcm,
            sampleRate: targetRate,
         });
      }

      if (!emuSamples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      // Check capacity against selected media
      const capBytes = calcEmuCapacity(emuSamples);
      if (capBytes > mediaLimit) {
         hideProgress();
         const msg = `disk image would be ${fmtBytes(capBytes)} — too large for ${media === 'disk' ? 'hard disk' : 'floppy'} (${fmtBytes(mediaLimit)}), remove some samples or use a lower sample rate${media === 'floppy' ? ' or switch to hard disk' : ''}`;
         setStatus(msg, true);
         if (typeof showToast === 'function') showToast(msg, 'EMU EOS capacity exceeded', 'error');
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building disk image…', 60, 'Encoding 16-bit PCM samples');

      try {
         const diskImage = buildEmuDisk(emuSamples, { name: bankName, media });
         updateProgress('Downloading…', 95, bankName + '.img');
         const blob = new Blob([diskImage], { type: 'application/octet-stream' });
         const filename = bankName.replace(/[\\/:*?"<>|]/g, '').trim() + '.img';
         triggerDownload(blob, filename);
         hideProgress();
         setStatus(`done — ${emuSamples.length} sample(s) exported as EMU EOS disk image (${fmtBytes(diskImage.length)})`);
      } catch (err) {
         hideProgress();
         setStatus(`EMU EOS export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- EMU E4B bank file export logic ----
   async function runEmuE4bExport(target) {
      const nameEl = document.getElementById('exp-emu-name');
      const rateEl = document.getElementById('exp-emu-rate');
      const bankName = (nameEl ? nameEl.value : 'EMU Bank').trim() || 'EMU Bank';
      const targetRate = rateEl ? parseInt(rateEl.value) : 44100;

      const items = exportItems;
      if (!items.length) {
         setStatus('no samples — drag audio from the bucket first', true);
         return;
      }

      showProgress();
      updateProgress('Decoding samples…', 10, 'Processing');

      // Decode all WAV items to mono Int16Array PCM and resample to target rate
      const emuSamples = [];
      for (let i = 0; i < items.length; i++) {
         const it = items[i];
         if (!it.wav) continue;

         let wavBuf = it.wav;
         if (wavBuf instanceof ArrayBuffer) {
            // ok
         } else if (ArrayBuffer.isView(wavBuf)) {
            wavBuf = wavBuf.buffer.slice(wavBuf.byteOffset || 0, (wavBuf.byteOffset || 0) + wavBuf.byteLength);
         } else {
            continue;
         }

         updateProgress('Decoding samples…', 10 + Math.round(((i + 1) / items.length) * 40), `${i + 1}/${items.length}`);

         const dv = new DataView(wavBuf);
         let off = 12;
         let sampleRate = 44100;
         let numChannels = 1;
         let bitsPerSample = 16;
         let dataOff = 0, dataLen = 0;
         while (off + 8 <= wavBuf.byteLength) {
            const cid = dv.getUint32(off, true);
            const csz = dv.getUint32(off + 4, true);
            if (cid === 0x20746d66) { // 'fmt '
               sampleRate = dv.getUint32(off + 12, true);
               numChannels = dv.getUint16(off + 10, true);
               bitsPerSample = dv.getUint16(off + 22, true);
            } else if (cid === 0x61746164) { // 'data'
               dataOff = off + 8;
               dataLen = csz;
               break;
            }
            off += 8 + csz + (csz & 1);
         }
         if (!dataOff) continue;

         const bytesPerSmp = (bitsPerSample / 8) * numChannels;
         if (!bytesPerSmp || !isFinite(bytesPerSmp) || bytesPerSmp <= 0) continue;
         const numSamples = Math.floor(dataLen / bytesPerSmp);
         if (numSamples <= 0 || !isFinite(numSamples)) continue;
         const pcm = new Int16Array(numSamples);

         if (bitsPerSample === 16) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getInt16(dataOff + j * bytesPerSmp + c * 2, true);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 24) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  const bo = dataOff + j * bytesPerSmp + c * 3;
                  v += (dv.getUint8(bo) | (dv.getUint8(bo + 1) << 8) | (dv.getUint8(bo + 2) << 16)) << 8 >> 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 32) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += Math.round(dv.getFloat32(dataOff + j * bytesPerSmp + c * 4, true) * 32767);
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         } else if (bitsPerSample === 8) {
            for (let j = 0; j < numSamples; j++) {
               let v = 0;
               for (let c = 0; c < numChannels; c++) {
                  v += dv.getUint8(dataOff + j * bytesPerSmp + c) - 128 << 8;
               }
               pcm[j] = numChannels > 1 ? Math.round(v / numChannels) : v;
            }
         }

         // Resample to target EMU rate to preserve tonality
         let resampledPcm = pcm;
         if (sampleRate !== targetRate && sampleRate > 0 && isFinite(sampleRate)) {
            resampledPcm = resample(pcm, sampleRate, targetRate);
         }

         const smpName = (it.name || `sample_${i}`).replace(/\.wav$/i, '');

         emuSamples.push({
            name: smpName,
            pcm: resampledPcm,
            sampleRate: targetRate,
         });
      }

      if (!emuSamples.length) {
         hideProgress();
         setStatus('no valid samples to export', true);
         runBtn.disabled = false;
         return;
      }

      updateProgress('Building E4B bank file…', 60, 'Encoding 16-bit PCM samples');

      try {
         const e4bData = buildEmuE4b(emuSamples, { name: bankName });
         updateProgress('Downloading…', 95, bankName + '.e4b');
         const blob = new Blob([e4bData], { type: 'application/octet-stream' });
         const filename = bankName.replace(/[\\/:*?"<>|]/g, '').trim() + '.e4b';
         triggerDownload(blob, filename);
         hideProgress();
         setStatus(`done — ${emuSamples.length} sample(s) exported as E4B bank file (${fmtBytes(e4bData.length)})`);
      } catch (err) {
         hideProgress();
         setStatus(`E4B export failed: ${err.message}`, true);
      }

      runBtn.disabled = false;
   }

   // ---- Bucket Picker Modal ----
   // Reusable modal that lists bucket items and lets the user pick one.
   // callback(bucketItem) is called when an item is selected.
   // options: { filter: (item) => bool, title: string }
   let bucketPickerOverlay = null;
   function openBucketPicker(callback, options = {}) {
      const filter = options.filter || (() => true);
      const title = options.title || 'Pick from bucket';
      const items = bucket.items.filter(filter);

      // Remove any existing picker
      closeBucketPicker();

      // Overlay
      bucketPickerOverlay = document.createElement('div');
      bucketPickerOverlay.className = 'xep-bucket-picker-overlay';
      bucketPickerOverlay.style.zIndex = '999999';
      bucketPickerOverlay.setAttribute('role', 'dialog');
      bucketPickerOverlay.setAttribute('aria-modal', 'true');
      bucketPickerOverlay.setAttribute('aria-label', title);

      // Dialog
      const dialog = document.createElement('div');
      dialog.className = 'xep-bucket-picker-dialog';

      // Header
      const header = document.createElement('div');
      header.className = 'xep-bucket-picker-header';

      const titleEl = document.createElement('span');
      titleEl.className = 'xep-bucket-picker-title';
      titleEl.textContent = title;
      header.appendChild(titleEl);

      const closeBtn = document.createElement('button');
      closeBtn.className = 'xep-bucket-picker-close';
      closeBtn.innerHTML = '&#x2715;';
      closeBtn.setAttribute('aria-label', 'Close picker');
      closeBtn.addEventListener('click', closeBucketPicker);
      header.appendChild(closeBtn);

      dialog.appendChild(header);

      // Search
      const searchWrap = document.createElement('div');
      searchWrap.className = 'xep-bucket-picker-search';
      const searchInput = document.createElement('input');
      searchInput.type = 'text';
      searchInput.placeholder = 'filter…';
      searchInput.setAttribute('aria-label', 'Filter bucket items');
      searchWrap.appendChild(searchInput);
      dialog.appendChild(searchWrap);

      // List
      const listEl = document.createElement('div');
      listEl.className = 'xep-bucket-picker-list scroll_enabled2';
      listEl.setAttribute('role', 'list');
      listEl.setAttribute('aria-label', 'Bucket items');
      dialog.appendChild(listEl);

      bucketPickerOverlay.appendChild(dialog);

      // Click on overlay backdrop closes
      bucketPickerOverlay.addEventListener('click', (e) => {
         if (e.target === bucketPickerOverlay) closeBucketPicker();
      });

      // Escape closes
      const escHandler = (e) => {
         if (e.key === 'Escape') {
            closeBucketPicker();
            document.removeEventListener('keydown', escHandler);
         }
      };
      document.addEventListener('keydown', escHandler);

      // Append to document.body so the fixed-position overlay escapes any
      // ancestor containment (e.g. .panel-area has `contain: strict` which
      // creates a containing block for fixed elements, clipping the overlay).
      document.body.appendChild(bucketPickerOverlay);

      function renderList(filterText) {
         listEl.innerHTML = '';
         const filtered = filterText
            ? items.filter((it) => it.name.toLowerCase().includes(filterText.toLowerCase()))
            : items;

         if (filtered.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'xep-picker-empty';
            empty.textContent = items.length === 0
               ? 'Bucket is empty — add samples to the bucket first.'
               : 'No matching samples.';
            listEl.appendChild(empty);
            return;
         }

         for (const item of filtered) {
            const row = document.createElement('div');
            row.className = 'xep-bucket-picker-item';
            row.setAttribute('role', 'listitem');
            row.tabIndex = 0;

            // Mini waveform canvas
            const canvas = document.createElement('canvas');
            canvas.className = 'xep-bucket-picker-canvas';
            canvas.width = 60;
            canvas.height = 24;
            if (item.wav) {
               drawWaveformFromWavBucket(canvas, item.wav, item.pcm, item.sampleRate, item.channels, item.bitDepth);
            }
            row.appendChild(canvas);

            // Label
            const label = document.createElement('span');
            label.className = 'xep-bucket-picker-label';
            label.textContent = `${item.name} (${item.type || 'wav'})`;
            row.appendChild(label);

            const pick = () => {
               callback(item);
               closeBucketPicker();
            };
            row.addEventListener('click', pick);
            row.addEventListener('keydown', (e) => {
               if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  pick();
               }
            });

            listEl.appendChild(row);
         }
      }

      searchInput.addEventListener('input', () => renderList(searchInput.value));
      renderList('');
      searchInput.focus();
   }

   function closeBucketPicker() {
      if (bucketPickerOverlay) {
         bucketPickerOverlay.remove();
         bucketPickerOverlay = null;
      }
   }

   return panel;
}
